import { Grade } from '../../../../_molecules/SettingsModalDesigner/gradeAndSubject';
import { deriveExampleWords } from './phonicsExampleWords';

export type PhonicsObjective = {
  unitNumber?: number;
  lessonNumber: number | string;
  value: string;
  label: string;
  disabled?: boolean;
  isFree?: boolean;
  /**
   * Five example words that obey the target rule. Populated at construction
   * time from `phonicsExampleWords.ts` based on the rule string. Undefined
   * for objectives where no clean phoneme/grapheme/affix could be extracted
   * (assessments, descriptive review days, etc.).
   */
  exampleWords?: string[];
};

/**
 * Lessons that aren't decoding/encoding practice — pure pedagogy
 * (Demonstration Story, Partner Reading), letter-name recognition without a
 * sound focus, sight/trick-word days, cumulative roll-up reviews, writing
 * and grammar blocks — get auto-disabled so they don't show up in the
 * Phonics Wizard picker or on the per-objective landing pages. The rule
 * the user agreed on: keep anything anchored to a specific phoneme /
 * grapheme / spelling pattern (e.g. "Vowel-Consonant-E in Two-Syllable
 * Words" stays); drop anything that's only a strategy lesson or pure
 * pedagogy (e.g. bare "Prefixes", "Demonstration Story", "Letter
 * Recognition (Aa, Bb, …)").
 */
const NON_DECODING_PATTERNS: RegExp[] = [
  /^Writing:/i,
  /^Grammar:/i,
  /^Grammar\/Writing:/i,
  /^Review\/Assessment:/i,
  /\bUnit\s+Assessment\b/i,
  /\bStudent\s+Assessment\b/i,
  /\bSpelling\s+Inventory\b/i,
  /\bCapitalization\b/i,
  /\bPunctuation\b/i,
  /^Demonstration\s+Story/i,
  /^Partner\s+Reading/i,
  /^Small\s+Group(?:\s+Reading)?$/i,
  /^Small\s+Group\s*\+\s*Partner\s+Reading/i,
  /^Whole\s+Group\s+Reading/i,
  /^Practice\s+reading\s+words?/i,
  /^Reading\s+Wiggle\s+Cards/i,
  /^Introduction\s+to\s+Phrases/i,
  /^Alphabet\/Letter\s+Names?\b/i,
  /^Letter\s+Recognition\b/i,
  /^Uppercase\s+[A-Z]-[A-Z]/i,
  /^Uppercase:/i,
  /^Review\s+Uppercase/i,
  /^Read\/spell\s+irregular\s+Trick\s+Words?/i,
  /^Cumulative\s+end-of-/i,
  /^End-of-G\d\s+cumulative/i,
  /^End-of-Unit\s+Assessment/i,
  /\bTricky\s+Words?\s+and\s+Punctuation\b/i,
  /^Sentence\s+dictation/i,
  /^Punctuation\s+marks?$/i,
  /^Question\s+mark\s+punctuation/i,
  /Introduce\s+Phonics\s+Boost/i,
  /Teach\s+Oral\s+Reading\s+(Marking|Scoring|Procedure)/i,
  /^Prefix\s+or\s+Not\??$/i,
  /^Suffix\s+or\s+Not\??$/i,
  // Bare "Prefixes" / "Suffixes" with no specific affix listed.
  /^Prefixes?$/i,
  /^Suffixes?$/i,
];

export function isNonDecodingLesson(skill: string): boolean {
  return NON_DECODING_PATTERNS.some(p => p.test(skill));
}

const makeUnitLesson = (
  unitNumber: number,
  lessonNumber: number | 'PP',
  skill: string,
  disabled = false,
): PhonicsObjective => ({
  unitNumber,
  lessonNumber,
  value: skill,
  label: `Unit ${unitNumber} - Lesson ${lessonNumber} - ${skill}`,
  disabled: disabled || isNonDecodingLesson(skill),
  exampleWords: deriveExampleWords(skill),
});

export const PHONICS_OBJECTIVES_BY_GRADE: Partial<Record<Grade, PhonicsObjective[]>> = {
  K: [
    // FS1 — Prerequisite Skills (motor, listening, attention) — all disabled
    // makeUnitLesson(1, 1, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 2, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 3, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 4, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 5, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 6, 'Prerequisite Skills: Student Performance Assessment', true),
    // makeUnitLesson(1, 7, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 8, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 9, 'Prerequisite Skills: Reading and Writing', true),
    // makeUnitLesson(1, 10, 'Unit Assessment: Student Performance Assessment', true),
    // // FS2 — Prerequisite Skills (blending, prewriting) — all disabled
    // makeUnitLesson(2, 1, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 2, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 3, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 4, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 5, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 6, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 7, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 8, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 9, 'Prerequisite Skills: Blending and Prewriting', true),
    // makeUnitLesson(2, 10, 'Unit Assessment: Student Performance Assessment', true),
    // FS3 — Basic Code: /m/ /a/ /t/ /d/ /o/ /k/ /g/ /i/
    makeUnitLesson(3, 1, `/m/ > 'm'`),
    makeUnitLesson(3, 2, `/a/ > 'a'`),
    makeUnitLesson(3, 3, `/t/ > 't'`),
    makeUnitLesson(3, 4, `/d/ > 'd'`),
    makeUnitLesson(3, 5, 'Review sound/spellings: /m/, /t/, /d/, /a/'),
    makeUnitLesson(3, 6, `/o/ > 'o'`),
    makeUnitLesson(3, 7, `/k/ > 'c'`),
    makeUnitLesson(3, 8, `/g/ > 'g'`),
    makeUnitLesson(3, 9, `/i/ > 'i'`),
    makeUnitLesson(3, 10, 'Short Vowel Discrimination'),
    makeUnitLesson(3, 11, 'Student Performance Assessment', true),
    makeUnitLesson(3, 12, 'Student Performance Assessment', true),
    makeUnitLesson(3, 13, 'Tricky Words: One and Two (+ assessment)', true),
    makeUnitLesson(3, 14, 'Tricky Words: Three (+ assessment)', true),
    // FS4 — Basic Code expansion + tricky words
    makeUnitLesson(4, 1, `/n/ > 'n'`),
    makeUnitLesson(4, 2, `/h/ > 'h'`),
    makeUnitLesson(4, 3, `/s/ > 's'`),
    makeUnitLesson(4, 4, `/f/ > 'f'`),
    makeUnitLesson(
      4,
      5,
      'Review sound/spellings: /m/, /n/, /t/, /d/, /c/, /g/, /f/, /s/, /h/, /i/, /a/, /o/',
    ),
    makeUnitLesson(4, 6, `/v/ > 'v'`),
    makeUnitLesson(4, 7, `/z/ > 'z'`),
    makeUnitLesson(4, 8, `/p/ > 'p'`),
    makeUnitLesson(4, 9, `/e/ > 'e'`),
    makeUnitLesson(
      4,
      10,
      'Review sound/spellings: /m/, /n/, /p/, /t/, /d/, /f/, /v/, /c/, /g/, /s/, /z/, /h/, /i/, /a/, /o/',
    ),
    makeUnitLesson(4, 11, 'Student Performance Assessment', true),
    makeUnitLesson(4, 12, 'Student Performance Assessment', true),
    makeUnitLesson(4, 13, 'Student Performance Assessment', true),
    makeUnitLesson(4, 14, 'Tricky Words: The', true),
    makeUnitLesson(4, 15, 'Tricky Words: A', true),
    // FS5 — Basic Code: /b/ /l/ /r/ /u/ /w/ /j/ /y/ + tricky words
    makeUnitLesson(5, 1, `/b/ > 'b'`),
    makeUnitLesson(5, 2, `/l/ > 'l'`),
    makeUnitLesson(5, 3, `/r/ > 'r'`),
    makeUnitLesson(5, 4, `/u/ > 'u'`),
    makeUnitLesson(5, 5, 'Tricky Words: Blue', true),
    makeUnitLesson(5, 6, `/w/ > 'w'`),
    makeUnitLesson(5, 7, `/j/ > 'j'`),
    makeUnitLesson(5, 8, `/y/ > 'y' + Tricky Word: Yellow`),
    makeUnitLesson(5, 9, `/x/ > 'x'`),
    makeUnitLesson(5, 10, `/k/ > 'k' (alt)`),
    makeUnitLesson(5, 11, 'Tricky Words: Look', true),
    makeUnitLesson(5, 12, 'Student Performance Assessment', true),
    makeUnitLesson(5, 13, 'Student Performance Assessment', true),
    makeUnitLesson(5, 14, 'Student Performance Assessment', true),
    makeUnitLesson(5, 15, 'Student Performance Assessment', true),
    makeUnitLesson(5, 16, 'Student Performance Assessment', true),
    // FS6 — Basic Code expansion + tricky words
    makeUnitLesson(6, 1, 'Tricky Words: I', true),
    makeUnitLesson(6, 2, 'Consonant Clusters', true),
    makeUnitLesson(6, 3, `'s' > /z/ (tricky)`),
    makeUnitLesson(6, 4, `'s' > /z/ (tricky)`),
    makeUnitLesson(6, 5, 'Letter Names: Review Letter Names', true),
    makeUnitLesson(6, 6, 'Tricky Words: Are', true),
    makeUnitLesson(6, 7, 'Consonant Clusters', true),
    makeUnitLesson(6, 8, 'Spell one-syllable, short-vowel words'),
    makeUnitLesson(6, 9, 'Spell one-syllable, short-vowel words'),
    makeUnitLesson(6, 10, 'Spell one-syllable, short-vowel words'),
    makeUnitLesson(6, 11, 'Tricky Words: Little', true),
    makeUnitLesson(6, 12, 'Spell one-syllable, short-vowel words'),
    makeUnitLesson(6, 13, 'Spell one-syllable, short-vowel words'),
    makeUnitLesson(6, 14, 'Letter Names/Rhyming Review', true),
    makeUnitLesson(6, 15, 'Spell one-syllable, short-vowel words'),
    makeUnitLesson(6, 16, 'Spell one-syllable, short-vowel words'),
    makeUnitLesson(6, 17, 'Unit Assessment', true),
    // FS7 — Basic Code expansion + tricky words (digraphs ch, sh, th, qu, ng)
    makeUnitLesson(7, 1, `/ch/ > 'ch'`),
    makeUnitLesson(7, 2, `/sh/ > 'sh'`),
    makeUnitLesson(7, 3, 'Tricky Words: Down', true),
    makeUnitLesson(7, 4, `/th/ > 'th'`),
    makeUnitLesson(7, 5, `/th/ > 'th'`),
    makeUnitLesson(7, 6, `/ch/ > 'ch', /sh/ > 'sh', /th/ > 'th', and /th/ > 'th'`),
    makeUnitLesson(7, 7, `/qu/ > 'qu'`),
    makeUnitLesson(7, 8, `/ng/ > 'ng'`),
    makeUnitLesson(7, 9, 'Tricky Words: Out', true),
    makeUnitLesson(7, 10, 'Single-Syllable, Short-Vowel Words'),
    makeUnitLesson(7, 11, 'Tricky Word/Student Assessment: Introduce Of', true),
    makeUnitLesson(7, 12, 'Student Performance Assessment', true),
    makeUnitLesson(7, 13, 'Single-Syllable, Short-Vowel Words'),
    makeUnitLesson(7, 14, 'Student Performance Assessment', true),
    makeUnitLesson(7, 15, 'Student Performance Assessment', true),
    makeUnitLesson(7, 16, 'Student Performance Assessment', true),
    makeUnitLesson(7, 17, 'Tricky Words: Down, Out, Of', true),
    // FS8 — Basic Code + tricky words + uppercase letters
    makeUnitLesson(8, 1, 'Tricky Words: Funny', true),
    makeUnitLesson(8, 2, 'Tricky Words: All', true),
    makeUnitLesson(8, 3, 'Tricky Words Review', true),
    makeUnitLesson(8, 4, 'Tricky Words and Rhyming Words Review', true),
    makeUnitLesson(8, 5, 'Tricky Words: From', true),
    makeUnitLesson(8, 6, 'Spelling Review', true),
    makeUnitLesson(8, 7, 'Basic Code Review', true),
    makeUnitLesson(8, 8, 'Tricky Words: Was', true),
    makeUnitLesson(8, 9, 'Tricky Words and Rhyming Words Review', true),
    makeUnitLesson(8, 10, 'Double-Letter Spellings for Consonant Sounds (alt)', true),
    makeUnitLesson(8, 11, 'Double-Letter Spellings and Tricky Words Review', true),
    makeUnitLesson(8, 12, 'Double-Letter Spellings for Consonant Sounds (alt)', true),
    makeUnitLesson(8, 13, 'Double-Letter Spellings for Consonant Sounds Review', true),
    makeUnitLesson(8, 14, 'Double-Letter Spellings for Consonant Sounds (alt)', true),
    makeUnitLesson(8, 15, 'Student Performance Assessment: Word Recognition', true),
    makeUnitLesson(8, 16, 'Student Performance Assessment: Lowercase Letter Names', true),
    makeUnitLesson(8, 17, 'Student Performance Assessment: Tricky Words', true),
    makeUnitLesson(
      8,
      18,
      'Student Performance Assessment: Reading and Code Knowledge',
      true,
    ),
    makeUnitLesson(8, 19, 'Review/Assessment: Student Assessment'),
    makeUnitLesson(8, 20, 'Review/Assessment: Unit Assessment', true),
    // FS9 — Tricky Words + Uppercase Letters review (EOY)
    makeUnitLesson(
      9,
      1,
      `Tricky Words + Uppercase: When and Word and 'A', 'B', 'C', 'D'`,
      true,
    ),
    makeUnitLesson(9, 2, 'Tricky Words: Why and To', true),
    makeUnitLesson(9, 3, `Uppercase: 'E', 'F', 'G', 'H'`),
    makeUnitLesson(9, 4, 'Tricky Words and Uppercase Letters Review', true),
    makeUnitLesson(9, 5, 'Tricky Words: Where and No', true),
    makeUnitLesson(9, 6, `Uppercase: 'I', 'J', 'K', 'L', 'M'`),
    makeUnitLesson(
      9,
      7,
      `Tricky Words + Uppercase: What and So and 'N', 'O', 'P', 'Q', 'R'`,
      true,
    ),
    makeUnitLesson(9, 8, 'Tricky Words Review', true),
    makeUnitLesson(9, 9, 'Tricky Words Review', true),
    makeUnitLesson(
      9,
      10,
      `Assessment, Tricky Words, and Uppercase Letters: Introduce Which and 'S', 'T', 'U', 'V', 'W'`,
      true,
    ),
    makeUnitLesson(9, 11, 'Tricky Words: Once', true),
    makeUnitLesson(9, 12, `Uppercase: 'X', 'Y', 'Z'`),
    makeUnitLesson(9, 13, 'Tricky Words: Said and Says', true),
    makeUnitLesson(9, 14, 'Tricky Words Review', true),
    makeUnitLesson(9, 15, 'Basic Code and Tricky Words Review', true),
    makeUnitLesson(9, 16, 'Tricky Words: Were', true),
    makeUnitLesson(9, 17, 'Tricky Words: Here and There', true),
    makeUnitLesson(9, 18, 'Tricky Words Review', true),
    makeUnitLesson(9, 19, 'Punctuation Marks Review', true),
    makeUnitLesson(
      9,
      20,
      'Student Performance Assessment: Tricky Words, Uppercase Letters, and Punctuation',
      true,
    ),
    makeUnitLesson(
      9,
      21,
      'Student Performance Assessment: Uppercase Letters and Punctuation',
      true,
    ),
    makeUnitLesson(
      9,
      22,
      'Student Performance Assessment: Uppercase Letters and Punctuation',
      true,
    ),
    makeUnitLesson(
      9,
      23,
      'Student Performance Assessment: Uppercase Letters and Punctuation',
      true,
    ),
    makeUnitLesson(9, 24, 'Student Performance Assessment: Word Reading', true),
    makeUnitLesson(9, 25, 'Student Performance Assessment: Sound Writing', true),
    makeUnitLesson(9, 26, 'Student Performance Assessment: Letter Names', true),
  ],
  '1': [
    // FS1 — Back-to-school review + Placement Assessments (Notion source skips L6–L8)
    makeUnitLesson(
      1,
      1,
      'Students will orally blend and segment single-syllable words with up to three phonemes. Students will decode CVC words using /a/, /i/, and /o/',
    ),
    makeUnitLesson(
      1,
      2,
      `Students will identify the following basic code letter-sound correspondences: /s/ > 's,' /z/ > 'z,' /h/ > 'h,' /w/ > 'w,' /e/ > 'e,' and /u/ > 'u.'`,
    ),
    makeUnitLesson(1, 3, 'Basic Code, Nouns, and Tricky Words Review', true),
    makeUnitLesson(1, 4, 'Placement Assessment: Word Recognition', true),
    makeUnitLesson(1, 5, 'Placement Assessment: Story Reading', true),
    makeUnitLesson(1, 9, 'Basic Code Review', true),
    makeUnitLesson(1, 10, 'Basic Code and Tricky Words Review', true),
    makeUnitLesson(
      1,
      11,
      `Students will decode CVC, CCVC, and CVCC words, including those with the consonant digraphs 'ng' and 'qu.'`,
    ),
    makeUnitLesson(1, 12, 'Advanced Code, Nouns, and Tricky Words Review', true),
    makeUnitLesson(1, 13, 'Tricky Words: was, when, why', true),
    makeUnitLesson(
      1,
      14,
      'Tricky Words: a, I, no, so, of, is, to, all, some, word, are, have, were, one, once, do, two, the, said, says, where, what, which, who, when, why',
      true,
    ),
    makeUnitLesson(1, 15, 'Tricky Words: here, there', true),
    // FS2 — Long vowels via VCe / split digraphs
    makeUnitLesson(2, 1, `/ee/ > 'ee'`),
    makeUnitLesson(
      2,
      2,
      `Students will decode one-syllable words with /ee/ > 'ee', /e/ > 'e', /i/ > 'i'`,
    ),
    makeUnitLesson(2, 3, `/ae/ > 'a_e'`),
    makeUnitLesson(2, 4, `Common Nouns and /ae/ > 'a_e'`),
    makeUnitLesson(2, 5, 'Tricky Words: They and Their', true),
    makeUnitLesson(2, 6, `/ie/ > 'i_e'`),
    makeUnitLesson(
      2,
      7,
      'Grammar, Review: Common and Proper Nouns and Tricky Words',
      true,
    ),
    makeUnitLesson(2, 8, `/oe/ > 'o_e'`),
    makeUnitLesson(2, 9, 'Long Vowels and Nouns Review', true),
    makeUnitLesson(2, 10, 'Tricky Words: My and By', true),
    makeUnitLesson(
      2,
      11,
      `Students will identify the following sound/spellings; /ae/ > 'a_e', /ie/ > 'i_e', /oe/ > 'o_e'`,
    ),
    makeUnitLesson(2, 12, `/ue/ > 'u_e'`),
    makeUnitLesson(2, 13, 'Tricky Words: You', true),
    makeUnitLesson(2, 14, 'Tricky Words: Your', true),
    makeUnitLesson(2, 15, 'Tricky Words and Long and Short Vowels Review', true),
    makeUnitLesson(2, 16, 'Tricky Words and Long and Short Vowels Review', true),
    makeUnitLesson(
      2,
      17,
      `Students will distinguish words with long vowel, vowel-consonant-'e' sound/spellings from words with short vowel sound/spellings`,
    ),
    makeUnitLesson(
      2,
      18,
      'Unit Assessment: Word Recognition and Reading Comprehension',
      true,
    ),
    makeUnitLesson(2, 19, 'Unit Assessment: Identifying Nouns', true),
    // FS3 — Vowel digraphs & long-vowel spellings
    makeUnitLesson(3, 1, `/oo/ > 'oo'`),
    makeUnitLesson(
      3,
      2,
      `Students will provide the sounds for 'i', 'e', 'a', 'u', 'o', 'oo', and fourteen other spellings`,
    ),
    makeUnitLesson(
      3,
      3,
      'Students will listen to and identify one-syllable words that have the /oo/ sound',
    ),
    makeUnitLesson(
      3,
      4,
      'Students will orally review the short vowel sounds /i/, /e/, /a/, /u/, and /o/ in isolation',
    ),
    makeUnitLesson(
      3,
      5,
      `Assessment and Basic Code: Spelling Assessment and Introduce /oo/ > 'oo'`,
      true,
    ),
    makeUnitLesson(3, 6, 'Writing: Plan and Draft a Book Report'),
    makeUnitLesson(
      3,
      7,
      `Students will read words with the tricky spelling 'oo' and will sort words with 'oo' > /oo/ or /oo/`,
    ),
    makeUnitLesson(
      3,
      8,
      'Grammar and Writing: Identify Verbs and Publish a Book Report',
      true,
    ),
    makeUnitLesson(3, 9, `/ou/ > 'ou'`),
    makeUnitLesson(
      3,
      10,
      'Assessment, Grammar, and Tricky Word: Spelling Assessment, Identify Verbs, and Introduce: Down',
      true,
    ),
    makeUnitLesson(
      3,
      11,
      `Students will read and spell one-syllable words with 'oo' > /oo/ or /oo/ and previously taught sound/spellings`,
    ),
    makeUnitLesson(3, 12, `/oi/ > 'oi'`),
    makeUnitLesson(
      3,
      13,
      'Students will orally produce single-syllable words with the /oi/ sound',
    ),
    makeUnitLesson(3, 14, 'Digraphs Review', true),
    makeUnitLesson(3, 15, `/aw/ > 'aw'`),
    makeUnitLesson(3, 16, 'Students will read previously taught Tricky Words', true),
    makeUnitLesson(3, 17, 'Vowel Digraphs Review', true),
    makeUnitLesson(3, 18, 'Vowel Digraphs Review', true),
    makeUnitLesson(
      3,
      19,
      'Unit Assessment: Word Recognition and Story Comprehension',
      true,
    ),
    // FS4 — R-controlled vowels + 2-syllable words (MOY)
    makeUnitLesson(4, 1, `/er/ > 'er'`),
    makeUnitLesson(4, 2, 'Grammar: Introduce Contractions', true),
    makeUnitLesson(4, 3, 'Vowel Digraphs', true),
    makeUnitLesson(4, 4, `/ar/ > 'ar'`),
    makeUnitLesson(4, 5, 'Assessment: Spelling Assessment', true),
    makeUnitLesson(4, 6, 'Basic Code and Grammar Review', true),
    makeUnitLesson(4, 7, `Two-Syllable Words and /or/ > 'or'`),
    makeUnitLesson(4, 8, 'Tricky Words: Today, Yesterday, and Tomorrow', true),
    makeUnitLesson(4, 9, 'Practice: Two-Syllable Words'),
    makeUnitLesson(4, 10, 'Assessment: Spelling Assessment', true),
    makeUnitLesson(4, 11, 'Spelling: Two-Syllable Words'),
    makeUnitLesson(4, 12, 'Vowel Digraphs, Nouns, and Verbs Review', true),
    makeUnitLesson(4, 13, 'Grammar: Past-, Present-, and Future-Tense Verbs', true),
    makeUnitLesson(4, 14, 'Two-Syllable Words Review', true),
    makeUnitLesson(4, 15, 'Assessment: Spelling Assessment', true),
    makeUnitLesson(4, 16, 'Past-, Present-, and Future-Tense Verbs Review', true),
    makeUnitLesson(4, 17, 'Grammar: Review and Practice', true),
    makeUnitLesson(
      4,
      18,
      'Students will read and spell two-syllable words with the suffixes –er, –ness and –less',
    ),
    makeUnitLesson(4, 19, 'Grammar: Past-Tense Verbs', true),
    makeUnitLesson(
      4,
      20,
      'Assessment and Writing: Spelling Assessment and Plan a Descriptive Paragraph',
      true,
    ),
    makeUnitLesson(4, 21, 'Writing: Draft a Descriptive Paragraph', true),
    makeUnitLesson(4, 22, 'Writing: Plan Informational Text', true),
    makeUnitLesson(4, 23, 'Writing: Draft Informational Text', true),
    makeUnitLesson(
      4,
      24,
      'Students will read two-syllable compound words and one- and two-syllable words with r-controlled digraphs and inflectional endings',
    ),
    makeUnitLesson(4, 25, 'Unit Assessment: Dictation and Grammar', true),
    // FS5 — Code expansion (consonant spelling alts)
    makeUnitLesson(5, 1, `/p/ > 'p' and 'pp' (alt)`),
    makeUnitLesson(5, 2, 'Tricky Words: How', true),
    makeUnitLesson(5, 3, `/b/ > 'b' and 'bb' (alt)`),
    makeUnitLesson(5, 4, `/k/ > 'c', 'k', 'ck', and 'cc' (alt)`),
    makeUnitLesson(5, 5, `/k/ > 'c', 'k', 'ck', and 'cc' (alt)`),
    makeUnitLesson(5, 6, `/ch/ > 'ch' and 'tch' (alt)`),
    makeUnitLesson(5, 7, `/g/ > 'g' and 'gg' (alt)`),
    makeUnitLesson(5, 8, `/j/ > 'j', 'g', and 'ge' (alt)`),
    makeUnitLesson(5, 9, `'g' > /g/ and /j/ (tricky)`),
    makeUnitLesson(5, 10, 'Spelling Alternatives for /k/ and /j/ Review'),
    makeUnitLesson(5, 11, `/t/ > 't', 'tt', and 'ed' (alt)`),
    makeUnitLesson(
      5,
      12,
      `Tricky Word and Code: Review /d/ > 'd', 'dd', and 'ed'; Introduce: Stagecoach (alt)`,
    ),
    makeUnitLesson(5, 13, `/f/ > 'f' and 'ff' (alt)`),
    makeUnitLesson(
      5,
      14,
      `Tricky Word and Code: Introduce /v/ > 'v' and 've'; Introduce: Picture (alt)`,
    ),
    makeUnitLesson(5, 15, 'Writing: Introduce Opinion Writing', true),
    makeUnitLesson(
      5,
      16,
      'Students will identify words with an initial /r/ sound based on oral riddle clues',
    ),
    makeUnitLesson(5, 17, 'Students will review the spellings for /j/, /g/, and /k/'),
    makeUnitLesson(
      5,
      18,
      'Students will use sound-spelling patterns to write spellings for the following sounds: /ch/, /j/, /n/, /k/, /s/, /g/, /z/, and /m/',
    ),
    makeUnitLesson(5, 19, `/l/ > 'l' and 'll' (alt)`),
    makeUnitLesson(5, 20, 'Spelling Assessment; Tricky Words Review', true),
    makeUnitLesson(
      5,
      21,
      'Students will write spellings for the following sounds: /ch/, /m/, /j/, /n/, /k/, /s/, /g/, and /z/',
    ),
    makeUnitLesson(5, 22, 'Unit Assessment: Word Recognition; Grammar', true),
    // FS6 — Code expansion (more spelling alts + tricky spellings)
    makeUnitLesson(6, 1, `/s/ > 'c' (alt)`),
    makeUnitLesson(6, 2, `'c' > /k/ and /s/ (tricky)`),
    makeUnitLesson(
      6,
      3,
      `Students will identify the spellings for /oo/ > 'oo', /oo/ > 'oo', /oi/ > 'oi', /aw/ > 'aw', /ou/ > 'ou', and r-controlled vowel spellings`,
    ),
    makeUnitLesson(6, 4, `Students will read words with /s/ > 'ce', 'se', 'c', and 'ss'`),
    makeUnitLesson(6, 5, 'Spelling Assessment (review)', true),
    makeUnitLesson(6, 6, `'c' > /k/ and /s/ (tricky)`),
    makeUnitLesson(
      6,
      7,
      'Students will segment and blend two-syllable words with up to seven phonemes',
      true,
    ),
    makeUnitLesson(6, 8, `'g' > /g/ and /j/ (tricky)`),
    makeUnitLesson(6, 9, 'Grammar: Use Adjectives and Prepositions', true),
    makeUnitLesson(6, 10, `/n/ > 'kn' (alt)`),
    makeUnitLesson(6, 11, `/n/ > 'kn', 'n', 'nn' and /s/ > 'c', 'ce', 'se' (alt)`),
    makeUnitLesson(
      6,
      12,
      'Students will use previously taught Tricky Words to create an oral story',
      true,
    ),
    makeUnitLesson(
      6,
      13,
      'Students will segment and blend two-syllable words with up to six phonemes',
    ),
    makeUnitLesson(
      6,
      14,
      'Students will segment and blend two-syllable words with up to seven phonemes',
    ),
    makeUnitLesson(
      6,
      15,
      'Students will read plural nouns ending in –s and –es and sort words according to the pronunciation of their plural markers',
    ),
    makeUnitLesson(6, 16, `/w/ > 'w' and 'wh' (alt)`),
    makeUnitLesson(6, 17, 'Tricky Words and Spelling Alternatives (review)', true),
    makeUnitLesson(6, 18, `/ng/ > 'ng' and 'n' (alt)`),
    makeUnitLesson(6, 19, `'n' > /n/ and /ng/ (tricky)`),
    makeUnitLesson(6, 20, 'Spelling Assessment and Pronouns (review)', true),
    makeUnitLesson(6, 21, 'Grammar: Verb Tense and Root Words', true),
    makeUnitLesson(6, 22, 'Pronouns and Vowel Digraphs (review)', true),
    makeUnitLesson(
      6,
      23,
      'Students will segment and blend two-syllable words with up to seven phonemes',
      true,
    ),
    makeUnitLesson(6, 24, 'Unit Assessment: Word Recognition', true),
    makeUnitLesson(6, 25, 'Unit Assessment: Reading Comprehension', true),
    // FS7 — /ae/ alternative spellings 'ai', 'ay' + EOY
    makeUnitLesson(7, 1, `/ae/ > 'ai' and 'ay' (alt)`),
    makeUnitLesson(7, 2, `/ae/ > 'ai' and 'ay' (alt)`),
    makeUnitLesson(7, 3, 'Grammar: Plural Nouns and Noun-Verb Agreement', true),
    makeUnitLesson(7, 4, 'Grammar: Conjunctions and Noun-Verb Agreement', true),
    makeUnitLesson(
      7,
      5,
      `Students will decode words with /ae/ > 'ai' and 'ay', and words with the r-controlled vowel sounds /er/, /ar/, and /or/`,
    ),
    makeUnitLesson(7, 6, 'Grammar: Noun-Verb Agreement', true),
    makeUnitLesson(
      7,
      7,
      'Students will orally segment and blend two-syllable words into syllables and sounds and will provide the spelling for the segmented words',
    ),
    makeUnitLesson(7, 8, 'Grammar: Root Words with Inflectional Endings'),
    makeUnitLesson(7, 9, 'Tricky Words and Punctuation (review)', true),
    makeUnitLesson(7, 10, 'Spelling Assessment (review)', true),
    makeUnitLesson(7, 11, `/oe/ > 'oa' (alt)`),
    makeUnitLesson(7, 12, 'Conjunctions and Long Vowel Spellings (review)', true),
    makeUnitLesson(7, 13, 'Writing: Introduce Instructional Writing', true),
    makeUnitLesson(7, 14, 'Instructional Writing: Sequencing Steps', true),
    makeUnitLesson(7, 15, 'Spelling Assessment (review)', true),
    makeUnitLesson(
      7,
      16,
      'Students will alphabetize a series of words to the second letter and use a dictionary to find words',
      true,
    ),
    makeUnitLesson(7, 17, 'Instructional Writing: Editing and Publishing', true),
    makeUnitLesson(7, 18, 'Grammar Review: Conjunctions and Punctuation', true),
    makeUnitLesson(7, 19, 'Silent Reading Comprehension: End-of-Year Assessment', true),
    makeUnitLesson(7, 20, 'Fluency: End-of-Year Assessment', true),
    makeUnitLesson(7, 21, 'Word Reading in Isolation: End-of-Year Assessment', true),
  ],
  '2': [
    // FS1 — Back-to-school: Basic Code Review + Placement Assessments
    makeUnitLesson(
      1,
      1,
      'Students will practice producing a series of rhyming words',
      true,
    ),
    makeUnitLesson(
      1,
      2,
      `Students will practice producing a series of rhyming words. Students will review the sound/spellings /e/ > 'e', /u/ > 'u', /o/ > 'o', /k/ > 'c', and /g/ > 'g'`,
    ),
    makeUnitLesson(
      1,
      3,
      `Students will practice producing a series of rhyming words. Students will review the consonant sound/spellings /k/ > 'k', /g/ > 'g', /f/ > 'f', /v/ > 'v', /l/ > 'l', and /h/ > 'h', and the vowel sound/spellings /i/ > 'i', /e/ > 'e'`,
    ),
    makeUnitLesson(
      1,
      4,
      `Review of /th/ > 'th', /th/ > 'th', /ng/ > 'ng', /sh/ > 'sh', and /ch/ > 'ch' in one-syllable words with short vowels`,
    ),
    makeUnitLesson(
      1,
      5,
      `Review eight consonant sound/spellings, including /qu/ > 'qu' in one-syllable words with short vowels`,
    ),
    makeUnitLesson(1, 6, 'Placement Assessment: Story Reading Assessment', true),
    makeUnitLesson(1, 7, 'Placement Assessment: Word & Story Reading Assessments', true),
    makeUnitLesson(
      1,
      8,
      'Placement Assessment: Word & Story Reading Assessments (continued)',
      true,
    ),
    makeUnitLesson(
      1,
      9,
      'Placement Assessment: Word & Story Reading Assessments (continued)',
      true,
    ),
    makeUnitLesson(1, 10, 'Placement Assessment: Word Reading Assessment', true),
    makeUnitLesson(1, 11, 'Tricky Words: the, he, she, we, be, me', true),
    makeUnitLesson(
      1,
      12,
      'Tricky Words: was, of, a. Two-syllable words with double consonants',
      true,
    ),
    makeUnitLesson(
      1,
      13,
      `Double consonant sound/spellings /b/ > 'bb', /d/ > 'dd', /k/ > 'cc', 'ck', /g/ > 'gg', /f/ > 'ff', /l/ > 'll'`,
    ),
    makeUnitLesson(1, 14, `Tricky Words and Tricky Spelling 'g' Review`, true),
    makeUnitLesson(1, 15, `Tricky Spelling 'c' Review (+ spelling assessment)`),
    makeUnitLesson(
      1,
      16,
      `Consonant digraph sound/spellings /n/ > 'kn', /r/ > 'wr', /w/ > 'wh', /qu/ > 'qu'`,
    ),
    makeUnitLesson(1, 17, `Sound/spellings /j/ > 'ge' and /v/ > 've'`),
    makeUnitLesson(1, 18, 'Tricky Words: once, one, two', true),
    makeUnitLesson(
      1,
      19,
      'Cumulative Tricky Words review (he, she, we, be, me, the, a, was, from, of, to, do, down, how, what, where, why, once, one, two)',
      true,
    ),
    makeUnitLesson(1, 20, `Tricky Spelling 's' Review (+ spelling assessment)`),
    makeUnitLesson(1, 21, 'Sound/Spellings and Tricky Words Review', true),
    makeUnitLesson(1, 22, 'End-of-Unit Assessment', true),
    // FS2 — VCe two-syllable words + 'separated digraphs' o_e/u_e + 'y' as vowel
    makeUnitLesson(2, 1, `/ae/ > 'a_e' and /ie/ > 'i_e'`),
    makeUnitLesson(2, 2, `/oe/ > 'o_e' and /ue/ > 'u_e'`),
    makeUnitLesson(2, 3, `/ee/ > 'ee' and 'ea' (alt)`),
    makeUnitLesson(2, 4, 'Tricky Words: all, who', true),
    makeUnitLesson(2, 5, `'oo' > /oo/ or /oo/ in simple sentences`),
    makeUnitLesson(2, 6, 'Tricky Words: are, were, some', true),
    makeUnitLesson(2, 7, `/ou/ > 'ou' and 'ow' words`),
    makeUnitLesson(2, 8, 'Decode multisyllabic words with diphthongs'),
    makeUnitLesson(2, 9, 'Grammar: Commas and Quotation Marks', true),
    makeUnitLesson(2, 10, 'Tricky Words: they, their', true),
    makeUnitLesson(2, 11, `/or/ > 'or' and /ar/ > 'ar' review`),
    makeUnitLesson(2, 12, 'Writing: Draft a Narrative Book Report', true),
    makeUnitLesson(2, 13, 'Writing: Edit a Narrative Book Report', true),
    makeUnitLesson(2, 14, 'Cumulative vowel sound review (paired)', true),
    makeUnitLesson(2, 15, 'Spelling Assessment (review)', true),
    makeUnitLesson(
      2,
      16,
      'Unit Assessment: Word Recognition, Comprehension, Fluency, Cursive',
      true,
    ),
    // FS3 — Long /o/ spelling patterns + ae/oe/ie/ue/aw alts
    makeUnitLesson(3, 1, 'The /ae/ Sound and Its Spellings (alt)'),
    makeUnitLesson(3, 2, `/ae/ > 'ai' and 'ay' (alt)`),
    makeUnitLesson(3, 3, `/ae/ > 'a' and Tricky Spelling 'a' (alt)`),
    makeUnitLesson(3, 4, `'a' > /a/ and /ae/ (tricky)`),
    makeUnitLesson(3, 5, 'Spelling Assessment', true),
    makeUnitLesson(3, 6, 'The /oe/ Sound and Its Spellings (alt)'),
    makeUnitLesson(3, 7, `/oe/ > 'oa' and 'oe' (alt)`),
    makeUnitLesson(3, 8, `'o' > /o/ and /oe/ (tricky)`),
    makeUnitLesson(3, 9, `'o' > /oa/ and /oe/ (tricky)`),
    makeUnitLesson(3, 10, 'Spelling Assessment', true),
    makeUnitLesson(3, 11, 'The /ie/ Sound and Its Spellings (alt)'),
    makeUnitLesson(3, 12, '/ie/ Review + Common/Proper Nouns (alt)'),
    makeUnitLesson(3, 13, `'i' > /i/ and /ie/ (tricky)`),
    makeUnitLesson(3, 14, 'Grammar: Capitalization and Punctuation'),
    makeUnitLesson(3, 15, 'Spelling and Mid-Point Dictation', true),
    makeUnitLesson(3, 16, 'The /ue/ Sound and Its Spellings (alt)'),
    makeUnitLesson(3, 17, `/ue/ > 'u_e', 'u', and 'ue' (alt)`),
    makeUnitLesson(3, 18, `/ue/ > 'u_e', 'u', and 'ue' (alt)`),
    makeUnitLesson(3, 19, `/aw/ > 'aw' and 'au' (alt)`),
    makeUnitLesson(3, 20, 'Spelling Assessment', true),
    makeUnitLesson(3, 21, 'Words with prefix un–'),
    makeUnitLesson(3, 22, 'Writing: Plan and Draft a Personal Narrative', true),
    makeUnitLesson(3, 23, 'Writing: Plan and Draft a Personal Narrative', true),
    makeUnitLesson(3, 24, 'Writing: Edit a Personal Narrative', true),
    makeUnitLesson(3, 25, 'Unit Assessment', true),
    // FS4 — Syllable division patterns + tricky 'i' / 'y' / 'ow' / 'o' / 'e'
    makeUnitLesson(4, 1, `/er/ > 'er', 'ir', and 'ur' (alt)`),
    makeUnitLesson(4, 2, `/er/ > 'er', 'ir', and 'ur' (alt)`),
    makeUnitLesson(4, 3, `/i/ > 'y' (alt)`),
    makeUnitLesson(4, 4, `Tricky Spelling 'i' Review`),
    makeUnitLesson(4, 5, 'Spelling Assessment', true),
    makeUnitLesson(4, 6, `'y' > /y/, /i/, or /ie/ (tricky)`),
    makeUnitLesson(4, 7, `Grammar: Plural Nouns and Apostrophe-'s'`),
    makeUnitLesson(4, 8, `'o' > /o/ or /oe/ (tricky)`),
    makeUnitLesson(4, 9, `'ow' > /oe/ or /ou/ (tricky)`),
    makeUnitLesson(4, 10, 'Spelling Assessment + Irregular Plural Nouns', true),
    makeUnitLesson(4, 11, `'e' > /ee/ or /e/ (tricky)`),
    makeUnitLesson(4, 12, 'Grammar: Proper Nouns + Persuasive Writing intro', true),
    makeUnitLesson(4, 13, 'Grammar/Writing: Proper Nouns and Persuasive Writing', true),
    makeUnitLesson(4, 14, `Grammar/Writing: Verb 'to be' + Persuasive Writing`),
    makeUnitLesson(4, 15, 'Assessment + Edit a Persuasive Letter', true),
    makeUnitLesson(4, 16, 'Writing: Edit and Publish a Persuasive Letter', true),
    makeUnitLesson(4, 17, `/ee/ > 'y' and 'ey' (alt)`),
    makeUnitLesson(4, 18, `Tricky Spelling 'y' Review`),
    makeUnitLesson(4, 19, `Grammar: Present and Past Tense of 'to be'`, true),
    makeUnitLesson(4, 20, 'Spelling, Grammar, Cursive Review', true),
    makeUnitLesson(4, 21, `/aw/ > 'al', 'au', and 'aw' (alt)`),
    makeUnitLesson(4, 22, 'Unit Assessment: Reading Comp + Grammar', true),
    // FS5 — Schwa /ə/ + 'a' and 'e' + Tricky Spelling
    makeUnitLesson(5, 1, `/u/ > 'o', 'ou', 'o_e' (alt)`),
    makeUnitLesson(5, 2, `/u/ > 'o' and 'u' (alt)`),
    makeUnitLesson(5, 3, `/u/ > 'ou' and 'o_e' (alt)`),
    makeUnitLesson(5, 4, 'Grammar: Adjectives', true),
    makeUnitLesson(5, 5, `/u/ > 'o_e', 'ou', 'o' in sorted columns`),
    makeUnitLesson(
      5,
      6,
      `Vowel teams /u/ > 'ou', 'o_e' / /ee/ > 'ie', 'ey' / /ie/ > 'i_e', 'i', 'y'`,
    ),
    makeUnitLesson(5, 7, `/ə/ > 'a' and 'e' (alt)`),
    makeUnitLesson(5, 8, `/ə/ > 'a' and 'e' (alt)`),
    makeUnitLesson(5, 9, 'Apostrophes Review', true),
    makeUnitLesson(5, 10, 'Spelling Assessment', true),
    makeUnitLesson(5, 11, `/ə/ + /l/ > 'ul', 'al', 'il' (alt)`),
    makeUnitLesson(5, 12, 'Spelling Alternatives for /u/ and /ə/ Review'),
    makeUnitLesson(5, 13, `/ə/ + /l/ > 'el' and 'le' (alt)`),
    makeUnitLesson(5, 14, '/u/, /ə/, /ə/ + /l/ (alt)'),
    makeUnitLesson(5, 15, 'Spelling Assessment (review) + Suffix –tion intro'),
    makeUnitLesson(5, 16, 'Suffix –tion Review'),
    makeUnitLesson(5, 17, 'Writing: Plan a Narrative Ending', true),
    makeUnitLesson(5, 18, 'Writing: Plan a Narrative Ending', true),
    makeUnitLesson(
      5,
      19,
      'Verb tenses (present/past) + vowel sound/spelling identification',
      true,
    ),
    makeUnitLesson(5, 20, 'Spelling Assessment (review)', true),
    makeUnitLesson(5, 21, `'a' > /a/, /ae/, or /ə/ (tricky)`),
    makeUnitLesson(5, 22, `'e' > /e/, /ee/, or /ə/ (tricky)`),
    makeUnitLesson(5, 23, `'o' > /o/, /oe/, or /u/ (tricky)`),
    makeUnitLesson(5, 24, `'o_e' > /oe/ or /u/ (tricky)`),
    makeUnitLesson(
      5,
      25,
      `Spelling Assessment + Tricky Spelling 'ou' > /ou/ or /u/`,
      true,
    ),
    makeUnitLesson(5, 26, 'Unit Assessment: Reading Comp and Grammar', true),
    makeUnitLesson(5, 27, 'Unit Assessment: Decoding and Alphabetizing', true),
    // FS6 — Code review + EOY
    makeUnitLesson(6, 1, `/f/ > 'ph' (alt)`),
    makeUnitLesson(6, 2, 'Reading: Text Features in Informational Text', true),
    makeUnitLesson(6, 3, `/e/ > 'ea' (alt)`),
    makeUnitLesson(6, 4, `/e/ > 'ea' in connected text`),
    makeUnitLesson(6, 5, 'Spelling Assessment', true),
    makeUnitLesson(6, 6, `Tricky 'ea' > /ee/ or /e/`),
    makeUnitLesson(6, 7, 'Reading + Grammar: Informational Text and Adverbs', true),
    makeUnitLesson(6, 8, `/er/ > 'ar', 'or' (alt)`),
    makeUnitLesson(6, 9, 'Grammar: Adverbs', true),
    makeUnitLesson(6, 10, 'Spelling Assessment', true),
    makeUnitLesson(6, 11, `/k/ > 'ch' (alt)`),
    makeUnitLesson(6, 12, 'Grammar: Adverbs, Adjectives, Complete Sentences', true),
    makeUnitLesson(6, 13, `/ee/ > 'i' and Tricky Spelling 'i' (alt)`),
    makeUnitLesson(6, 14, `Tricky Spelling 'i' Review`),
    makeUnitLesson(6, 15, 'Spelling Assessment', true),
    makeUnitLesson(6, 16, `'i' > /i/, /ie/, /ee/ sorted`),
    makeUnitLesson(6, 17, 'Grammar: Building Sentences', true),
    makeUnitLesson(6, 18, 'Grammar: Building Sentences', true),
    makeUnitLesson(6, 19, `/ə/ > 'a', 'e' / /ə/ + /l/ / /sh/ + /ə/ + /n/`),
    makeUnitLesson(6, 20, 'Spelling Assessment', true),
    makeUnitLesson(6, 21, 'Cumulative sound/spelling fill-in', true),
    makeUnitLesson(6, 22, 'Reading: Re-Reading', true),
    makeUnitLesson(6, 23, 'Alphabetize one- and two-syllable words', true),
    makeUnitLesson(6, 24, 'Reading: Re-Reading', true),
    makeUnitLesson(6, 25, 'Spelling Assessment', true),
    makeUnitLesson(6, 26, `/o/ > 'a', 'wa' in sentences`),
    makeUnitLesson(6, 27, 'Unit Assessment: The War of 1812', true),
    makeUnitLesson(6, 28, 'Alphabetize to second letter', true),
    makeUnitLesson(6, 29, 'Prefixes un–, re–, dis–'),
    makeUnitLesson(6, 30, 'Spelling Assessment', true),
    makeUnitLesson(6, 31, 'Prefixes un–, re–, dis–'),
    makeUnitLesson(6, 32, 'Prefixes un–, re–, dis–'),
    makeUnitLesson(6, 33, 'End-of-Year Assessment: Silent Reading Comprehension', true),
    makeUnitLesson(6, 34, 'End-of-Year Assessment: Fluency', true),
    makeUnitLesson(6, 35, 'End-of-Year Assessment: Word Reading in Isolation', true),
  ],
  '3': [
    // FS1 — Review
    makeUnitLesson(1, 1, 'Placement Assessment A', true),
    makeUnitLesson(1, 2, 'Placement Assessment B', true),
    makeUnitLesson(1, 3, 'wh, sh, th, ng, nk Review'),
    makeUnitLesson(1, 4, 'ck Review'),
    makeUnitLesson(1, 5, 'ch, tch /ch/ Review'),
    makeUnitLesson(1, 6, 'dge, ge /j/'),
    makeUnitLesson(1, 7, 'ph, gh /f/'),
    makeUnitLesson(1, 8, 'kn, gn /n/'),
    makeUnitLesson(1, 9, 'igh, ear, eer, ere, ore (trigraphs)'),
    makeUnitLesson(1, 10, 'ear, ere, air, are (tricky trigraphs)'),
    makeUnitLesson(1, 11, 'Closed Syllables Review'),
    makeUnitLesson(1, 12, 'VC/CV Syllable Division'),
    makeUnitLesson(1, 13, 'VC/V Syllable Division'),
    makeUnitLesson(1, 14, 'VCCCV Syllable Division'),
    makeUnitLesson(1, 15, 'Fluency Assessment', true),
    makeUnitLesson(1, 16, 'Reading & Spelling Assessment', true),
    makeUnitLesson(1, 'PP', 'Pausing Point (1 day)', true),
    // FS2 — V/CV → Open Syllables → Suffixes -ed/-ing
    makeUnitLesson(2, 1, 'V/CV Syllable Division'),
    makeUnitLesson(2, 2, 'V/CV Syllable Division (decode/spell)'),
    makeUnitLesson(2, 3, 'V/CV Syllable Division (decode/spell, cont.)'),
    makeUnitLesson(2, 4, 'VCV Syllable Division Review'),
    makeUnitLesson(2, 5, 'Multiple-Meaning Words (context)', true),
    makeUnitLesson(2, 6, 'Open Syllables'),
    makeUnitLesson(2, 7, 'Multiple-Meaning Words (practice)', true),
    makeUnitLesson(2, 8, 'Open Syllables Review'),
    makeUnitLesson(2, 9, 'Open Syllables (read & spell)'),
    makeUnitLesson(2, 10, 'Reading Fluency', true),
    makeUnitLesson(2, 11, '–ed, –ing'),
    makeUnitLesson(2, 12, '–ed, –ing (practice)'),
    makeUnitLesson(2, 13, '–ed, –ing Review'),
    makeUnitLesson(2, 14, 'Fluency Assessment', true),
    makeUnitLesson(2, 15, 'Reading & Spelling Assessment', true),
    makeUnitLesson(2, 'PP', 'Pausing Point (1 day)', true),
    // FS3 — VCe / Split Digraphs → Suffix Doubling Rules
    makeUnitLesson(3, 1, 'a_e /ā/'),
    makeUnitLesson(3, 2, 'a_e /ā/ Review'),
    makeUnitLesson(3, 3, 'i_e /ī/'),
    makeUnitLesson(3, 4, 'i_e /ī/ Review'),
    makeUnitLesson(3, 5, 'o_e /ō/'),
    makeUnitLesson(3, 6, 'o_e /ō/ Review'),
    makeUnitLesson(3, 7, 'u_e /ū/'),
    makeUnitLesson(3, 8, 'Consonant Suffixes', true),
    makeUnitLesson(3, 9, 'Consonant Suffixes Review', true),
    makeUnitLesson(3, 10, 'Vowel Suffixes (drop e)'),
    makeUnitLesson(3, 11, 'Vowel Suffixes Review', true),
    makeUnitLesson(3, 12, 'Suffixes after VCe (drop e)'),
    makeUnitLesson(3, 13, 'Soft c, g + Suffixes'),
    makeUnitLesson(3, 14, 'Fluency Assessment', true),
    makeUnitLesson(3, 15, 'Reading & Spelling Assessment', true),
    makeUnitLesson(3, 'PP', 'Pausing Point (1 day)', true),
    // FS4 — Vowel Teams Review → Prefixes → Suffixes → Homophones
    makeUnitLesson(4, 1, 'ee, ea /ē/ Review'),
    makeUnitLesson(4, 2, 'ai, ay /ā/ Review'),
    makeUnitLesson(4, 3, 'ue /ū/ Review'),
    makeUnitLesson(4, 4, 'Long Vowel Teams Review (ee/ea, ai/ay, ue)'),
    makeUnitLesson(4, 5, 'un–'),
    makeUnitLesson(4, 6, 'dis–, mis–'),
    makeUnitLesson(4, 7, 're–'),
    makeUnitLesson(4, 8, 'pre–'),
    makeUnitLesson(4, 9, '–ist'),
    makeUnitLesson(4, 10, 'Affixes Review (un–, dis–, mis–, re–, pre–, –ist)'),
    makeUnitLesson(4, 11, '–al (review –ist, –ian, –al)'),
    makeUnitLesson(4, 12, 'Homophones', true),
    makeUnitLesson(4, 13, 'Homophones Review', true),
    makeUnitLesson(4, 14, 'Fluency Assessment', true),
    makeUnitLesson(4, 15, 'Reading & Spelling Assessment', true),
    makeUnitLesson(4, 'PP', 'Pausing Point (1 day)', true),
    // FS5 — R-Controlled Syllables → Suffix Family → VCCCV Review
    makeUnitLesson(5, 1, 'er, ir, ur /er/'),
    makeUnitLesson(5, 2, 'ar /ar/ + er, ir, ur Review'),
    makeUnitLesson(5, 3, 'ar /ar/ Review'),
    makeUnitLesson(5, 4, 'or /or/'),
    makeUnitLesson(5, 5, '–y'),
    makeUnitLesson(5, 6, '–ous'),
    makeUnitLesson(5, 7, '–ous Review'),
    makeUnitLesson(5, 8, '–ly'),
    makeUnitLesson(5, 9, '–ly Review'),
    makeUnitLesson(5, 10, '–ive'),
    makeUnitLesson(5, 11, 'VCCCV Syllables Review'),
    makeUnitLesson(5, 12, 'VCCCV Accent Shifts Review'),
    makeUnitLesson(5, 13, '–en + Suffix Review'),
    makeUnitLesson(5, 14, 'Fluency Assessment', true),
    makeUnitLesson(5, 15, 'Reading & Spelling Assessment (MOY)', true),
    makeUnitLesson(5, 'PP', 'Pausing Point (2 days, MOY)', true),
    // FS6 — Contractions → Final Stable Syllables → Suffix Spelling Rules
    makeUnitLesson(6, 1, "Contractions (n't, 's)"),
    makeUnitLesson(6, 2, "Contractions ('re, 'll, 'd) Review"),
    makeUnitLesson(6, 3, '–ble, –dle, –gle (consonant-le)'),
    makeUnitLesson(6, 4, '–sle, –zle, –ple'),
    makeUnitLesson(6, 5, '–cle, –kle, –ckle, –cal'),
    makeUnitLesson(6, 6, '3-Syllable Words Review'),
    makeUnitLesson(6, 7, '3-Syllable Words with C-le Review'),
    makeUnitLesson(6, 8, 'Final Stable Syllables Review'),
    makeUnitLesson(6, 9, '–ful, –less'),
    makeUnitLesson(6, 10, 'Suffix Rule: Double Final Consonant'),
    makeUnitLesson(6, 11, 'Suffix Rule: Drop e'),
    makeUnitLesson(6, 12, 'Suffix Rule: y → i'),
    makeUnitLesson(6, 13, 'Suffix Rules Review (double, drop e, y→i)'),
    makeUnitLesson(6, 14, 'Fluency Assessment', true),
    makeUnitLesson(6, 15, 'Reading & Spelling Assessment', true),
    makeUnitLesson(6, 'PP', 'Pausing Point (2 days)', true),
    // FS7 — Diphthongs → Homophones/Homographs → Suffixes -able/-ible/-ish
    makeUnitLesson(7, 1, 'aw, au /aw/'),
    makeUnitLesson(7, 2, 'aw, au /aw/ Review'),
    makeUnitLesson(7, 3, 'ow, ou /ou/'),
    makeUnitLesson(7, 4, 'ow, ou /ou/ Review'),
    makeUnitLesson(7, 5, 'oy, oi /oi/'),
    makeUnitLesson(7, 6, 'oy, oi /oi/ Review'),
    makeUnitLesson(7, 7, 'Diphthongs Review (/aw/, /ou/, /oi/)'),
    makeUnitLesson(7, 8, 'Homophones (ai/a_e, ee/ea)'),
    makeUnitLesson(7, 9, 'Common Homophones Review', true),
    makeUnitLesson(7, 10, 'Homographs', true),
    makeUnitLesson(7, 11, 'Homophones & Homographs Review'),
    makeUnitLesson(7, 12, '–able, –ible'),
    makeUnitLesson(7, 13, '–able, –ible, –ish Review'),
    makeUnitLesson(7, 14, 'Fluency Assessment', true),
    makeUnitLesson(7, 15, 'Reading & Spelling Assessment', true),
    makeUnitLesson(7, 'PP', 'Pausing Point (2 days)', true),
    // FS8 — Homophones → Vowel Teams → Abbreviations → Affixes anti-/in-/pro-
    makeUnitLesson(8, 1, 'Homophones (identify)', true),
    makeUnitLesson(8, 2, 'Homophones (use)', true),
    makeUnitLesson(8, 3, 'Homophones (spell)', true),
    makeUnitLesson(8, 4, 'ai, ay /ā/'),
    makeUnitLesson(8, 5, 'ai, ay /ā/ Review'),
    makeUnitLesson(8, 6, 'oo /ʊ/, /ū/'),
    makeUnitLesson(8, 7, 'oo Review'),
    makeUnitLesson(8, 8, 'ew, ue, ui /ū/'),
    makeUnitLesson(8, 9, 'ew, ue, ui /ū/ Review'),
    makeUnitLesson(8, 10, 'Abbreviations', true),
    makeUnitLesson(8, 11, 'anti–, in–'),
    makeUnitLesson(8, 12, 'pro–'),
    makeUnitLesson(8, 13, 'anti–, in–, pro– Review'),
    makeUnitLesson(8, 14, 'Fluency Assessment', true),
    makeUnitLesson(8, 15, 'Reading & Spelling Assessment', true),
    makeUnitLesson(8, 'PP', 'Pausing Point (2 days)', true),
    // FS9 — Synonyms/Antonyms → Affixes → Context Clues → Soft c/g → Digraphs kn/mb/wr → Suffix Rules
    makeUnitLesson(9, 1, 'Synonyms', true),
    makeUnitLesson(9, 2, 'Antonyms', true),
    makeUnitLesson(9, 3, 'im–, non–'),
    makeUnitLesson(9, 4, '–ful (meaning)'),
    makeUnitLesson(9, 5, 'Context Clues', true),
    makeUnitLesson(9, 6, 'Homophones (meaning)'),
    makeUnitLesson(9, 7, 'Homographs', true),
    makeUnitLesson(9, 8, 'Homographs (cont.)', true),
    makeUnitLesson(9, 9, 'Soft c (multisyllabic)'),
    makeUnitLesson(9, 10, 'Soft g (multisyllabic)'),
    makeUnitLesson(9, 11, 'kn, mb, wr (multisyllabic)'),
    makeUnitLesson(9, 12, 'Suffix Rule: Double Final Consonant Review'),
    makeUnitLesson(9, 13, 'Suffix Rules: Drop e + y→i Review'),
    makeUnitLesson(9, 14, 'Fluency Assessment', true),
    makeUnitLesson(9, 15, 'Reading & Spelling Assessment', true),
    makeUnitLesson(9, 'PP', 'Pausing Point (1 day)', true),
    // FS10 — eigh → Consonant-le → ough → Abbreviations → Compound Words → Affixes → Idioms / EOY
    makeUnitLesson(10, 1, 'eigh /ā/'),
    makeUnitLesson(10, 2, '–ble, –tle, –gle (final stable)'),
    makeUnitLesson(10, 3, '–ble, –tle, –gle Review'),
    makeUnitLesson(10, 4, 'ough'),
    makeUnitLesson(10, 5, 'Abbreviations Review'),
    makeUnitLesson(10, 6, 'Abbreviations Review (cont.)'),
    makeUnitLesson(10, 7, 'Compound Words', true),
    makeUnitLesson(10, 8, 'Compound Words Review', true),
    makeUnitLesson(10, 9, 'im–, in–, pre–'),
    makeUnitLesson(10, 10, '–ness, –y, –ful'),
    makeUnitLesson(10, 11, 'Affixes Review (im–, in–, pre–, –ness, –y, –ful)'),
    makeUnitLesson(10, 12, 'Idioms', true),
    makeUnitLesson(10, 13, 'Idioms Review', true),
    makeUnitLesson(10, 14, 'Fluency Assessment (EOY)', true),
    makeUnitLesson(10, 15, 'Reading & Spelling Assessment (EOY)', true),
    makeUnitLesson(10, 'PP', 'Pausing Point (1 day, EOY review)', true),
  ],
};

// UFLI Foundations uses flat lesson numbering (no Units). Lessons A–J are
// the Letter Introduction set; Lessons 1–128 are the main sequence with some
// sub-lettered halves (35a, 35b, 35c, 67a, 67b, etc.). All 148 are enabled —
// UFLI has no standalone assessments or pausing-point days.
const makeUFLI = (lessonNumber: number | string, skill: string): PhonicsObjective => ({
  lessonNumber,
  value: skill,
  label: `Lesson ${lessonNumber} - ${skill}`,
  disabled: isNonDecodingLesson(skill) || undefined,
  exampleWords: deriveExampleWords(skill),
});

export const UFLI_OBJECTIVES_BY_GRADE: Partial<Record<Grade, PhonicsObjective[]>> = {
  K: [
    // Letter Introduction (A–J)
    makeUFLI('A', 'Letter A'),
    makeUFLI('B', 'Letter B'),
    makeUFLI('C', 'Letter C'),
    makeUFLI('D', 'Letter D'),
    makeUFLI('E', 'Letter E'),
    makeUFLI('F', 'Letter F'),
    makeUFLI('G', 'Letter G'),
    makeUFLI('H', 'Letter H'),
    makeUFLI('I', 'Letter I'),
    makeUFLI('J', 'Letter J'),
    // Alphabet — Single Phonemes (1–34)
    makeUFLI(1, 'a /ă/'),
    makeUFLI(2, 'm /m/'),
    makeUFLI(3, 's /s/'),
    makeUFLI(4, 't /t/'),
    makeUFLI(5, 'VC & CVC Words'),
    makeUFLI(6, 'p /p/'),
    makeUFLI(7, 'f /f/'),
    makeUFLI(8, 'i /ĭ/'),
    makeUFLI(9, 'n /n/'),
    makeUFLI(10, 'CVC Practice (a, i)'),
    makeUFLI(11, 'Nasalized a (an, am)'),
    makeUFLI(12, 'o /ŏ/'),
    makeUFLI(13, 'd /d/'),
    makeUFLI(14, 'c /k/'),
    makeUFLI(15, 'u /ŭ/'),
    makeUFLI(16, 'g /g/'),
    makeUFLI(17, 'b /b/'),
    makeUFLI(18, 'e /ĕ/'),
    makeUFLI(19, 'Short Vowel Review'),
    makeUFLI(20, '-s /s/ (plural)'),
    makeUFLI(21, '-s /z/ (plural)'),
    makeUFLI(22, 'k /k/'),
    makeUFLI(23, 'h /h/'),
    makeUFLI(24, 'r /r/ Part 1'),
    makeUFLI(25, 'r /r/ Part 2'),
    makeUFLI(26, 'l /l/ Part 1'),
    makeUFLI(27, 'l /l/ Part 2'),
    makeUFLI(28, 'w /w/'),
    makeUFLI(29, 'j /j/'),
    makeUFLI(30, 'y /y/'),
    makeUFLI(31, 'x /ks/'),
    makeUFLI(32, 'qu /kw/'),
    makeUFLI(33, 'v /v/'),
    makeUFLI(34, 'z /z/'),
    // Alphabet Review & Longer Words (35–41)
    makeUFLI('35a', 'Short A Review'),
    makeUFLI('35b', 'Nasalized A Review (an, am)'),
    makeUFLI('35c', 'Short A Advanced Review'),
    makeUFLI('36a', 'Short I Review'),
    makeUFLI('36b', 'Short I Advanced Review'),
    makeUFLI('37a', 'Short O Review'),
    makeUFLI('37b', 'Short O Advanced Review'),
    makeUFLI('38a', 'Short A, I, O Review'),
    makeUFLI('38b', 'Short A, I, O Advanced Review'),
    makeUFLI('39a', 'Short U Review'),
    makeUFLI('39b', 'Short U Advanced Review'),
    makeUFLI('40a', 'Short E Review'),
    makeUFLI('40b', 'Short E Advanced Review'),
    makeUFLI('41a', 'Short Vowel Review (all)'),
    makeUFLI('41b', 'Short Vowel Review (all)'),
    makeUFLI('41c', 'Short Vowel Review (all)'),
    // Digraphs (42–44 placed in K per UFLI's K boundary)
    makeUFLI(42, 'FLSZ Spelling Rule'),
    makeUFLI(43, '-all, -oll, -ull'),
    makeUFLI(44, 'ck /k/'),
  ],
  '1': [
    // Digraphs (45–53)
    makeUFLI(45, 'sh /sh/'),
    makeUFLI(46, 'th /th/ (voiced)'),
    makeUFLI(47, 'th /th/ (unvoiced)'),
    makeUFLI(48, 'ch /ch/'),
    makeUFLI(49, 'Digraphs Review 1'),
    makeUFLI(50, 'wh /w/, ph /f/'),
    makeUFLI(51, 'ng /ŋ/'),
    makeUFLI(52, 'nk /ŋk/'),
    makeUFLI(53, 'Digraphs Review 2'),
    // VCe / Split Digraphs (54–62)
    makeUFLI(54, 'a_e /ā/'),
    makeUFLI(55, 'i_e /ī/'),
    makeUFLI(56, 'o_e /ō/'),
    makeUFLI(57, 'VCe Review 1; e_e /ē/'),
    makeUFLI(58, 'u_e /yū/'),
    makeUFLI(59, 'VCe Review 2'),
    makeUFLI(60, '_ce /s/'),
    makeUFLI(61, '_ge /j/'),
    makeUFLI(62, 'VCe Review 3; Exceptions'),
    // Reading Longer Words (63–68)
    makeUFLI(63, '-es'),
    makeUFLI(64, '-ed'),
    makeUFLI(65, '-ing'),
    makeUFLI(66, 'Syllables'),
    makeUFLI('67a', 'Compound Words'),
    makeUFLI('67b', 'Closed/Closed'),
    makeUFLI(68, 'Open and Closed'),
    // Ending Spelling Patterns (69–76)
    makeUFLI(69, 'tch /ch/'),
    makeUFLI(70, 'dge /j/'),
    makeUFLI(71, 'tch, dge Review'),
    makeUFLI(72, 'Long VCC: -ild, -old, -ind, -olt, -ost'),
    makeUFLI(73, 'y /ī/'),
    makeUFLI(74, 'y /ē/'),
    makeUFLI(75, '-le'),
    makeUFLI(76, 'Ending Patterns Review'),
    // R-Controlled Vowels (77–83)
    makeUFLI(77, 'ar /ar/'),
    makeUFLI(78, 'or /or/, ore /or/'),
    makeUFLI(79, 'ar, or, ore Review'),
    makeUFLI(80, 'er /er/'),
    makeUFLI(81, 'ir /er/, ur /er/'),
    makeUFLI(82, 'Spelling /er/: er, ir, ur, w + or'),
    makeUFLI(83, 'R-Controlled Vowels Review'),
    // Long Vowel Teams (84–88)
    makeUFLI(84, 'ai /ā/, ay /ā/'),
    makeUFLI(85, 'ee /ē/, ea /ē/, ey /ē/'),
    makeUFLI(86, 'oa /ō/, ow /ō/, oe /ō/'),
    makeUFLI(87, 'ie /ī/, igh /ī/'),
    makeUFLI(88, 'Vowel Teams Review 1'),
    // Other Vowel Teams (89–94)
    makeUFLI(89, 'u /oo/, oo /oo/'),
    makeUFLI(90, 'oo /ū/'),
    makeUFLI(91, 'ew /ū/, ui /ū/, ue /ū/'),
    makeUFLI(92, 'Vowel Teams Review 2'),
    makeUFLI(93, 'au /aw/, aw /aw/, augh /aw/'),
    makeUFLI(94, 'ea /ĕ/, a /ŏ/'),
    // Diphthongs and Silent Letters (95–98)
    makeUFLI(95, 'oi /oi/, oy /oi/'),
    makeUFLI(96, 'ou /ow/, ow /ow/'),
    makeUFLI(97, 'Vowel Teams and Diphthongs Review'),
    makeUFLI(98, 'kn /n/, wr /r/, mb /m/'),
  ],
  '2': [
    // Suffixes and Prefixes (99–106)
    makeUFLI(99, 'Suffixes; -s/-es'),
    makeUFLI(100, '-er, -est'),
    makeUFLI(101, '-ly'),
    makeUFLI(102, '-less, -ful'),
    makeUFLI(103, 'Prefixes; un-'),
    makeUFLI(104, 'pre-, re-'),
    makeUFLI(105, 'dis-'),
    makeUFLI(106, 'Affixes Review'),
    // Suffix Spelling Changes (107–110)
    makeUFLI(107, 'Doubling Rule: -ed, -ing'),
    makeUFLI(108, 'Doubling Rule: -er, -est'),
    makeUFLI(109, 'Drop E Rule'),
    makeUFLI(110, 'Y to I Rule'),
    // Low Frequency Spellings (111–118)
    makeUFLI(111, 'ar /er/, or /er/'),
    makeUFLI(112, 'air /air/, are /air/, ear /air/'),
    makeUFLI(113, 'ear /ear/'),
    makeUFLI(114, 'ei /ā/, eigh /ā/, ey /ā/, ea /ā/, aigh /ā/'),
    makeUFLI(115, 'ew /yū/, eu /yū/, ue /yū/, ou /ū/'),
    makeUFLI(116, 'ough /aw/, ough /ō/'),
    makeUFLI(117, 'Signal Vowels: c /s/, g /j/'),
    makeUFLI(118, 'ch /sh/, ch /k/, gn /n/, gh /g/, silent t'),
    // Additional Affixes (119–128)
    makeUFLI(119, '-sion, -tion'),
    makeUFLI(120, '-ture'),
    makeUFLI(121, '-er, -or, -ist'),
    makeUFLI(122, '-ish'),
    makeUFLI(123, '-y'),
    makeUFLI(124, '-ness'),
    makeUFLI(125, '-ment'),
    makeUFLI(126, '-able, -ible'),
    makeUFLI(127, 'bi-, tri-, uni-'),
    makeUFLI(128, 'Affixes Review 2'),
  ],
};

// Amplify CKLA Skills Strand — same Unit/Lesson hierarchy as Bluebonnet (which
// was derived from CKLA). Uses makeUnitLesson directly so labels match the
// `Unit X - Lesson Y - skill` format. K Units 1-2 are prerequisite skills
// (auditory awareness + prewriting) — all disabled per the source. Phonics
// begins at K Unit 3 with /m/. Pausing Points and assessments are disabled
// to preserve curriculum numbering.
export const CKLA_OBJECTIVES_BY_GRADE: Partial<Record<Grade, PhonicsObjective[]>> = {
  K: [
    // Unit 1 — Awareness of Noises/Words/Phrases + Prewriting (all disabled)
    // makeUnitLesson(1, 1, 'Awareness of Noises', true),
    // makeUnitLesson(1, 2, 'Awareness of Noises', true),
    // makeUnitLesson(1, 3, 'Awareness of Noises', true),
    // makeUnitLesson(1, 4, 'Blending Pretest', true),
    // makeUnitLesson(1, 5, 'Awareness of Noises and Words', true),
    // makeUnitLesson(1, 6, 'Awareness of Noises, Words, Phrases', true),
    // makeUnitLesson(1, 7, 'Awareness + Tracking', true),
    // makeUnitLesson(1, 8, 'Awareness of Words', true),
    // makeUnitLesson(1, 9, 'Awareness of Words', true),
    // makeUnitLesson(1, 10, 'Tracking Assessment', true),
    // makeUnitLesson(1, 'PP', 'Pausing Point', true),
    // // Unit 2 — Blending + Prewriting (all disabled)
    // makeUnitLesson(2, 1, 'Tracking Practice', true),
    // makeUnitLesson(2, 2, 'Blending Syllables and Sounds', true),
    // makeUnitLesson(2, 3, 'Blending Sounds into Words', true),
    // makeUnitLesson(2, 4, 'Blending Sounds into Words', true),
    // makeUnitLesson(2, 5, 'Blending Sounds into Words', true),
    // makeUnitLesson(2, 6, 'Blending Sounds into Words', true),
    // makeUnitLesson(2, 7, 'Blending Sounds into Words', true),
    // makeUnitLesson(2, 8, 'Blending Sounds into Words', true),
    // makeUnitLesson(2, 9, 'Blending Sounds into Words', true),
    // makeUnitLesson(2, 10, 'Sound Blending Assessment', true),
    // makeUnitLesson(2, 'PP', 'Pausing Point', true),
    // Unit 3 — First sounds: /m/ /a/ /t/ /d/ /o/ /c/ /g/ /i/
    makeUnitLesson(3, 1, `/m/ > 'm'`),
    makeUnitLesson(3, 2, `/a/ > 'a'`),
    makeUnitLesson(3, 3, `/t/ > 't' + chaining`),
    makeUnitLesson(3, 4, `/d/ > 'd' + chaining`),
    makeUnitLesson(3, 5, 'Review oral blending + sound spelling'),
    makeUnitLesson(3, 6, `/o/ > 'o' + chaining`),
    makeUnitLesson(3, 7, `/k/ > 'c' + chaining`),
    makeUnitLesson(3, 8, `/g/ > 'g' + chaining`),
    makeUnitLesson(3, 9, `/i/ > 'i' + chaining`),
    makeUnitLesson(3, 10, 'Review oral blending + sound spelling'),
    makeUnitLesson(3, 11, 'Reading Assessment', true),
    makeUnitLesson(3, 12, 'Reading Assessment', true),
    makeUnitLesson(3, 13, 'Tricky Words: one, two', true),
    makeUnitLesson(3, 14, 'Tricky Word: three', true),
    makeUnitLesson(3, 'PP', 'Pausing Point', true),
    // Unit 4 — More sounds: /n/ /h/ /s/ /f/ /v/ /z/ /p/ /e/
    makeUnitLesson(4, 1, `/n/ > 'n' + chaining`),
    makeUnitLesson(4, 2, `/h/ > 'h' + chaining`),
    makeUnitLesson(4, 3, `/s/ > 's' + chaining`),
    makeUnitLesson(4, 4, `/f/ > 'f' + chaining`),
    makeUnitLesson(4, 5, 'Practice reading words'),
    makeUnitLesson(4, 6, `/v/ > 'v' + chaining`),
    makeUnitLesson(4, 7, `/z/ > 'z' + chaining`),
    makeUnitLesson(4, 8, `/p/ > 'p' + chaining`),
    makeUnitLesson(4, 9, `/e/ > 'e' + chaining`),
    makeUnitLesson(4, 10, 'Introduction to Phrases'),
    makeUnitLesson(4, 11, 'Reading Assessment', true),
    makeUnitLesson(4, 12, 'Reading Assessment', true),
    makeUnitLesson(4, 13, 'Reading Assessment', true),
    makeUnitLesson(4, 14, 'Tricky Word: the', true),
    makeUnitLesson(4, 15, 'Tricky Word: a', true),
    makeUnitLesson(4, 'PP', 'Pausing Point', true),
    // Unit 5 — More consonants + /u/ + Spelling Alt 'k'
    makeUnitLesson(5, 1, `/b/ > 'b', /d/ > 'd' review`),
    makeUnitLesson(5, 2, `/l/ > 'l'`),
    makeUnitLesson(5, 3, `/r/ > 'r'`),
    makeUnitLesson(5, 4, `/u/ > 'u'`),
    makeUnitLesson(5, 5, 'Tricky Word: blue', true),
    makeUnitLesson(5, 6, `/w/ > 'w'`),
    makeUnitLesson(5, 7, `/j/ > 'j'`),
    makeUnitLesson(5, 8, `/y/ > 'y' + Tricky Word: yellow`),
    makeUnitLesson(5, 9, `/x/ > 'x'`),
    makeUnitLesson(5, 10, `/k/ > 'k' (alt)`),
    makeUnitLesson(5, 11, 'Tricky Word: look', true),
    makeUnitLesson(5, 12, 'Reading Assessment', true),
    makeUnitLesson(5, 13, 'Reading Assessment', true),
    makeUnitLesson(5, 14, 'Reading Assessment', true),
    makeUnitLesson(5, 15, 'Reading Assessment', true),
    makeUnitLesson(5, 16, 'Reading Assessment', true),
    makeUnitLesson(5, 'PP', 'Pausing Point', true),
    // Unit 6 — Letter names, /z/ > 's', consonant clusters
    makeUnitLesson(6, 1, 'Alphabet/Letter Names + Tricky Word: I'),
    makeUnitLesson(6, 2, 'Alphabet/Letter Names + clusters'),
    makeUnitLesson(6, 3, `/z/ > 's'`),
    makeUnitLesson(6, 4, '/s/ vs /z/'),
    makeUnitLesson(6, 5, 'Alphabet/Letter Names + reading'),
    makeUnitLesson(6, 6, 'Tricky Word: are', true),
    makeUnitLesson(6, 7, 'Reading + clusters', true),
    makeUnitLesson(6, 8, 'Demonstration Story + Small Group', true),
    makeUnitLesson(6, 9, 'Rhyming Words', true),
    makeUnitLesson(6, 10, 'Demonstration Story + Small Group', true),
    makeUnitLesson(6, 11, 'Review Rhyming + Tricky Word: little'),
    makeUnitLesson(6, 12, 'Demonstration Story + Partner Reading', true),
    makeUnitLesson(6, 13, 'Review Rhyming', true),
    makeUnitLesson(6, 14, 'Review: Letter Names + Rhyming', true),
    makeUnitLesson(6, 15, 'Review: Letter Names', true),
    makeUnitLesson(6, 16, 'Demonstration Story', true),
    makeUnitLesson(6, 17, 'Letter Names/Rhyming/Clusters Assessment', true),
    makeUnitLesson(6, 'PP', 'Pausing Point', true),
    // Unit 7 — Digraphs: ch, sh, th, qu, ng
    makeUnitLesson(7, 1, `/ch/ > 'ch'`),
    makeUnitLesson(7, 2, `/sh/ > 'sh'`),
    makeUnitLesson(7, 3, '/ch/ /sh/ Review + Tricky Word: down'),
    makeUnitLesson(7, 4, `/th/ (voiceless) > 'th'`),
    makeUnitLesson(7, 5, `/th/ (voiced) > 'th'`),
    makeUnitLesson(7, 6, 'Review digraphs ch, sh, th'),
    makeUnitLesson(7, 7, `/qu/ > 'qu'`),
    makeUnitLesson(7, 8, `/ng/ > 'ng'`),
    makeUnitLesson(7, 9, 'Tricky Word: out + segmenting'),
    makeUnitLesson(7, 10, 'Demonstration Story + Partner Reading', true),
    makeUnitLesson(7, 11, 'Tricky Word: of + Reading Assessment', true),
    makeUnitLesson(7, 12, 'Reading Assessment', true),
    makeUnitLesson(7, 13, 'Demonstration Story + Partner Reading', true),
    makeUnitLesson(7, 14, 'Reading Assessment', true),
    makeUnitLesson(7, 15, 'Reading Assessment', true),
    makeUnitLesson(7, 16, 'Reading Assessment', true),
    makeUnitLesson(7, 17, 'Reading Assessment', true),
    makeUnitLesson(7, 'PP', 'Pausing Point', true),
    // Unit 8 — Tricky Words + Double-Letter Spellings
    makeUnitLesson(8, 1, 'Tricky Word: funny', true),
    makeUnitLesson(8, 2, 'Tricky Word: all', true),
    makeUnitLesson(8, 3, 'Review Tricky Words', true),
    makeUnitLesson(8, 4, 'Review Tricky Words + Rhyming', true),
    makeUnitLesson(8, 5, 'Tricky Word: from', true),
    makeUnitLesson(8, 6, 'Demonstration Story + Partner Reading'),
    makeUnitLesson(8, 7, 'Small Group + Partner Reading', true),
    makeUnitLesson(8, 8, 'Tricky Word: was', true),
    makeUnitLesson(8, 9, 'Review Tricky Words + Rhyming', true),
    makeUnitLesson(8, 10, 'Double-Letter Spellings'),
    makeUnitLesson(8, 11, 'Chaining + Partner Reading', true),
    makeUnitLesson(8, 12, 'Double-Letter Spellings'),
    makeUnitLesson(8, 13, 'Practice reading sounds, words, sentences', true),
    makeUnitLesson(8, 14, 'Practice reading phrases', true),
    makeUnitLesson(8, 15, 'Word Recognition Assessment', true),
    makeUnitLesson(8, 16, 'Lowercase Letter Name + Reading Assessment', true),
    makeUnitLesson(8, 17, 'Tricky Word + Code Knowledge Diagnostic', true),
    makeUnitLesson(8, 18, 'Story Reading + Code Knowledge Diagnostic', true),
    makeUnitLesson(8, 19, 'Demonstration Story + Partner Reading'),
    makeUnitLesson(8, 20, 'Review Rhyming Words', true),
    makeUnitLesson(8, 'PP', 'Pausing Point', true),
    // Unit 9 — Tricky Words + Uppercase Letters
    makeUnitLesson(9, 1, 'Tricky Words: when, word + Uppercase A-D', true),
    makeUnitLesson(9, 2, 'Tricky Words: why, to', true),
    makeUnitLesson(9, 3, 'Uppercase E-H'),
    makeUnitLesson(9, 4, 'Review Uppercase Letters'),
    makeUnitLesson(9, 5, 'Tricky Words: where, no', true),
    makeUnitLesson(9, 6, 'Uppercase I-M'),
    makeUnitLesson(9, 7, 'Tricky Words: what, so + Uppercase N-R', true),
    makeUnitLesson(9, 8, 'Review Tricky Words', true),
    makeUnitLesson(9, 9, 'Review Tricky Words', true),
    makeUnitLesson(9, 10, 'Tricky Word Recognition Assessment', true),
    makeUnitLesson(9, 11, 'Review Uppercase + Tricky Word: once'),
    makeUnitLesson(9, 12, 'Uppercase X-Z'),
    makeUnitLesson(9, 13, 'Tricky Words: said, says', true),
    makeUnitLesson(9, 14, 'Small Group + Partner Reading', true),
    makeUnitLesson(9, 15, 'Reading Wiggle Cards + Sentences'),
    makeUnitLesson(9, 16, 'Tricky Word: were', true),
    makeUnitLesson(9, 17, 'Tricky Words: here, there', true),
    makeUnitLesson(9, 18, 'Review Tricky Words', true),
    makeUnitLesson(9, 19, 'Small Group + Partner Reading'),
    makeUnitLesson(9, 20, 'Tricky Word + Uppercase + Punctuation Assessment', true),
    makeUnitLesson(9, 21, 'Reading Assessment', true),
    makeUnitLesson(9, 22, 'Reading Assessment', true),
    makeUnitLesson(9, 23, 'Reading Assessment', true),
    makeUnitLesson(9, 'PP', 'Pausing Point', true),
    // Unit 10 — Long vowels (Magic-e) + EOY assessments
    makeUnitLesson(10, 1, `/ee/ > 'ee'`),
    makeUnitLesson(10, 2, 'Small Group + Partner Reading (ee)'),
    makeUnitLesson(10, 3, 'Long vowel chaining + Tricky Words: he, she, we'),
    makeUnitLesson(10, 4, '/e/ vs /ee/ practice'),
    makeUnitLesson(10, 5, 'Tricky Words: be, me', true),
    makeUnitLesson(10, 6, 'Review Tricky Words', true),
    makeUnitLesson(10, 7, `/ae/ > 'a_e'`),
    makeUnitLesson(10, 8, 'Tricky Words: they, their', true),
    makeUnitLesson(10, 9, 'Whole Group Reading'),
    makeUnitLesson(10, 10, 'Long vs short vowel dictation'),
    makeUnitLesson(10, 11, `/ie/ > 'i_e'`),
    makeUnitLesson(10, 12, 'Small Group + Partner Reading (i_e)'),
    makeUnitLesson(10, 13, 'Tricky Word: my', true),
    makeUnitLesson(10, 14, 'Practice reading Magic-e words'),
    makeUnitLesson(10, 15, 'Tricky Word: by', true),
    makeUnitLesson(10, 16, 'Review Tricky Words', true),
    makeUnitLesson(10, 17, `/oe/ > 'o_e'`),
    makeUnitLesson(10, 18, 'Small Group + Partner Reading (o_e)'),
    makeUnitLesson(10, 19, 'Review Sound Spelling', true),
    makeUnitLesson(10, 20, 'Long-vowel dictation'),
    makeUnitLesson(10, 21, 'Small Group + Partner Reading', true),
    makeUnitLesson(10, 22, `/ue/ > 'u_e'`),
    makeUnitLesson(10, 23, 'Tricky Words: you, your', true),
    makeUnitLesson(10, 24, 'Whole Group Reading', true),
    makeUnitLesson(10, 25, 'Reading Wiggle Cards', true),
    makeUnitLesson(10, 26, 'EOY Word Reading Assessment', true),
    makeUnitLesson(10, 27, 'EOY Letter Sounds Assessment', true),
    makeUnitLesson(10, 28, 'EOY Uppercase Letters + Letter Sounds Assessment', true),
    makeUnitLesson(10, 29, 'Small Group + Partner Reading'),
    makeUnitLesson(10, 'PP', 'Pausing Point', true),
  ],
  '1': [
    // Unit 1 — Review of K basic code
    makeUnitLesson(1, 1, '/p/ /k/ /g/ /n/ /a/ review'),
    makeUnitLesson(1, 2, '/i/ /o/ /t/ /d/ review'),
    makeUnitLesson(1, 3, 'Tricky Words: a, I + /e/ /u/', true),
    makeUnitLesson(1, 4, '/m/ /f/ /v/ review'),
    makeUnitLesson(1, 5, '/s/ /z/ /h/ + Tricky Words: no, so, of'),
    makeUnitLesson(1, 6, 'Word Recognition Assessment', true),
    makeUnitLesson(1, 7, 'Reading Assessment', true),
    makeUnitLesson(1, 8, 'Reading Assessment', true),
    makeUnitLesson(1, 9, 'Reading Assessment', true),
    makeUnitLesson(1, 10, 'Reading Assessment', true),
    makeUnitLesson(1, 11, 'Sister Sounds /s/-/z/, /f/-/v/ + Tricky Words: is, to'),
    makeUnitLesson(1, 12, '/b/ /l/ /r/ review'),
    makeUnitLesson(1, 13, '/w/ /e/ /u/ + Tricky Words: all, some'),
    makeUnitLesson(1, 14, 'Tricky Words: from, word', true),
    makeUnitLesson(1, 15, '/j/ /y/ + Tricky Words: are, have, were'),
    makeUnitLesson(1, 16, '/x/ /k/ + Tricky Words: one, once'),
    makeUnitLesson(1, 17, '/ch/ /sh/ + Tricky Words: do, two'),
    makeUnitLesson(1, 18, 'Sister Sounds /th/ vs /th/'),
    makeUnitLesson(1, 19, 'Tricky Words: the, who', true),
    makeUnitLesson(1, 20, '/ng/ /qu/ + Tricky Words: said, says'),
    makeUnitLesson(1, 21, 'Review vowel sounds'),
    makeUnitLesson(1, 22, 'Review consonant + vowel sounds'),
    makeUnitLesson(1, 23, `Double letters 'ck' 'bb' 'dd' 'ff' 'gg' 'll' 'ss'`),
    makeUnitLesson(1, 24, `Double letters 'cc' 'nn' 'pp' 'rr' 'tt' 'zz' + /k/ alts`),
    makeUnitLesson(1, 25, 'Sister Sounds /s/-/z/ + Tricky Words: was, when, why'),
    makeUnitLesson(1, 26, 'Demonstration Story + Small Group'),
    makeUnitLesson(1, 27, 'Tricky Words: where, what, which', true),
    makeUnitLesson(1, 28, 'Partner Reading + Tricky Words review', true),
    makeUnitLesson(1, 29, 'Tricky Words: here, there', true),
    makeUnitLesson(1, 30, 'Review letter names + Demonstration Story'),
    makeUnitLesson(1, 31, 'Review Tricky Words', true),
    makeUnitLesson(1, 32, 'Review Tricky Words', true),
    makeUnitLesson(1, 'PP', 'Pausing Point', true),
    // Unit 2 — VCe long vowel digraphs (a_e, i_e, o_e, u_e, ee)
    makeUnitLesson(2, 1, `/ee/ > 'ee'`),
    makeUnitLesson(2, 2, 'Long vowel chaining + Tricky Words: he, she, be, me, we'),
    makeUnitLesson(2, 3, `/ae/ > 'a_e'`),
    makeUnitLesson(2, 4, 'Review vowel digraphs'),
    makeUnitLesson(2, 5, 'Tricky Words: they, their', true),
    makeUnitLesson(2, 6, `/ie/ > 'i_e'`),
    makeUnitLesson(2, 7, 'Review Tricky Words + Demo Story', true),
    makeUnitLesson(2, 8, `/oe/ > 'o_e'`),
    makeUnitLesson(2, 9, 'Small Group Reading', true),
    makeUnitLesson(2, 10, 'Tricky Words: my, by', true),
    makeUnitLesson(2, 11, 'Review Tricky Words', true),
    makeUnitLesson(2, 12, `/ue/ > 'u_e'`),
    makeUnitLesson(2, 13, 'Partner Reading', true),
    makeUnitLesson(2, 14, 'Tricky Words: you, your', true),
    makeUnitLesson(2, 15, 'Review Tricky Words + sound spelling', true),
    makeUnitLesson(2, 16, 'Review sound spelling + Tricky Words', true),
    makeUnitLesson(2, 17, 'Partner Reading + Sound Spelling review'),
    makeUnitLesson(2, 18, 'Word Recognition Assessment', true),
    makeUnitLesson(2, 19, 'Small Group Reading', true),
    makeUnitLesson(2, 'PP', 'Pausing Point', true),
    // Unit 3 — /oo/ /ou/ /oi/ /aw/
    makeUnitLesson(3, 1, `/oo/ > 'oo'`),
    makeUnitLesson(3, 2, 'Tricky Word: because', true),
    makeUnitLesson(3, 3, 'Review /oo/'),
    makeUnitLesson(3, 4, 'Small Group Reading (oo)'),
    makeUnitLesson(3, 5, 'Review Tricky Words + /oo/', true),
    makeUnitLesson(3, 6, 'Small Group Reading', true),
    makeUnitLesson(3, 7, `Tricky Spelling 'oo' + Tricky Words: could, should, would`),
    makeUnitLesson(3, 8, 'Identifying Verbs', true),
    makeUnitLesson(3, 9, `/ou/ > 'ou'`),
    makeUnitLesson(3, 10, 'Tricky Word: down', true),
    makeUnitLesson(3, 11, 'Chaining + review Tricky Words', true),
    makeUnitLesson(3, 12, `/oi/ > 'oi'`),
    makeUnitLesson(3, 13, 'Review sounds', true),
    makeUnitLesson(3, 14, 'Small Group Reading', true),
    makeUnitLesson(3, 15, `/aw/ > 'aw'`),
    makeUnitLesson(3, 16, 'Review Tricky Words', true),
    makeUnitLesson(3, 17, 'Small Group/Partner Reading'),
    makeUnitLesson(3, 18, 'Review Tricky Words + chaining', true),
    makeUnitLesson(3, 19, 'Word Recognition + Story Comprehension Assessment', true),
    makeUnitLesson(3, 'PP', 'Pausing Point', true),
    // Unit 4 — R-controlled vowels (er, ar, or) + 2-syllable words
    makeUnitLesson(4, 1, `/er/ > 'er'`),
    makeUnitLesson(4, 2, 'Review /er/'),
    makeUnitLesson(4, 3, 'Vowel digraph spellings review'),
    makeUnitLesson(4, 4, `Medial sounds + /ar/ > 'ar'`),
    makeUnitLesson(4, 5, 'Review medial sounds'),
    makeUnitLesson(4, 6, 'Reading Wiggle Cards', true),
    makeUnitLesson(4, 7, `Minimal pairs + /or/ > 'or'`),
    makeUnitLesson(4, 8, '2-syllable words + Tricky Words: yesterday, today, tomorrow'),
    makeUnitLesson(4, 9, '2-syllable words'),
    makeUnitLesson(4, 10, '2-syllable chaining'),
    makeUnitLesson(4, 11, '2-syllable chaining'),
    makeUnitLesson(4, 12, 'Reading Wiggle Cards + Small Group', true),
    makeUnitLesson(4, 13, 'Small Group Reading', true),
    makeUnitLesson(4, 14, 'Clapping + 2-syllable words'),
    makeUnitLesson(4, 15, 'Partner Reading', true),
    makeUnitLesson(4, 16, 'Whole Group Reading', true),
    makeUnitLesson(4, 17, 'Small Group Reading', true),
    makeUnitLesson(4, 18, '2-syllable chaining with -ness, -less'),
    makeUnitLesson(4, 19, 'Partner Reading', true),
    makeUnitLesson(4, 20, 'Review /ar/ /or/ /er/'),
    makeUnitLesson(4, 21, 'Small Group Reading', true),
    makeUnitLesson(4, 22, 'Past-Tense Marker -ed', true),
    makeUnitLesson(4, 23, 'Small Group Reading', true),
    makeUnitLesson(4, 24, 'Review word reading', true),
    makeUnitLesson(4, 25, 'Grammar Assessment', true),
    makeUnitLesson(4, 26, 'Reading Comprehension Assessment', true),
    makeUnitLesson(4, 27, 'Reading Comprehension Assessment', true),
    makeUnitLesson(4, 28, 'Reading Comprehension Assessment', true),
    makeUnitLesson(4, 'PP', 'Pausing Point', true),
    // Unit 5 — Consonant spelling alternatives
    makeUnitLesson(5, 1, '/p/ > p, pp (alts)'),
    makeUnitLesson(5, 2, 'Tricky Word: how', true),
    makeUnitLesson(5, 3, '/b/ > b, bb (alts)'),
    makeUnitLesson(5, 4, '/k/ > c, k, cc, ck (alts)'),
    makeUnitLesson(5, 5, 'Review /k/ alts'),
    makeUnitLesson(5, 6, '/ch/ > ch, tch (alts)'),
    makeUnitLesson(5, 7, '/g/ > g, gg (alts)'),
    makeUnitLesson(5, 8, '/j/ > j, g, ge (alts)'),
    makeUnitLesson(5, 9, `'g' > /g/ or /j/ (tricky)`),
    makeUnitLesson(5, 10, 'Review sound spelling', true),
    makeUnitLesson(5, 11, '/t/ > t, tt, ed (alts)'),
    makeUnitLesson(5, 12, '/d/ > d, dd, ed (alts) + Tricky Word: (stage)coach'),
    makeUnitLesson(5, 13, '/f/ > f, ff (alts)'),
    makeUnitLesson(5, 14, '/v/ > v, ve (alts) + Tricky Word: picture'),
    makeUnitLesson(5, 15, 'Partner Reading', true),
    makeUnitLesson(5, 16, '/r/ > r, rr, wr (alts)'),
    makeUnitLesson(5, 17, 'Reading Wiggle Cards + Small Group', true),
    makeUnitLesson(5, 18, 'Editing an Opinion Paragraph', true),
    makeUnitLesson(5, 19, '/l/ > l, ll (alts)'),
    makeUnitLesson(5, 20, 'Review Tricky Words + Wiggle Cards', true),
    makeUnitLesson(5, 21, 'Review 2-syllable words'),
    makeUnitLesson(5, 22, 'Word Recognition + Grammar Assessment', true),
    makeUnitLesson(5, 'PP', 'Pausing Point', true),
    // Unit 6 — Spelling alternatives for /s/ /n/ /w/ /ng/ + Tricky Spellings
    makeUnitLesson(6, 1, `/s/ > 'c' (alt)`),
    makeUnitLesson(6, 2, `'c' > /s/ or /k/ (tricky)`),
    makeUnitLesson(6, 3, `Review Tricky Spelling 'c'`),
    makeUnitLesson(6, 4, '/s/ > ce, se (alts)'),
    makeUnitLesson(6, 5, 'Spelling Assessment', true),
    makeUnitLesson(6, 6, `Review /s/ alts + Tricky Spelling 'c'`),
    makeUnitLesson(6, 7, 'Adjectives, Prepositions and Pronouns', true),
    makeUnitLesson(6, 8, `Review Tricky Spelling 'g' + /j/ alts`),
    makeUnitLesson(6, 9, 'Nouns and Pronouns', true),
    makeUnitLesson(6, 10, `/n/ > 'kn' (alt)`),
    makeUnitLesson(6, 11, 'Review /s/ + /n/ alts'),
    makeUnitLesson(6, 12, 'Review Tricky Words', true),
    makeUnitLesson(6, 13, 'Nouns and Pronouns', true),
    makeUnitLesson(6, 14, 'Building Sentences', true),
    makeUnitLesson(6, 15, 'Plural Nouns review', true),
    makeUnitLesson(6, 16, `/w/ > 'wh' (alt)`),
    makeUnitLesson(6, 17, 'Partner Reading', true),
    makeUnitLesson(6, 18, `/ng/ > 'n' (alt)`),
    makeUnitLesson(6, 19, `'n' > /n/ or /ng/ (tricky)`),
    makeUnitLesson(6, 20, 'Pronouns review', true),
    makeUnitLesson(6, 21, 'Review root words + endings'),
    makeUnitLesson(6, 22, 'Small Group Reading', true),
    makeUnitLesson(6, 23, 'Tricky Word: cow', true),
    makeUnitLesson(6, 24, 'Word Recognition Assessment', true),
    makeUnitLesson(6, 25, 'Story Comprehension Assessment', true),
    makeUnitLesson(6, 'PP', 'Pausing Point', true),
    // Unit 7 — /ae/ + /oe/ alts + EOY assessments
    makeUnitLesson(7, 1, '/ae/ alts (ai, ay, a_e)'),
    makeUnitLesson(7, 2, '1-syllable chaining /ae/'),
    makeUnitLesson(7, 3, 'Small Group Reading', true),
    makeUnitLesson(7, 4, 'Small Group Reading', true),
    makeUnitLesson(7, 5, 'Reading Comprehension + Small Group', true),
    makeUnitLesson(7, 6, 'Small Group Reading', true),
    makeUnitLesson(7, 7, 'Partner Reading', true),
    makeUnitLesson(7, 8, 'Small Group Reading', true),
    makeUnitLesson(7, 9, 'Partner Reading', true),
    makeUnitLesson(7, 10, 'Small Group Reading', true),
    makeUnitLesson(7, 11, '/oe/ alts (oa, oe, o_e)'),
    makeUnitLesson(7, 12, 'Small Group Reading', true),
    makeUnitLesson(7, 13, 'Whole Group Reading', true),
    makeUnitLesson(7, 14, 'Small Group Reading', true),
    makeUnitLesson(7, 15, 'Small Group Reading', true),
    makeUnitLesson(7, 16, 'Partner Reading', true),
    makeUnitLesson(7, 17, 'Small Group Reading', true),
    makeUnitLesson(7, 18, 'Small Group Reading', true),
    makeUnitLesson(7, 19, 'Silent Reading Comprehension Assessment', true),
    makeUnitLesson(7, 20, 'Fluency Assessment', true),
    makeUnitLesson(7, 21, 'Word Reading in Isolation Assessment', true),
    makeUnitLesson(7, 'PP', 'Pausing Point', true),
  ],
  '2': [
    // Unit 1 — Basic code review + double letters + Tricky Spellings
    makeUnitLesson(1, 1, 'Basic code /a/ /i/ /p/ /b/ /t/ /d/'),
    makeUnitLesson(1, 2, 'Basic code /o/ /e/ /u/ /k/ /g/'),
    makeUnitLesson(1, 3, 'Basic code /k/ /j/ /v/ /f/ /h/ /l/'),
    makeUnitLesson(1, 4, 'Basic code /th/ /n/ /ng/ /sh/ /ch/'),
    makeUnitLesson(1, 5, 'Basic code /s/ /z/ /m/ /w/ /r/ /y/ /x/ /qu/'),
    makeUnitLesson(1, 6, 'Story Reading Assessment', true),
    makeUnitLesson(1, 7, 'Story Reading + Word Reading Assessment', true),
    makeUnitLesson(1, 8, 'Story Reading + Word Reading Assessment', true),
    makeUnitLesson(1, 9, 'Story Reading + Word Reading Assessment', true),
    makeUnitLesson(1, 10, 'Word Reading Assessment', true),
    makeUnitLesson(
      1,
      11,
      'Tricky Words: the, he, she, we, be, me + double letters',
      true,
    ),
    makeUnitLesson(1, 12, 'Tricky Words: was, of, a + double letters', true),
    makeUnitLesson(1, 13, '2-syllable words'),
    makeUnitLesson(1, 14, `Tricky Words: do, down, how, to + 'g' tricky`, true),
    makeUnitLesson(1, 15, `'c' > /k/ or /s/ (tricky)`),
    makeUnitLesson(1, 16, 'Spelling alts qu, wh, wr, kn'),
    makeUnitLesson(1, 17, 'Spelling alts ge, ve + Tricky Words: what, where, why, from'),
    makeUnitLesson(1, 18, 'Tricky Words: once, one + spelling alts se, ce, tch', true),
    makeUnitLesson(1, 19, 'Small Group Reading', true),
    makeUnitLesson(1, 20, `'s' > /s/ or /z/ (tricky)`),
    makeUnitLesson(
      1,
      21,
      `'n' > /n/ or /ng/ (tricky) + Tricky Words: could, would, should`,
    ),
    makeUnitLesson(1, 22, 'Skills + Spelling Assessment', true),
    makeUnitLesson(1, 'PP', 'Pausing Point', true),
    // Unit 2 — Basic code /ae/ /ie/ /oe/ /ue/ /ee/ + /ou/ /oy/ + /er/ /or/ /ar/
    makeUnitLesson(
      2,
      1,
      'Basic code /ae/ /ie/ + Magic-e + Tricky Words: I, you, your, street',
    ),
    makeUnitLesson(2, 2, 'Basic code /oe/ /ue/ + Tricky Words: my, by, have'),
    makeUnitLesson(2, 3, 'Basic code /ee/ + Magic-e'),
    makeUnitLesson(2, 4, 'Tricky Words: all, who', true),
    makeUnitLesson(2, 5, `'oo' tricky + Tricky Words: no, go, so`),
    makeUnitLesson(2, 6, 'Tricky Words: are, were, some', true),
    makeUnitLesson(2, 7, '/ou/ > ou, ow'),
    makeUnitLesson(2, 8, '/oy/ > oi, oy'),
    makeUnitLesson(2, 9, 'Close Reading', true),
    makeUnitLesson(2, 10, 'Tricky Words: they, their + /er/ basic code', true),
    makeUnitLesson(2, 11, '/or/ /ar/ basic code'),
    makeUnitLesson(2, 12, 'Drafting a Narrative Book Report', true),
    makeUnitLesson(2, 13, 'Antonyms', true),
    makeUnitLesson(2, 14, 'Antonyms', true),
    makeUnitLesson(2, 15, 'Whole Group Reading', true),
    makeUnitLesson(2, 16, 'Reading Comprehension + WCPM Assessment', true),
    makeUnitLesson(2, 'PP', 'Pausing Point', true),
    // Unit 3 — Spelling alternatives for /ae/ /oe/ /ie/ /ue/ /aw/
    makeUnitLesson(3, 1, '/ae/ alts'),
    makeUnitLesson(3, 2, '/ae/ > a_e, ai, ay'),
    makeUnitLesson(3, 3, `/ae/ > 'a' + 'a' tricky`),
    makeUnitLesson(3, 4, `Review /s/ /j/ alts + 'a' tricky`),
    makeUnitLesson(3, 5, 'Review spelling alts', true),
    makeUnitLesson(3, 6, '/oe/ alts'),
    makeUnitLesson(3, 7, '/oe/ > oa, oe'),
    makeUnitLesson(3, 8, `/oe/ > 'o' + 'o' tricky`),
    makeUnitLesson(3, 9, `Review kn, wr, wh + 'o' tricky`),
    makeUnitLesson(3, 10, 'Review spelling alts', true),
    makeUnitLesson(3, 11, '/ie/ alts'),
    makeUnitLesson(3, 12, '/ie/ > ie, i'),
    makeUnitLesson(3, 13, `'i' tricky + review`),
    makeUnitLesson(3, 14, 'Partner/Independent Reading', true),
    makeUnitLesson(3, 15, 'Dictation Decoding Assessment', true),
    makeUnitLesson(3, 16, '/ue/ alts'),
    makeUnitLesson(3, 17, '/ue/ > u, u_e, ue'),
    makeUnitLesson(3, 18, 'Review /ue/ alts'),
    makeUnitLesson(3, 19, '/aw/ > aw, au'),
    makeUnitLesson(3, 20, `/aw/ > 'augh'`),
    makeUnitLesson(3, 21, 'Review /aw/ alts'),
    makeUnitLesson(3, 22, 'Action Verbs', true),
    makeUnitLesson(3, 23, 'Action Verbs', true),
    makeUnitLesson(3, 24, 'Edit Personal Narrative', true),
    makeUnitLesson(3, 25, 'Reading Comp + Noun-Verb Assessment', true),
    makeUnitLesson(3, 'PP', 'Pausing Point', true),
    // Unit 4 — /er/ /ie/ /oe/ /ee/ /aw/ alternatives + 'to be'
    makeUnitLesson(4, 1, '/er/ > er, ur, ir'),
    makeUnitLesson(4, 2, '/er/ > er, ur, ir'),
    makeUnitLesson(4, 3, `/i/ > 'y'`),
    makeUnitLesson(4, 4, `/ie/ > 'i' (1-syllable)`),
    makeUnitLesson(4, 5, `/ie/ > 'igh'`),
    makeUnitLesson(4, 6, `/ie/ > 'y' + y→i+es`),
    makeUnitLesson(4, 7, 'Singular and Plural Nouns', true),
    makeUnitLesson(4, 8, `'o' tricky + /oe/ > 'o' (1-syllable)`),
    makeUnitLesson(4, 9, `/oe/ > 'ow' + 'ow' tricky`),
    makeUnitLesson(4, 10, 'Singular and Plural Nouns', true),
    makeUnitLesson(4, 11, `'e' tricky + /ee/ > 'e'`),
    makeUnitLesson(4, 12, 'Proper Nouns', true),
    makeUnitLesson(4, 13, 'Proper Nouns', true),
    makeUnitLesson(4, 14, `'to be' as Verb`, true),
    makeUnitLesson(4, 15, 'Close Reading', true),
    makeUnitLesson(4, 16, 'Partner Reading', true),
    makeUnitLesson(4, 17, `'y' tricky + /ee/ > 'y', 'ey'`),
    makeUnitLesson(4, 18, `'y' tricky + y→i+es`),
    makeUnitLesson(4, 19, `Past Tense 'to be'`, true),
    makeUnitLesson(4, 20, 'Grammar Review', true),
    makeUnitLesson(4, 21, `/aw/ > 'a' + 'l'`),
    makeUnitLesson(4, 22, 'Reading Comp + Grammar Assessment', true),
    makeUnitLesson(4, 23, 'Oral Reading Fluency Assessment', true),
    makeUnitLesson(4, 24, 'Oral Reading Fluency + Word ID Assessment', true),
    makeUnitLesson(4, 25, 'Oral Reading Fluency Assessment', true),
    makeUnitLesson(4, 'PP', 'Pausing Point', true),
    // Unit 5 — /u/ alts + schwa /ə/ + /tion/ + Tricky Spellings
    makeUnitLesson(5, 1, '/u/ > o, ou, o_e'),
    makeUnitLesson(5, 2, `/u/ > 'o'`),
    makeUnitLesson(5, 3, '/u/ > o_e, ou'),
    makeUnitLesson(5, 4, 'Close Reading', true),
    makeUnitLesson(5, 5, 'Review /u/ alts'),
    makeUnitLesson(5, 6, 'Review /u/ alts'),
    makeUnitLesson(5, 7, 'Schwa /ə/ > a, e'),
    makeUnitLesson(5, 8, 'Schwa /ə/ > a, e'),
    makeUnitLesson(5, 9, 'Schwa /ə/ > a, e'),
    makeUnitLesson(5, 10, 'Parts of Speech, Subject and Predicate', true),
    makeUnitLesson(5, 11, '/ə/ + /l/ > al, il, ul'),
    makeUnitLesson(5, 12, 'Review /ə/ + /l/: al, il, ul'),
    makeUnitLesson(5, 13, '/ə/ + /l/ > el, le'),
    makeUnitLesson(5, 14, 'Review /ə/ + /l/: el, le'),
    makeUnitLesson(5, 15, `/sh/ /ə/ /n/ > 'tion'`),
    makeUnitLesson(5, 16, 'Review /tion/'),
    makeUnitLesson(5, 17, 'Action Verb Tense', true),
    makeUnitLesson(5, 18, 'Verb Tenses Review', true),
    makeUnitLesson(5, 19, 'Review vowel code'),
    makeUnitLesson(5, 20, 'Partner Reading', true),
    makeUnitLesson(5, 21, `'a' tricky`),
    makeUnitLesson(5, 22, `'e' tricky`),
    makeUnitLesson(5, 23, `'o' tricky`),
    makeUnitLesson(5, 24, `'o_e' tricky`),
    makeUnitLesson(5, 25, `'ou' tricky`),
    makeUnitLesson(5, 26, 'Reading + Grammar Assessment', true),
    makeUnitLesson(5, 27, 'Decoding + Alphabetizing Assessment', true),
    makeUnitLesson(5, 28, 'Individual Decoding + High-Frequency Word Assessment', true),
    makeUnitLesson(5, 29, 'Individual Decoding + High-Frequency Word Assessment', true),
    makeUnitLesson(5, 30, 'Individual Decoding + High-Frequency Word Assessment', true),
    makeUnitLesson(5, 'PP', 'Pausing Point', true),
    // Unit 6 — /f/ > 'ph' + War of 1812 EOY
    makeUnitLesson(6, 1, `/f/ > 'ph'`),
    makeUnitLesson(6, 2, `/f/ > 'ph'`),
    makeUnitLesson(6, 3, `'ea' > /e/ (tricky)`),
    makeUnitLesson(6, 4, 'Whole Group Reading', true),
    makeUnitLesson(
      6,
      5,
      'Tricky Words: Great Britain, Europe, native, Americans, signature, war',
      true,
    ),
    makeUnitLesson(6, 6, '/er/ alts'),
    makeUnitLesson(6, 7, 'Adverbs', true),
    makeUnitLesson(6, 8, '/er/ > ar, or'),
    makeUnitLesson(6, 9, 'Adverbs', true),
    makeUnitLesson(6, 10, 'Complete vs. Incomplete Sentences', true),
    makeUnitLesson(
      6,
      11,
      `Tricky Words: imagine, soldier, Washington + /k/ > 'ch'`,
      true,
    ),
    makeUnitLesson(6, 12, 'Run-On Sentences', true),
    makeUnitLesson(6, 13, `Tricky Word: iron + 'i' tricky`),
    makeUnitLesson(6, 14, `'i' tricky`),
    makeUnitLesson(6, 15, 'Tricky Word: special', true),
    makeUnitLesson(6, 16, `'i' tricky`),
    makeUnitLesson(6, 17, 'Tricky Word: shoe', true),
    makeUnitLesson(6, 18, 'Small Group/Independent Reading', true),
    makeUnitLesson(6, 19, 'Tricky Word: Fort McHenry + /ə/ alts'),
    makeUnitLesson(6, 20, 'Partner Reading', true),
    makeUnitLesson(6, 21, 'Tricky Words: early, whose, broad, bomb', true),
    makeUnitLesson(6, 22, 'Topic Sentence', true),
    makeUnitLesson(6, 23, 'Tricky Words: Andrew, new', true),
    makeUnitLesson(6, 24, 'Capitalization and Punctuation Review', true),
    makeUnitLesson(6, 25, `/o/ > 'a'`),
    makeUnitLesson(6, 26, `/o/ > 'a'`),
    makeUnitLesson(6, 27, 'War of 1812 Reading Assessment', true),
    makeUnitLesson(6, 28, `'a' tricky review`),
    makeUnitLesson(6, 29, 'Drafting a Report', true),
    makeUnitLesson(6, 30, 'Partner Reading', true),
    makeUnitLesson(6, 31, 'Whole Group Reading', true),
    makeUnitLesson(6, 32, 'Whole Group Reading', true),
    makeUnitLesson(6, 33, 'Silent Reading Comprehension Assessment', true),
    makeUnitLesson(6, 34, 'Optional Fluency Assessment', true),
    makeUnitLesson(6, 35, 'Optional Word Reading Assessment', true),
    makeUnitLesson(6, 36, 'Optional Word Reading Assessment', true),
    makeUnitLesson(6, 'PP', 'Pausing Point', true),
  ],
};

// ─── Heggerty Phonemic Awareness ──────────────────────────────────────────────

const makeWeek = (
  weekNumber: number,
  displayLabel: string,
  skillDetail: string,
): PhonicsObjective => ({
  lessonNumber: weekNumber,
  value: skillDetail,
  label: displayLabel,
  disabled: isNonDecodingLesson(skillDetail) || undefined,
  exampleWords: deriveExampleWords(skillDetail),
});

const HEGGERTY_OBJECTIVES_BY_GRADE: Partial<Record<Grade, PhonicsObjective[]>> = {
  'Pre-K': [
    makeWeek(
      1,
      'PreK · Week 1 — Rhyme Repetition + Onset Fluency + Blending Compound Words',
      'Rhyme Repetition; Initial phoneme isolation via Onset Fluency; Blend two parts of a compound word',
    ),
    makeWeek(
      2,
      'PreK · Week 2 — Rhyme Repetition + Onset Fluency + Blending Compound Words',
      'Continued Rhyme Repetition, Onset Fluency, Compound blending',
    ),
    makeWeek(
      3,
      'PreK · Week 3 — Rhyme Repetition + Onset Fluency + Blending Compound Words',
      'Continued Rhyme Repetition, Onset Fluency, Compound blending',
    ),
    makeWeek(
      4,
      'PreK · Week 4 — Rhyme Repetition + Onset Fluency + Blending Compound Words',
      'Continued Rhyme Repetition, Onset Fluency, Compound blending',
    ),
    makeWeek(
      5,
      'PreK · Week 5 — Rhyme Repetition + Onset Fluency + Blending Syllables',
      'Rhyme; Onset Fluency; Blend syllables of 2-syllable words',
    ),
    makeWeek(
      6,
      'PreK · Week 6 — Rhyme Repetition + Onset Fluency + Blending Syllables',
      'Continued Rhyme, Onset Fluency, Syllable blending',
    ),
    makeWeek(
      7,
      'PreK · Week 7 — Rhyme Repetition + Onset Fluency + Blending Syllables',
      'Continued Rhyme, Onset Fluency, Syllable blending',
    ),
    makeWeek(
      8,
      'PreK · Week 8 — Rhyme Recognition + Onset Fluency + Blending Body-Coda',
      'Rhyme Recognition; Onset Fluency (alliteration: generate same-initial-phoneme word); Body-Coda blending',
    ),
    makeWeek(
      9,
      'PreK · Week 9 — Rhyme Recognition + Onset Fluency + Blending Body-Coda',
      'Continued Rhyme Recognition, alliteration, Body-Coda',
    ),
    makeWeek(
      10,
      'PreK · Week 10 — Rhyme Recognition + Onset Fluency + Blending Body-Coda',
      'Continued Rhyme Recognition, alliteration, Body-Coda',
    ),
    makeWeek(
      11,
      'PreK · Week 11 — Initial phoneme isolation (b, h, m, n, p)',
      'Initial phoneme categorization — same vs. different — with focus phonemes /b/, /h/, /m/, /n/, /p/',
    ),
    makeWeek(
      12,
      'PreK · Week 12 — Initial phoneme isolation (f, g, k, t, m, d)',
      'Initial phoneme categorization with /f/, /g/, /k/, /t/, /m/, /d/',
    ),
    makeWeek(
      13,
      'PreK · Week 13 — Initial phoneme isolation (f, k, w, t, p)',
      'Initial phoneme categorization with /f/, /k/, /w/, /t/, /p/',
    ),
    makeWeek(
      14,
      'PreK · Week 14 — Initial phoneme isolation (d, h, g, w, p, t)',
      'Initial phoneme categorization with /d/, /h/, /g/, /w/, /p/, /t/',
    ),
    makeWeek(
      15,
      'PreK · Week 15 — Initial phoneme isolation (p, m, h, n, w, b)',
      'Initial phoneme categorization with /p/, /m/, /h/, /n/, /w/, /b/',
    ),
    makeWeek(
      16,
      'PreK · Week 16 — Initial phoneme isolation (k, g, d, t, f, h)',
      'Initial phoneme categorization with /k/, /g/, /d/, /t/, /f/, /h/',
    ),
    makeWeek(
      17,
      'PreK · Week 17 — Initial phoneme isolation (ch)',
      'Initial phoneme categorization with the /ch/ digraph sound',
    ),
    makeWeek(
      18,
      'PreK · Week 18 — Initial phoneme isolation (j, s)',
      'Initial phoneme categorization with /j/, /s/',
    ),
    makeWeek(
      19,
      'PreK · Week 19 — Initial phoneme isolation (sh, y)',
      'Initial phoneme categorization with /sh/, /y/',
    ),
    makeWeek(
      20,
      'PreK · Week 20 — Initial phoneme isolation (continuous sounds)',
      'Generate words with continuous initial sounds; Onset-Rime blending; Final phoneme isolation begins',
    ),
    makeWeek(
      21,
      'PreK · Week 21 — Initial phoneme isolation (stop sounds)',
      'Generate words with stop initial sounds; Onset-Rime blending; Final phoneme isolation',
    ),
    makeWeek(
      22,
      'PreK · Week 22 — Initial phoneme isolation (mixed review, vocab focus)',
      'Mixed initial phoneme review with vocabulary focus; Onset-Rime blending; Final phoneme isolation',
    ),
    makeWeek(
      23,
      'PreK · Week 23 — Onset-Rime blending + Final phoneme isolation',
      'Blend onset and rime; Isolate final sound in series of words',
    ),
    makeWeek(
      24,
      'PreK · Week 24 — Onset-Rime blending + Final phoneme isolation',
      'Continued Onset-Rime blending and Final phoneme isolation',
    ),
    makeWeek(
      25,
      'PreK · Week 25 — Body-Coda blending + Final phoneme categorization',
      'Body-Coda blending; Categorize final phonemes',
    ),
    makeWeek(
      26,
      'PreK · Week 26 — Body-Coda blending + Final phoneme categorization (m, p, k, d, n)',
      'Body-Coda blending; Final phoneme categorization with /m/, /p/, /k/, /d/, /n/',
    ),
    makeWeek(
      27,
      'PreK · Week 27 — Blending two phonemes + Final phoneme categorization (n, b, h, p, m)',
      'Blend 2 phonemes; Final phoneme categorization with /n/, /b/, /h/, /p/, /m/',
    ),
    makeWeek(
      28,
      'PreK · Week 28 — Blending two phonemes + Medial phoneme isolation (focused short vowels)',
      'Blend 2 phonemes; Isolate medial short vowel sounds',
    ),
    makeWeek(
      29,
      'PreK · Week 29 — Blending three phonemes + Medial short vowels',
      'Blend 3 phonemes; Medial short vowel isolation',
    ),
    makeWeek(
      30,
      'PreK · Week 30 — Blending three phonemes + Medial short vowels',
      'Continued 3-phoneme blending; Medial short vowel isolation',
    ),
    makeWeek(
      31,
      'PreK · Week 31 — Segmenting compound words + Medial long & short vowel sounds',
      'Segment compound words into parts; Distinguish long vs short medial vowels',
    ),
    makeWeek(
      32,
      'PreK · Week 32 — Segmenting syllables + Add syllables',
      'Segment 2-syllable words; Add a syllable',
    ),
    makeWeek(
      33,
      'PreK · Week 33 — Segmenting onset-rime + Substitute initial phoneme',
      'Segment onset and rime; Substitute the initial phoneme',
    ),
    makeWeek(
      34,
      'PreK · Week 34 — Segmenting words into 2 phonemes + Delete syllables + Substitute initial phoneme',
      'Segment 2-phoneme words; Delete a syllable; Substitute initial phoneme',
    ),
    makeWeek(
      35,
      'PreK · Week 35 — Segmenting words into 3 phonemes + Add/Delete/Substitute initial phoneme',
      'Segment 3-phoneme words; Add, delete, and substitute initial phonemes',
    ),
  ],
  K: [
    makeWeek(
      1,
      'K · Week 1 — Rhyme Repetition + Onset Fluency + Blending Compound Words',
      'Rhyme repetition; Initial phoneme isolation; Blend compound words',
    ),
    makeWeek(
      2,
      'K · Week 2 — Rhyme Repetition + Onset Fluency + Blending Compound Words',
      'Continued rhyme, onset fluency, compound blending',
    ),
    makeWeek(
      3,
      'K · Week 3 — Rhyme Recognition + Onset Fluency + Blending Syllables',
      'Rhyme recognition; Onset fluency; Blend syllables',
    ),
    makeWeek(
      4,
      'K · Week 4 — Rhyme Recognition + Onset Fluency + Blending Syllables',
      'Continued rhyme recognition, onset fluency, syllable blending',
    ),
    makeWeek(
      5,
      'K · Week 5 — Rhyme Recognition + Initial Phoneme Isolation + Blending Onset-Rime',
      'Rhyme recognition; Initial phoneme isolation; Onset-Rime blending',
    ),
    makeWeek(
      6,
      'K · Week 6 — Rhyme Recognition + Initial Phoneme Isolation + Blending Onset-Rime',
      'Continued rhyme recognition, initial phoneme isolation, onset-rime blending',
    ),
    makeWeek(
      7,
      'K · Week 7 — Rhyme Production + Initial Phoneme Isolation + Blending Body-Coda',
      'Rhyme production; Initial phoneme isolation; Body-Coda blending',
    ),
    makeWeek(
      8,
      'K · Week 8 — Rhyme Production + Initial Phoneme Isolation + Blending Body-Coda',
      'Continued rhyme production, initial phoneme isolation, body-coda blending',
    ),
    makeWeek(
      9,
      'K · Week 9 — Rhyme Production + Final Phoneme Isolation + Blending 2 Phonemes',
      'Rhyme production; Final phoneme isolation; Blend 2 phonemes',
    ),
    makeWeek(
      10,
      'K · Week 10 — Rhyme Production + Final Phoneme Isolation + Blending 2 Phonemes',
      'Continued rhyme production, final phoneme isolation, 2-phoneme blending',
    ),
    makeWeek(
      11,
      'K · Week 11 — Final Phoneme Isolation + Blending 3 Phonemes (CVC)',
      'Final phoneme isolation; Blend 3 phonemes (CVC)',
    ),
    makeWeek(
      12,
      'K · Week 12 — Final Phoneme Isolation + Blending 3 Phonemes (CVC)',
      'Continued final phoneme isolation; 3-phoneme blending',
    ),
    makeWeek(
      13,
      'K · Week 13 — Medial Phoneme Isolation (short vowels) + Blending 3 Phonemes',
      'Medial short vowel isolation; 3-phoneme blending',
    ),
    makeWeek(
      14,
      'K · Week 14 — Medial Phoneme Isolation (short vowels) + Blending 3 Phonemes',
      'Continued medial short vowel isolation; 3-phoneme blending',
    ),
    makeWeek(
      15,
      'K · Week 15 — Medial Phoneme Isolation + Segmenting Compound Words',
      'Medial phoneme isolation; Segment compound words',
    ),
    makeWeek(
      16,
      'K · Week 16 — Medial Phoneme Isolation + Segmenting Compound Words',
      'Continued medial phoneme isolation; Compound segmenting',
    ),
    makeWeek(
      17,
      'K · Week 17 — Medial Phoneme Isolation + Segmenting Syllables',
      'Medial phoneme isolation; Segment syllables',
    ),
    makeWeek(
      18,
      'K · Week 18 — Medial Phoneme Isolation + Segmenting Syllables',
      'Continued medial phoneme isolation; Syllable segmenting',
    ),
    makeWeek(
      19,
      'K · Week 19 — Medial Phoneme Isolation + Segmenting Onset-Rime',
      'Medial phoneme isolation; Segment onset and rime',
    ),
    makeWeek(
      20,
      'K · Week 20 — Medial Phoneme Isolation + Segmenting Onset-Rime',
      'Continued medial phoneme isolation; Onset-Rime segmenting',
    ),
    makeWeek(
      21,
      'K · Week 21 — Segmenting 2 Phonemes + Add Initial Phoneme',
      'Segment 2-phoneme words; Add an initial phoneme',
    ),
    makeWeek(
      22,
      'K · Week 22 — Segmenting 2 Phonemes + Add Initial Phoneme',
      'Continued 2-phoneme segmenting; Add initial phoneme',
    ),
    makeWeek(
      23,
      'K · Week 23 — Segmenting 3 Phonemes + Add Final Phoneme',
      'Segment 3-phoneme (CVC) words; Add a final phoneme',
    ),
    makeWeek(
      24,
      'K · Week 24 — Segmenting 3 Phonemes + Add Final Phoneme',
      'Continued 3-phoneme segmenting; Add final phoneme',
    ),
    makeWeek(
      25,
      'K · Week 25 — Segmenting 3 Phonemes + Delete Initial Phoneme',
      'Segment 3 phonemes; Delete the initial phoneme',
    ),
    makeWeek(
      26,
      'K · Week 26 — Segmenting 3 Phonemes + Delete Initial Phoneme',
      'Continued 3-phoneme segmenting; Delete initial phoneme',
    ),
    makeWeek(
      27,
      'K · Week 27 — Segmenting 3 Phonemes + Delete Final Phoneme',
      'Segment 3 phonemes; Delete the final phoneme',
    ),
    makeWeek(
      28,
      'K · Week 28 — Segmenting 3 Phonemes + Delete Final Phoneme',
      'Continued 3-phoneme segmenting; Delete final phoneme',
    ),
    makeWeek(
      29,
      'K · Week 29 — Segmenting 4 Phonemes (CVCC/CCVC) + Substitute Initial Phoneme',
      'Segment 4-phoneme words; Substitute initial phoneme',
    ),
    makeWeek(
      30,
      'K · Week 30 — Segmenting 4 Phonemes + Substitute Initial Phoneme',
      'Continued 4-phoneme segmenting; Substitute initial phoneme',
    ),
    makeWeek(
      31,
      'K · Week 31 — Segmenting 4 Phonemes + Substitute Final Phoneme',
      '4-phoneme segmenting; Substitute final phoneme',
    ),
    makeWeek(
      32,
      'K · Week 32 — Segmenting 4 Phonemes + Substitute Final Phoneme',
      'Continued 4-phoneme segmenting; Substitute final phoneme',
    ),
    makeWeek(
      33,
      'K · Week 33 — Segmenting 4 Phonemes + Substitute Medial Phoneme',
      '4-phoneme segmenting; Substitute medial vowel',
    ),
    makeWeek(
      34,
      'K · Week 34 — Segmenting 4 Phonemes + Substitute Medial Phoneme',
      'Continued 4-phoneme segmenting; Substitute medial vowel',
    ),
    makeWeek(
      35,
      'K · Week 35 — Cumulative Review (all 8 PA skills, 4-5 phoneme words)',
      'Cumulative end-of-K review across all 8 PA skills',
    ),
  ],
  '1': [
    makeWeek(
      1,
      'Gr 1 · Primary Week 1 — Phoneme Isolation (initial, final, medial) + Blending 3-4 Phonemes',
      'Isolate initial/final/medial phonemes; Blend 3-4 phoneme words',
    ),
    makeWeek(
      2,
      'Gr 1 · Primary Week 2 — Phoneme Isolation + Blending 4 Phonemes (CVCC)',
      'Phoneme isolation; Blend 4-phoneme CVCC words',
    ),
    makeWeek(
      3,
      'Gr 1 · Primary Week 3 — Phoneme Isolation + Blending 4 Phonemes (CCVC)',
      'Phoneme isolation; Blend 4-phoneme CCVC words',
    ),
    makeWeek(
      4,
      'Gr 1 · Primary Week 4 — Segmenting 3 Phonemes + Add Initial Phoneme',
      'Segment 3 phonemes; Add initial phoneme',
    ),
    makeWeek(
      5,
      'Gr 1 · Primary Week 5 — Segmenting 4 Phonemes + Add Final Phoneme',
      'Segment 4 phonemes; Add final phoneme',
    ),
    makeWeek(
      6,
      'Gr 1 · Primary Week 6 — Segmenting 4 Phonemes + Delete Initial Phoneme',
      'Segment 4 phonemes; Delete initial phoneme',
    ),
    makeWeek(
      7,
      'Gr 1 · Primary Week 7 — Segmenting 4 Phonemes + Delete Final Phoneme',
      'Segment 4 phonemes; Delete final phoneme',
    ),
    makeWeek(
      8,
      'Gr 1 · Primary Week 8 — Segmenting 4-5 Phonemes + Delete Initial Phoneme of a Blend',
      'Segment longer words; Delete initial phoneme in a blend (e.g., stop → top)',
    ),
    makeWeek(
      9,
      'Gr 1 · Primary Week 9 — Segmenting 4-5 Phonemes + Delete Final Phoneme of a Blend',
      'Segment longer words; Delete final phoneme in a blend (e.g., last → lass)',
    ),
    makeWeek(
      10,
      'Gr 1 · Primary Week 10 — Segmenting 4-5 Phonemes + Substitute Initial Phoneme',
      'Segment 4-5 phonemes; Substitute initial phoneme',
    ),
    makeWeek(
      11,
      'Gr 1 · Primary Week 11 — Segmenting 4-5 Phonemes + Substitute Final Phoneme',
      'Segment 4-5 phonemes; Substitute final phoneme',
    ),
    makeWeek(
      12,
      'Gr 1 · Primary Week 12 — Segmenting 4-5 Phonemes + Substitute Medial Phoneme (short vowel)',
      '4-5 phoneme segmenting; Substitute medial short vowel',
    ),
    makeWeek(
      13,
      'Gr 1 · Primary Week 13 — Phoneme Chaining (substitute any position) + Long Vowels',
      'Phoneme chaining across positions; Introduce long vowels',
    ),
    makeWeek(
      14,
      'Gr 1 · Primary Week 14 — Phoneme Chaining + Long Vowels (a_e, i_e)',
      'Phoneme chaining; Long a, long i',
    ),
    makeWeek(
      15,
      'Gr 1 · Primary Week 15 — Phoneme Chaining + Long Vowels (o_e, u_e, e_e)',
      'Phoneme chaining; Long o, u, e',
    ),
    makeWeek(
      16,
      'Gr 1 · Primary Week 16 — Phoneme Chaining + R-controlled Vowels (ar, or)',
      'Phoneme chaining; R-controlled ar, or',
    ),
    makeWeek(
      17,
      'Gr 1 · Primary Week 17 — Phoneme Chaining + R-controlled Vowels (er, ir, ur)',
      'Phoneme chaining; R-controlled er, ir, ur',
    ),
    makeWeek(
      18,
      'Gr 1 · Primary Week 18 — Phoneme Chaining + Vowel Teams (ai, ay, ee, ea)',
      'Phoneme chaining; Long a/e vowel teams',
    ),
    makeWeek(
      19,
      'Gr 1 · Primary Week 19 — Phoneme Chaining + Vowel Teams (oa, ow, ie, igh)',
      'Phoneme chaining; Long o/i vowel teams',
    ),
    makeWeek(
      20,
      'Gr 1 · Primary Week 20 — Phoneme Chaining + Diphthongs (oi, oy, ou, ow)',
      'Phoneme chaining; Diphthongs /oi/, /ou/',
    ),
    makeWeek(
      21,
      'Gr 1 · Primary Week 21 — Phoneme Chaining + Vowel Teams (oo, ew, au, aw)',
      'Phoneme chaining; oo/ew and au/aw teams',
    ),
    makeWeek(
      22,
      'Gr 1 · Primary Week 22 — Cumulative Phoneme Chaining (all 8 PA skills, advanced manipulation)',
      'End-of-G1 cumulative phoneme manipulation review',
    ),
  ],
  '2': [
    makeWeek(
      6,
      'Gr 2 · Primary Week 6 — Phoneme Isolation + Blending 4-5 Phonemes (CCVCC)',
      'Review phoneme isolation; Blend 4-5 phoneme words',
    ),
    makeWeek(
      7,
      'Gr 2 · Primary Week 7 — Segmenting 4-5 Phonemes + Delete Initial Phoneme of a Blend',
      'Segment longer words; Delete initial phoneme of a blend',
    ),
    makeWeek(
      8,
      'Gr 2 · Primary Week 8 — Segmenting 4-5 Phonemes + Delete Final Phoneme of a Blend',
      'Segment longer words; Delete final phoneme of a blend',
    ),
    makeWeek(
      9,
      'Gr 2 · Primary Week 9 — Substitute Initial Phoneme + Long Vowels',
      'Initial phoneme substitution; Long vowel words',
    ),
    makeWeek(
      10,
      'Gr 2 · Primary Week 10 — Substitute Final Phoneme + Long Vowels (VCe)',
      'Final phoneme substitution; VCe pattern',
    ),
    makeWeek(
      11,
      'Gr 2 · Primary Week 11 — Substitute Medial Phoneme + R-controlled Vowels',
      'Medial phoneme substitution; R-controlled vowels',
    ),
    makeWeek(
      12,
      'Gr 2 · Primary Week 12 — Phoneme Chaining (any position) + Vowel Teams (ai, ay, ee, ea)',
      'Phoneme chaining; Long a/e vowel teams',
    ),
    makeWeek(
      13,
      'Gr 2 · Primary Week 13 — Phoneme Chaining + Vowel Teams (oa, ow, ie, igh)',
      'Phoneme chaining; Long o/i vowel teams',
    ),
    makeWeek(
      14,
      'Gr 2 · Primary Week 14 — Phoneme Chaining + Diphthongs (oi, oy, ou, ow)',
      'Phoneme chaining; Diphthongs',
    ),
    makeWeek(
      15,
      'Gr 2 · Primary Week 15 — Phoneme Chaining + Vowel Teams (oo, ew, au, aw)',
      'Phoneme chaining; oo/ew/au/aw teams',
    ),
    makeWeek(
      16,
      'Gr 2 · Primary Week 16 — Syllable Manipulation (add, delete, substitute)',
      'Add/delete/substitute syllables in multisyllabic words',
    ),
    makeWeek(
      17,
      'Gr 2 · Primary Week 17 — Syllable Manipulation + Schwa Awareness',
      'Continued syllable manipulation; Identify schwa',
    ),
    makeWeek(
      18,
      'Gr 2 · Primary Week 18 — Phoneme Manipulation in Multisyllabic Words',
      'Phoneme manipulation across syllable boundaries',
    ),
    makeWeek(
      19,
      'Gr 2 · Primary Week 19 — Phoneme Manipulation in Multisyllabic Words',
      'Continued multisyllabic phoneme manipulation',
    ),
    makeWeek(
      20,
      'Gr 2 · Primary Week 20 — Cumulative Phoneme Manipulation (all 8 skills, multisyllabic)',
      'Cumulative review across all PA skills with multisyllabic words',
    ),
    makeWeek(
      21,
      'Gr 2 · Primary Week 21 — Cumulative Phoneme Manipulation + Affixes (-s, -ed, -ing)',
      'Cumulative PA review; Manipulate base + suffix words',
    ),
    makeWeek(
      22,
      'Gr 2 · Primary Week 22 — Cumulative Phoneme Manipulation + Affixes (-er, -est, prefixes)',
      'End-of-G2 cumulative review with morphologically complex words',
    ),
  ],
};

// ─── Wilson Fundations ────────────────────────────────────────────────────────

const makeWilson = (
  unitNumber: number | 'Bonus',
  conceptIndex: number,
  displayLabel: string,
  skillDetail: string,
  disabled = false,
): PhonicsObjective => ({
  unitNumber: typeof unitNumber === 'number' ? unitNumber : undefined,
  lessonNumber: `${unitNumber === 'Bonus' ? 'B' : unitNumber}.${conceptIndex}`,
  value: skillDetail,
  label: displayLabel,
  disabled: disabled || isNonDecodingLesson(skillDetail),
  exampleWords: deriveExampleWords(skillDetail),
});

const WILSON_OBJECTIVES_BY_GRADE: Partial<Record<Grade, PhonicsObjective[]>> = {
  K: [
    makeWilson(
      1,
      1,
      'K · Level K · Unit 1 — Letter-Keyword-Sound for consonants',
      'Introduce the Letter-Keyword-Sound association for all consonants',
    ),
    makeWilson(
      1,
      2,
      'K · Level K · Unit 1 — Letter-Keyword-Sound for short vowels',
      'Introduce the Letter-Keyword-Sound association for short vowels a, e, i, o, u',
    ),
    makeWilson(
      1,
      3,
      'K · Level K · Unit 1 — Letter Formation (lowercase a-z)',
      'Lowercase letter formation',
      true,
    ),
    makeWilson(
      1,
      4,
      'K · Level K · Unit 1 — Sound recognition: consonants and short vowels',
      'Recognize and produce the sound when shown the consonant or short vowel letter',
    ),
    makeWilson(
      1,
      5,
      'K · Level K · Unit 1 — Print and word awareness',
      'Print and word awareness',
      true,
    ),
    makeWilson(
      1,
      6,
      'K · Level K · Unit 1 — Phonological awareness: rhyming, alliteration, syllable awareness',
      'PA: rhyme, alliteration, syllable counting',
    ),
    makeWilson(
      1,
      7,
      'K · Level K · Unit 1 — Phonemic awareness: initial and final sounds',
      'Isolate the initial and final phoneme in spoken words',
    ),
    makeWilson(1, 8, 'K · Level K · Unit 1 — Story retelling', 'Story retelling', true),
    makeWilson(
      1,
      9,
      'K · Level K · Unit 1 — Beginning composition skills',
      'Beginning composition skills',
      true,
    ),
    makeWilson(
      1,
      10,
      'K · Level K · Unit 1 — Fluency/phrasing with echo and choral reading',
      'Fluency/prosody routine',
      true,
    ),
    makeWilson(
      2,
      1,
      'K · Level K · Unit 2 — PA: blending, segmenting, manipulation of sounds',
      'Blend, segment, and manipulate phonemes in spoken words',
    ),
    makeWilson(
      2,
      2,
      'K · Level K · Unit 2 — Blending and reading 3-sound short vowel words',
      'Blend CVC short vowel words for reading',
    ),
    makeWilson(2, 3, 'K · Level K · Unit 2 — Story prediction', 'Story prediction', true),
    makeWilson(
      2,
      4,
      'K · Level K · Unit 2 — Alphabetical Order',
      'Alphabetical Order',
      true,
    ),
    makeWilson(
      2,
      5,
      'K · Level K · Unit 2 — Uppercase letter formation',
      'Uppercase handwriting',
      true,
    ),
    makeWilson(
      2,
      6,
      'K · Level K · Unit 2 — CVC sample words: map, sad, rat',
      'Decode/spell short vowel CVC words',
    ),
    makeWilson(
      2,
      7,
      'K · Level K · Unit 2 — Phonetically regular HFW: fit, led, gas, sun, box, yet',
      'Decode and spell phonetically regular high-frequency CVC words',
    ),
    makeWilson(
      3,
      1,
      'K · Level K · Unit 3 — PA: blending, segmenting, manipulation of sounds',
      'Continued PA practice with blending, segmenting, manipulating phonemes',
    ),
    makeWilson(
      3,
      2,
      'K · Level K · Unit 3 — Blending sounds in nonsense CVC words',
      'Decode nonsense CVC words to confirm sound-spelling mastery',
    ),
    makeWilson(
      3,
      3,
      'K · Level K · Unit 3 — Segmenting and spelling 3-sound short vowel words',
      'Segment and spell CVC short vowel words',
    ),
    makeWilson(
      3,
      4,
      'K · Level K · Unit 3 — Distinguish long and short vowel sounds',
      'Discriminate long vs short vowels in spoken words',
    ),
    makeWilson(
      3,
      5,
      'K · Level K · Unit 3 — Narrative story form: character, setting, main events',
      'Narrative story form',
      true,
    ),
    makeWilson(
      3,
      6,
      'K · Level K · Unit 3 — Fluency and phrasing with echo and choral reading',
      'Fluency/prosody routine',
      true,
    ),
    makeWilson(
      3,
      7,
      'K · Level K · Unit 3 — Beginning composition skills',
      'Beginning composition skills',
      true,
    ),
    makeWilson(
      3,
      8,
      'K · Level K · Unit 3 — Trick Words (HFW): the, a, and, are, to, is, his, as, has, was',
      'Read/spell irregular high-frequency Trick Words',
    ),
    makeWilson(
      3,
      9,
      'K · Level K · Unit 3 — Phonetically regular HFW: had, ten, six, not, up, bad',
      'Decode and spell phonetically regular HFW',
    ),
    makeWilson(
      3,
      10,
      'K · Level K · Unit 3 — CVC sample words: cut, tap, wet',
      'Decode/spell CVC short vowel words',
    ),
    makeWilson(
      4,
      1,
      'K · Level K · Unit 4 — PA: blending, segmenting, manipulation of sounds',
      'Continued PA work',
    ),
    makeWilson(
      4,
      2,
      'K · Level K · Unit 4 — Phoneme segmentation',
      'Segment spoken words into phonemes',
    ),
    makeWilson(
      4,
      3,
      'K · Level K · Unit 4 — Consonant digraphs: wh, ch, sh, th, ck (keyword + sound)',
      'Introduce digraphs wh, ch, sh, th, ck with keywords',
    ),
    makeWilson(
      4,
      4,
      'K · Level K · Unit 4 — Decoding 3-sound words with digraphs',
      'Read 3-sound words containing wh/ch/sh/th/ck',
    ),
    makeWilson(
      4,
      5,
      'K · Level K · Unit 4 — Spelling 3-sound words with digraphs',
      'Spell 3-sound words with digraphs',
    ),
    makeWilson(
      4,
      6,
      'K · Level K · Unit 4 — Spelling of ck at end of words',
      'Use ck at the end of CVC words after a short vowel',
    ),
    makeWilson(
      4,
      7,
      'K · Level K · Unit 4 — Narrative story form: character, setting, main events',
      'Narrative story form',
      true,
    ),
    makeWilson(
      4,
      8,
      'K · Level K · Unit 4 — Sample words: bath, chop, thick',
      'Decode/spell digraph CVC words',
    ),
    makeWilson(
      4,
      9,
      'K · Level K · Unit 4 — Trick Words (HFW): we, she, he, be, me, I, you, they',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      4,
      10,
      'K · Level K · Unit 4 — Phonetically regular HFW: when, much, ship, them, back, with',
      'Decode and spell phonetically regular HFW with digraphs',
    ),
    makeWilson(
      5,
      1,
      'K · Level K · Unit 5 — PA: blending, segmenting, manipulation of sounds',
      'Cumulative PA practice',
    ),
    makeWilson(
      5,
      2,
      'K · Level K · Unit 5 — Sentence structure',
      'Sentence structure',
      true,
    ),
    makeWilson(
      5,
      3,
      'K · Level K · Unit 5 — Sentence dictation',
      'Sentence dictation',
      true,
    ),
    makeWilson(
      5,
      4,
      'K · Level K · Unit 5 — Narrative fiction vs. informational books',
      'Genre awareness',
      true,
    ),
    makeWilson(
      5,
      5,
      'K · Level K · Unit 5 — Fluency and expression',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      5,
      6,
      'K · Level K · Unit 5 — Trick Words (HFW): or, for, of, have, from, by, my, do, one',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      5,
      7,
      'K · Level K · Unit 5 — Phonetically regular HFW: rock, such, that, which, when, shop',
      'Decode and spell phonetically regular HFW',
    ),
  ],
  '1': [
    makeWilson(
      1,
      1,
      'Gr 1 · Level 1 · Unit 1 — Letter-Keyword-Sound for consonants',
      'Review consonant letter-keyword-sound',
    ),
    makeWilson(
      1,
      2,
      'Gr 1 · Level 1 · Unit 1 — Letter-Keyword-Sound for short vowels',
      'Review short vowel letter-keyword-sound',
    ),
    makeWilson(
      1,
      3,
      'Gr 1 · Level 1 · Unit 1 — Letter Formation (lowercase a-z)',
      'Lowercase letter formation',
      true,
    ),
    makeWilson(
      1,
      4,
      'Gr 1 · Level 1 · Unit 1 — Alphabetical Order',
      'Alphabetical Order',
      true,
    ),
    makeWilson(
      1,
      5,
      'Gr 1 · Level 1 · Unit 1 — Phonological awareness: alliteration, rhyme',
      'PA: alliteration & rhyme',
    ),
    makeWilson(
      1,
      6,
      'Gr 1 · Level 1 · Unit 1 — First sound fluency',
      'Isolate the initial phoneme in spoken words',
    ),
    makeWilson(
      1,
      7,
      'Gr 1 · Level 1 · Unit 1 — Phonemic awareness: sound manipulation',
      'Manipulate phonemes in spoken words',
    ),
    makeWilson(
      1,
      8,
      'Gr 1 · Level 1 · Unit 1 — Sound recognition for consonants and short vowels',
      'Recognize sound when shown the letter',
    ),
    makeWilson(
      2,
      1,
      'Gr 1 · Level 1 · Unit 2 — PA: sound manipulation (initial, final, medial)',
      'Manipulate phonemes in initial/final/medial position',
    ),
    makeWilson(
      2,
      2,
      'Gr 1 · Level 1 · Unit 2 — Blending and reading 3-sound short vowel words',
      'Decode CVC short vowel words',
    ),
    makeWilson(
      2,
      3,
      'Gr 1 · Level 1 · Unit 2 — Segmenting and spelling 3-sound short vowel words',
      'Segment and spell CVC short vowel words',
    ),
    makeWilson(
      2,
      4,
      'Gr 1 · Level 1 · Unit 2 — Sentence dictation procedures',
      'Sentence dictation: capitalization, period, proofreading',
      true,
    ),
    makeWilson(
      2,
      5,
      'Gr 1 · Level 1 · Unit 2 — PA: alliteration, onset and rime, syllable awareness, rhyme',
      'PA: onset/rime, syllables, rhyme',
    ),
    makeWilson(
      2,
      6,
      'Gr 1 · Level 1 · Unit 2 — Story retelling in detail and sequence',
      'Story retelling',
      true,
    ),
    makeWilson(
      2,
      7,
      'Gr 1 · Level 1 · Unit 2 — Trick Words (HFW): the, a, and, is, his, of',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      2,
      8,
      'Gr 1 · Level 1 · Unit 2 — Phonetically regular HFW: sat, fun, leg, him, box, yes',
      'Decode and spell phonetically regular CVC HFW',
    ),
    makeWilson(
      2,
      9,
      'Gr 1 · Level 1 · Unit 2 — CVC sample words: map, lit, job, fix, quit',
      'Decode/spell CVC short vowel words',
    ),
    makeWilson(
      3,
      1,
      'Gr 1 · Level 1 · Unit 3 — Phoneme segmentation',
      'Segment phonemes in spoken words',
    ),
    makeWilson(
      3,
      2,
      'Gr 1 · Level 1 · Unit 3 — Consonant digraphs: wh, ch, sh, th, ck (keyword + sound)',
      'Digraphs wh/ch/sh/th/ck',
    ),
    makeWilson(
      3,
      3,
      'Gr 1 · Level 1 · Unit 3 — Spelling of ck at end of words',
      'ck after short vowel at end of CVC',
    ),
    makeWilson(
      3,
      4,
      'Gr 1 · Level 1 · Unit 3 — Punctuation (question mark)',
      'Question mark punctuation',
      true,
    ),
    makeWilson(
      3,
      5,
      'Gr 1 · Level 1 · Unit 3 — Narrative story form: character, setting, main events',
      'Narrative story form',
      true,
    ),
    makeWilson(
      3,
      6,
      'Gr 1 · Level 1 · Unit 3 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      3,
      7,
      'Gr 1 · Level 1 · Unit 3 — Retelling with picture notes and visualization',
      'Retelling',
      true,
    ),
    makeWilson(
      3,
      8,
      'Gr 1 · Level 1 · Unit 3 — Trick Words (HFW): as, has, to, into, we, he, she, be, me, for, or',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      3,
      9,
      'Gr 1 · Level 1 · Unit 3 — Phonetically regular HFW: when, this, shop, such, back, wish',
      'Decode/spell phonetically regular HFW with digraphs',
    ),
    makeWilson(
      3,
      10,
      'Gr 1 · Level 1 · Unit 3 — Sample words: wish, chop, sock',
      'Decode/spell digraph CVC words',
    ),
    makeWilson(
      4,
      1,
      'Gr 1 · Level 1 · Unit 4 — Bonus letter spelling rule: ff, ll, ss, sometimes zz',
      'FLSZ doubling rule at end of CVC',
    ),
    makeWilson(4, 2, 'Gr 1 · Level 1 · Unit 4 — Glued sound: all', 'Glued sound -all'),
    makeWilson(
      4,
      3,
      'Gr 1 · Level 1 · Unit 4 — Narrative story form: character, setting, main events',
      'Narrative story form',
      true,
    ),
    makeWilson(
      4,
      4,
      'Gr 1 · Level 1 · Unit 4 — Punctuation: exclamation point, quotation marks',
      'Punctuation marks',
      true,
    ),
    makeWilson(
      4,
      5,
      'Gr 1 · Level 1 · Unit 4 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      4,
      6,
      'Gr 1 · Level 1 · Unit 4 — Trick Words (HFW): you, your, I, they, was, one, said',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      4,
      7,
      'Gr 1 · Level 1 · Unit 4 — Phonetically regular HFW: off, fell, miss, call, less, all',
      'Decode/spell HFW with bonus letters & all',
    ),
    makeWilson(
      4,
      8,
      'Gr 1 · Level 1 · Unit 4 — Sample words: hill, puff, bill, miss, call',
      'Decode/spell bonus letter & glued sound words',
    ),
    makeWilson(
      5,
      1,
      'Gr 1 · Level 1 · Unit 5 — Glued sounds: am, an',
      'Glued sounds am/an',
    ),
    makeWilson(
      5,
      2,
      'Gr 1 · Level 1 · Unit 5 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(5, 3, 'Gr 1 · Level 1 · Unit 5 — Proofreading', 'Proofreading', true),
    makeWilson(
      5,
      4,
      'Gr 1 · Level 1 · Unit 5 — Trick Words (HFW): from, have, do, does',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      5,
      5,
      'Gr 1 · Level 1 · Unit 5 — Phonetically regular HFW: am, an, can, man, ran, than',
      'Decode/spell HFW with am/an glued sounds',
    ),
    makeWilson(
      5,
      6,
      'Gr 1 · Level 1 · Unit 5 — Sample words: ham, can, fan',
      'Decode/spell am/an words',
    ),
    makeWilson(
      6,
      1,
      'Gr 1 · Level 1 · Unit 6 — Base word and suffix -s',
      'Suffix -s on base words',
    ),
    makeWilson(6, 2, 'Gr 1 · Level 1 · Unit 6 — Pluralization', 'Pluralization with -s'),
    makeWilson(
      6,
      3,
      'Gr 1 · Level 1 · Unit 6 — Narrative fiction vs. informational books',
      'Genre awareness',
      true,
    ),
    makeWilson(
      6,
      4,
      'Gr 1 · Level 1 · Unit 6 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      6,
      5,
      'Gr 1 · Level 1 · Unit 6 — Trick Words (HFW): were, are, who, what, when, where, there, here',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      6,
      6,
      'Gr 1 · Level 1 · Unit 6 — Phonetically regular HFW with -s: fills, sits, dogs, ships, maps, rocks',
      'Decode/spell base+s HFW',
    ),
    makeWilson(
      6,
      7,
      'Gr 1 · Level 1 · Unit 6 — Sample words: hills, bugs, chills',
      'Decode/spell -s plural words',
    ),
    makeWilson(
      7,
      1,
      'Gr 1 · Level 1 · Unit 7 — Glued sounds: ang, ing, ong, ung, ank, ink, onk, unk',
      'Glued sounds -ang/-ing/-ong/-ung/-ank/-ink/-onk/-unk',
    ),
    makeWilson(
      7,
      2,
      'Gr 1 · Level 1 · Unit 7 — Blending and reading words with ng or nk',
      'Decode -ng/-nk glued sound words',
    ),
    makeWilson(
      7,
      3,
      'Gr 1 · Level 1 · Unit 7 — Segmenting and spelling words with ng or nk',
      'Spell -ng/-nk glued sound words',
    ),
    makeWilson(
      7,
      4,
      'Gr 1 · Level 1 · Unit 7 — Narrative fiction vs. informational books',
      'Genre awareness',
      true,
    ),
    makeWilson(
      7,
      5,
      'Gr 1 · Level 1 · Unit 7 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      7,
      6,
      'Gr 1 · Level 1 · Unit 7 — Trick Words (HFW): why, by, my, try, put, two, too, very, also, some, come',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      7,
      7,
      'Gr 1 · Level 1 · Unit 7 — Phonetically regular HFW: long, sing, ring, song, bank, think',
      'Decode/spell -ng/-nk HFW',
    ),
    makeWilson(
      7,
      8,
      'Gr 1 · Level 1 · Unit 7 — Sample words: bang, bank, pink, chunks',
      'Decode/spell -ng/-nk words',
    ),
    makeWilson(
      8,
      1,
      'Gr 1 · Level 1 · Unit 8 — Consonant blends and digraph blends',
      'Blends and digraph blends (e.g., bl, str, shr)',
    ),
    makeWilson(
      8,
      2,
      'Gr 1 · Level 1 · Unit 8 — Blending and reading words with up to four sounds',
      'Decode CCVC, CVCC, CCVCC words',
    ),
    makeWilson(
      8,
      3,
      'Gr 1 · Level 1 · Unit 8 — Segmenting and spelling words with up to four sounds',
      'Spell 4-sound blend words',
    ),
    makeWilson(
      8,
      4,
      'Gr 1 · Level 1 · Unit 8 — Suffix -s added to words with four sounds',
      'Add -s to 4-sound base words',
    ),
    makeWilson(
      8,
      5,
      'Gr 1 · Level 1 · Unit 8 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      8,
      6,
      'Gr 1 · Level 1 · Unit 8 — Trick Words (HFW): would, could, should, her, over, number',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      8,
      7,
      'Gr 1 · Level 1 · Unit 8 — Phonetically regular HFW: and, best, milk, spot, dress, small',
      'Decode/spell blend HFW',
    ),
    makeWilson(
      8,
      8,
      'Gr 1 · Level 1 · Unit 8 — Sample words: bump, stash, bled, past, pinch, shrug, steps',
      'Decode/spell blend & digraph-blend words',
    ),
    makeWilson(
      9,
      1,
      'Gr 1 · Level 1 · Unit 9 — R-controlled vowel sounds: ar, or, er, ir, ur',
      'R-controlled vowels ar/or/er/ir/ur',
    ),
    makeWilson(
      9,
      2,
      'Gr 1 · Level 1 · Unit 9 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      9,
      3,
      'Gr 1 · Level 1 · Unit 9 — Trick Words (HFW): say, says, see, between, each',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      10,
      1,
      'Gr 1 · Level 1 · Unit 10 — Closed-syllable concept with short vowels',
      'Closed syllable type',
    ),
    makeWilson(
      10,
      2,
      'Gr 1 · Level 1 · Unit 10 — Closed syllable vs. open syllable',
      'Distinguish closed vs open syllables',
    ),
    makeWilson(
      10,
      3,
      'Gr 1 · Level 1 · Unit 10 — Vowel team sounds: ai, ay, ee, ea, ey, oi, oy',
      'Vowel teams ai/ay/ee/ea/ey/oi/oy',
    ),
    makeWilson(
      10,
      4,
      'Gr 1 · Level 1 · Unit 10 — Story retelling',
      'Story retelling',
      true,
    ),
    makeWilson(
      10,
      5,
      'Gr 1 · Level 1 · Unit 10 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      10,
      6,
      'Gr 1 · Level 1 · Unit 10 — Sample words: click, chunk, sniffs',
      'Decode/spell closed syllable & cumulative words',
    ),
    makeWilson(
      11,
      1,
      'Gr 1 · Level 1 · Unit 11 — Segmenting and blending up to 5 sounds',
      'Segment/blend 5-sound words',
    ),
    makeWilson(
      11,
      2,
      'Gr 1 · Level 1 · Unit 11 — Suffix -s added to words with 5 sounds',
      '-s suffix on 5-sound base words',
    ),
    makeWilson(
      11,
      3,
      'Gr 1 · Level 1 · Unit 11 — Suffixes -ed, -ing on unchanging closed-syllable bases',
      'Add -ed, -ing without spelling change',
    ),
    makeWilson(
      11,
      4,
      'Gr 1 · Level 1 · Unit 11 — Vowel team sounds: oa, oe, ow, ou, oo, ue, ew, au, aw',
      'Vowel teams oa/oe/ow/ou/oo/ue/ew/au/aw',
    ),
    makeWilson(
      11,
      5,
      'Gr 1 · Level 1 · Unit 11 — Narrative fiction vs. informational books',
      'Genre awareness',
      true,
    ),
    makeWilson(
      11,
      6,
      'Gr 1 · Level 1 · Unit 11 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      11,
      7,
      'Gr 1 · Level 1 · Unit 11 — Trick Words (HFW): any, many, how, now, down, out, about, our',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      11,
      8,
      'Gr 1 · Level 1 · Unit 11 — Phonetically regular HFW: bring, plant, string, printed, French, strong',
      'Decode/spell multisyllabic blend HFW',
    ),
    makeWilson(
      11,
      9,
      'Gr 1 · Level 1 · Unit 11 — Sample words: stump, clasp, strap, slashing, blended',
      'Decode/spell suffix + blend words',
    ),
    makeWilson(
      12,
      1,
      'Gr 1 · Level 1 · Unit 12 — Vowel-consonant-e (VCe) syllable in one-syllable words',
      'VCe / Magic E syllable type',
    ),
    makeWilson(
      12,
      2,
      'Gr 1 · Level 1 · Unit 12 — Long vowel sounds',
      'Long vowel sounds in VCe context',
    ),
    makeWilson(
      12,
      3,
      'Gr 1 · Level 1 · Unit 12 — Narrative fiction vs. informational books',
      'Genre awareness',
      true,
    ),
    makeWilson(
      12,
      4,
      'Gr 1 · Level 1 · Unit 12 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      12,
      5,
      'Gr 1 · Level 1 · Unit 12 — Trick Words (HFW): friend, other, another, none, nothing',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      12,
      6,
      'Gr 1 · Level 1 · Unit 12 — Phonetically regular HFW: here, safe, fine, those, use, wide',
      'Decode/spell VCe HFW',
    ),
    makeWilson(
      12,
      7,
      'Gr 1 · Level 1 · Unit 12 — Sample words: stove, hope, caves',
      'Decode/spell VCe words',
    ),
    makeWilson(
      13,
      1,
      'Gr 1 · Level 1 · Unit 13 — Concept of syllable in multisyllabic words',
      'Multisyllabic word concept',
    ),
    makeWilson(13, 2, 'Gr 1 · Level 1 · Unit 13 — Compound words', 'Compound words'),
    makeWilson(
      13,
      3,
      'Gr 1 · Level 1 · Unit 13 — Syllable division rules',
      'Syllable division',
    ),
    makeWilson(
      13,
      4,
      'Gr 1 · Level 1 · Unit 13 — Reading and spelling 2-syllable words (closed + closed / closed + VCe)',
      'Decode/spell 2-syllable words',
    ),
    makeWilson(
      13,
      5,
      'Gr 1 · Level 1 · Unit 13 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      13,
      6,
      'Gr 1 · Level 1 · Unit 13 — Paragraph structure',
      'Paragraph structure',
      true,
    ),
    makeWilson(
      13,
      7,
      'Gr 1 · Level 1 · Unit 13 — Trick Words (HFW): people, month, little, been, own, want, Mr., Mrs.',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      13,
      8,
      'Gr 1 · Level 1 · Unit 13 — Phonetically regular HFW: cannot, until, visit, entire, object, subject',
      'Decode/spell 2-syllable HFW',
    ),
    makeWilson(
      13,
      9,
      'Gr 1 · Level 1 · Unit 13 — Sample words: sunset, finish, reptile',
      'Decode/spell multisyllabic words',
    ),
    makeWilson(
      14,
      1,
      'Gr 1 · Level 1 · Unit 14 — Suffixes -s, -ing, -ed on multisyllabic words',
      'Add -s/-ing/-ed to multisyllabic bases',
    ),
    makeWilson(
      14,
      2,
      'Gr 1 · Level 1 · Unit 14 — Suffix -es on closed-syllable bases',
      '-es plural after sibilants',
    ),
    makeWilson(
      14,
      3,
      'Gr 1 · Level 1 · Unit 14 — Reading with accuracy and prosody',
      'Fluency/prosody',
      true,
    ),
    makeWilson(
      14,
      4,
      'Gr 1 · Level 1 · Unit 14 — Paragraph structure',
      'Paragraph structure',
      true,
    ),
    makeWilson(
      14,
      5,
      'Gr 1 · Level 1 · Unit 14 — Trick Words (HFW): work, word, write, being, their, first, look, good, new',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      14,
      6,
      'Gr 1 · Level 1 · Unit 14 — Phonetically regular HFW: insects, products, branches, inches',
      'Decode/spell suffix HFW',
    ),
    makeWilson(
      14,
      7,
      'Gr 1 · Level 1 · Unit 14 — Sample words: trombones, picnics, boxes, finishes',
      'Decode/spell suffix + multisyllabic words',
    ),
  ],
  '2': [
    makeWilson(
      1,
      1,
      'Gr 2 · Level 2 · Unit 1 — Letter-Keyword-Sound for consonants and vowels',
      'Review L-K-S for all consonants and vowels',
    ),
    makeWilson(
      1,
      2,
      'Gr 2 · Level 2 · Unit 1 — Consonant digraphs (concept + letter-keyword-sound)',
      'wh, ch, sh, th, ck',
    ),
    makeWilson(
      1,
      3,
      'Gr 2 · Level 2 · Unit 1 — Concept of consonant blends',
      'Two- and three-letter blends',
    ),
    makeWilson(
      1,
      4,
      'Gr 2 · Level 2 · Unit 1 — Concept of digraph blends',
      'Digraph blends (e.g., shr, thr)',
    ),
    makeWilson(
      1,
      5,
      'Gr 2 · Level 2 · Unit 1 — Spelling of /k/ sound (c, k, ck)',
      'Choose c/k/ck for the /k/ sound',
    ),
    makeWilson(
      1,
      6,
      'Gr 2 · Level 2 · Unit 1 — Closed-syllable type',
      'Closed syllable concept',
    ),
    makeWilson(
      1,
      7,
      'Gr 2 · Level 2 · Unit 1 — Phonetically regular HFW: soft, just, that, check, milk, kept',
      'Decode/spell closed syllable HFW',
    ),
    makeWilson(
      1,
      8,
      'Gr 2 · Level 2 · Unit 1 — Sample words: quilt, elf, shred, kick, crush',
      'Decode/spell closed syllable & blend words',
    ),
    makeWilson(
      2,
      1,
      'Gr 2 · Level 2 · Unit 2 — Bonus letter spelling rule: ff, ll, ss, sometimes zz',
      'FLSZ doubling rule',
    ),
    makeWilson(
      2,
      2,
      'Gr 2 · Level 2 · Unit 2 — Glued sounds: all, am, an, ang, ing, ong, ung, ank, ink, onk, unk',
      'All Level 1 glued sounds',
    ),
    makeWilson(
      2,
      3,
      'Gr 2 · Level 2 · Unit 2 — Blending and reading words with glued sounds',
      'Decode glued sound words',
    ),
    makeWilson(
      2,
      4,
      'Gr 2 · Level 2 · Unit 2 — Segmenting and spelling words with bonus letters and glued sounds',
      'Spell bonus letter & glued sound words',
    ),
    makeWilson(2, 5, 'Gr 2 · Level 2 · Unit 2 — Prosody', 'Prosody', true),
    makeWilson(2, 6, 'Gr 2 · Level 2 · Unit 2 — Fluent passage reading', 'Fluency', true),
    makeWilson(
      2,
      7,
      'Gr 2 · Level 2 · Unit 2 — Story retelling',
      'Story retelling',
      true,
    ),
    makeWilson(
      3,
      1,
      'Gr 2 · Level 2 · Unit 3 — R-controlled vowels: ar, er, ir, or, ur',
      'R-controlled syllable type',
    ),
    makeWilson(
      3,
      2,
      'Gr 2 · Level 2 · Unit 3 — Trick Words (HFW): shall, pull, full, both, talk, walk',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      3,
      3,
      'Gr 2 · Level 2 · Unit 3 — Phonetically regular HFW: small, will, off, than, strong, think',
      'Decode/spell phonetically regular HFW',
    ),
    makeWilson(
      3,
      4,
      'Gr 2 · Level 2 · Unit 3 — Sample words: spill, string, stand, toss',
      'Decode/spell cumulative review words',
    ),
    makeWilson(
      4,
      1,
      'Gr 2 · Level 2 · Unit 4 — Closed-syllable exceptions concept',
      'Closed syllable exceptions',
    ),
    makeWilson(
      4,
      2,
      'Gr 2 · Level 2 · Unit 4 — Glued sounds: ild, ind, old, olt, ost',
      'Long VCC patterns',
    ),
    makeWilson(
      4,
      3,
      'Gr 2 · Level 2 · Unit 4 — Story retelling',
      'Story retelling',
      true,
    ),
    makeWilson(4, 4, 'Gr 2 · Level 2 · Unit 4 — Fluent passage reading', 'Fluency', true),
    makeWilson(
      4,
      5,
      'Gr 2 · Level 2 · Unit 4 — Vowel teams: ai, ay, ea, ee, ey, oi, oy',
      'Long a/e and oi/oy vowel teams',
    ),
    makeWilson(
      4,
      6,
      'Gr 2 · Level 2 · Unit 4 — Trick Words (HFW): done, goes, pretty',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      4,
      7,
      'Gr 2 · Level 2 · Unit 4 — Phonetically regular HFW: child, hold, kind, most, find, old',
      'Decode/spell exception HFW',
    ),
    makeWilson(
      4,
      8,
      'Gr 2 · Level 2 · Unit 4 — Sample words: wild, find, cold, colt, post',
      'Decode/spell long VCC exception words',
    ),
    makeWilson(
      5,
      1,
      'Gr 2 · Level 2 · Unit 5 — Review suffixes: -s, -es, -ed, -ing',
      'Review baseline suffixes',
    ),
    makeWilson(
      5,
      2,
      'Gr 2 · Level 2 · Unit 5 — Additional sounds of -ed suffix: /d/, /t/',
      'The three sounds of -ed',
    ),
    makeWilson(
      5,
      3,
      'Gr 2 · Level 2 · Unit 5 — Comparison suffixes: -er, -est',
      'Comparative & superlative suffixes',
    ),
    makeWilson(
      5,
      4,
      'Gr 2 · Level 2 · Unit 5 — Spelling procedure for words with suffixes',
      'How to spell base + suffix',
    ),
    makeWilson(
      5,
      5,
      'Gr 2 · Level 2 · Unit 5 — Forming plurals',
      'Plural formation rules',
    ),
    makeWilson(
      5,
      6,
      'Gr 2 · Level 2 · Unit 5 — Forming present and past tense',
      'Present and past tense via suffixes',
    ),
    makeWilson(
      5,
      7,
      'Gr 2 · Level 2 · Unit 5 — Categorizing vowel and consonant suffixes',
      'Vowel suffix vs consonant suffix',
    ),
    makeWilson(5, 8, 'Gr 2 · Level 2 · Unit 5 — Fluent passage reading', 'Fluency', true),
    makeWilson(
      5,
      9,
      'Gr 2 · Level 2 · Unit 5 — Vowel teams: oa, oe, ow, ou, oo, ue, ew',
      'Long o/u vowel teams',
    ),
    makeWilson(
      5,
      10,
      'Gr 2 · Level 2 · Unit 5 — Trick Words (HFW): again, please, animal, sure, use, used',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      5,
      11,
      'Gr 2 · Level 2 · Unit 5 — Phonetically regular HFW: called, branches, filled, jumped, passed, picked',
      'Decode/spell suffix HFW',
    ),
    makeWilson(
      5,
      12,
      'Gr 2 · Level 2 · Unit 5 — Sample words: stronger, tallest, spilled, passed',
      'Decode/spell suffix words',
    ),
    makeWilson(
      6,
      1,
      'Gr 2 · Level 2 · Unit 6 — Reading and spelling two-syllable words',
      'Two-syllable word work',
    ),
    makeWilson(6, 2, 'Gr 2 · Level 2 · Unit 6 — Compound words', 'Compound words'),
    makeWilson(
      6,
      3,
      'Gr 2 · Level 2 · Unit 6 — Syllable division rules (between closed syllables)',
      'Syllable division (VC/CV, VC/V)',
    ),
    makeWilson(
      6,
      4,
      'Gr 2 · Level 2 · Unit 6 — Spelling -ic at end of multisyllabic words',
      '-ic spelling at word end',
    ),
    makeWilson(
      6,
      5,
      'Gr 2 · Level 2 · Unit 6 — Suffixes: -ful, -ment, -ness, -less, -able, -en, -ish',
      'New suffix introduction',
    ),
    makeWilson(
      6,
      6,
      'Gr 2 · Level 2 · Unit 6 — Prefixes: mis-, un-, non-, dis-, trans-',
      'Prefix introduction',
    ),
    makeWilson(
      6,
      7,
      'Gr 2 · Level 2 · Unit 6 — Vowel teams: au, aw',
      '/aw/ vowel teams au & aw',
    ),
    makeWilson(
      6,
      8,
      'Gr 2 · Level 2 · Unit 6 — Trick Words (HFW): against, knew, know, always, often, once',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      6,
      9,
      'Gr 2 · Level 2 · Unit 6 — Phonetically regular HFW: cannot, expect, object, visit, finish, until',
      'Decode/spell 2-syllable HFW',
    ),
    makeWilson(
      6,
      10,
      'Gr 2 · Level 2 · Unit 6 — Sample words: catnip, habit, tonic, mascot, hundred, freshen, boldness',
      'Decode/spell multisyllabic + suffix words',
    ),
    makeWilson(
      7,
      1,
      'Gr 2 · Level 2 · Unit 7 — Vowel-consonant-e (VCe) syllables review',
      'VCe syllable review',
    ),
    makeWilson(7, 2, 'Gr 2 · Level 2 · Unit 7 — s saying /s/ vs. /z/', 'Two sounds of s'),
    makeWilson(
      7,
      3,
      'Gr 2 · Level 2 · Unit 7 — Two-syllable words with closed and VCe syllables',
      'Combine closed + VCe in 2-syllable words',
    ),
    makeWilson(7, 4, 'Gr 2 · Level 2 · Unit 7 — Compound words', 'Compound words'),
    makeWilson(
      7,
      5,
      'Gr 2 · Level 2 · Unit 7 — VCe exception (-ive)',
      'VCe exception in -ive',
    ),
    makeWilson(7, 6, 'Gr 2 · Level 2 · Unit 7 — Suffix -ive', '-ive suffix'),
    makeWilson(
      7,
      7,
      'Gr 2 · Level 2 · Unit 7 — Trick Words (HFW): only, house, move, right, place, together',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      7,
      8,
      'Gr 2 · Level 2 · Unit 7 — Phonetically regular HFW: use, close, these, entire, give, statement',
      'Decode/spell VCe HFW',
    ),
    makeWilson(
      7,
      9,
      'Gr 2 · Level 2 · Unit 7 — Sample words: hope, confuse, reptile, olive, inventive',
      'Decode/spell multisyllabic VCe',
    ),
    makeWilson(
      8,
      1,
      'Gr 2 · Level 2 · Unit 8 — Open syllable type',
      'Open syllable concept',
    ),
    makeWilson(
      8,
      2,
      'Gr 2 · Level 2 · Unit 8 — y as a vowel',
      'y functioning as a vowel',
    ),
    makeWilson(
      8,
      3,
      'Gr 2 · Level 2 · Unit 8 — Combining open syllables with closed and VCe syllables',
      'Two-syllable open + closed/VCe combinations',
    ),
    makeWilson(
      8,
      4,
      'Gr 2 · Level 2 · Unit 8 — Additional syllable division rules',
      'Open syllable division (V/CV)',
    ),
    makeWilson(
      8,
      5,
      'Gr 2 · Level 2 · Unit 8 — Suffixes: -y, -ly, -ty',
      '-y/-ly/-ty suffixes',
    ),
    makeWilson(
      8,
      6,
      'Gr 2 · Level 2 · Unit 8 — Trick Words (HFW): eight, large, change, city, every, family, night, carry, something',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      8,
      7,
      'Gr 2 · Level 2 · Unit 8 — Sample words: no, music, relate, cry, baby, chilly',
      'Decode/spell open syllable & y-as-vowel words',
    ),
    makeWilson(
      9,
      1,
      'Gr 2 · Level 2 · Unit 9 — R-controlled syllable type',
      'R-controlled syllable type',
    ),
    makeWilson(
      9,
      2,
      'Gr 2 · Level 2 · Unit 9 — Sounds of ar and or',
      'ar /ar/ and or /or/',
    ),
    makeWilson(
      9,
      3,
      'Gr 2 · Level 2 · Unit 9 — Combining r-controlled syllables with other syllable types',
      '2-syllable words with r-controlled',
    ),
    makeWilson(
      9,
      4,
      'Gr 2 · Level 2 · Unit 9 — Trick Words (HFW): world, answer, different',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      9,
      5,
      'Gr 2 · Level 2 · Unit 9 — Phonetically regular HFW: or, for, sharp, before, report, party',
      'Decode/spell ar/or HFW',
    ),
    makeWilson(
      9,
      6,
      'Gr 2 · Level 2 · Unit 9 — Sample words: fort, part, orbit, party',
      'Decode/spell ar/or words',
    ),
    makeWilson(
      10,
      1,
      'Gr 2 · Level 2 · Unit 10 — Sounds of er, ir, and ur',
      'er/ir/ur as /er/',
    ),
    makeWilson(
      10,
      2,
      'Gr 2 · Level 2 · Unit 10 — Spelling option procedure for /er/ sound',
      'Choose er/ir/ur for /er/',
    ),
    makeWilson(
      10,
      3,
      'Gr 2 · Level 2 · Unit 10 — Combining er/ir/ur r-controlled with other syllable types',
      '2-syllable er/ir/ur words',
    ),
    makeWilson(
      10,
      4,
      'Gr 2 · Level 2 · Unit 10 — Dictionary skills',
      'Dictionary skills',
      true,
    ),
    makeWilson(
      10,
      5,
      'Gr 2 · Level 2 · Unit 10 — Trick Words (HFW): picture, learn, earth, father, brother, mother',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      10,
      6,
      'Gr 2 · Level 2 · Unit 10 — Phonetically regular HFW: her, number, over, first, turn, surprise',
      'Decode/spell er/ir/ur HFW',
    ),
    makeWilson(
      10,
      7,
      'Gr 2 · Level 2 · Unit 10 — Sample words: burst, termite, dirty',
      'Decode/spell er/ir/ur words',
    ),
    makeWilson(
      11,
      1,
      'Gr 2 · Level 2 · Unit 11 — Double vowel (vowel team) syllable type',
      'Vowel team syllable type',
    ),
    makeWilson(
      11,
      2,
      'Gr 2 · Level 2 · Unit 11 — Sounds of ai and ay',
      'Long a vowel teams ai/ay',
    ),
    makeWilson(
      11,
      3,
      'Gr 2 · Level 2 · Unit 11 — Spelling option procedure for /ā/ sound',
      'Choose ai/ay for /ā/',
    ),
    makeWilson(
      11,
      4,
      'Gr 2 · Level 2 · Unit 11 — Combining double vowel syllables with other syllable types',
      'Multisyllabic words with vowel teams',
    ),
    makeWilson(
      11,
      5,
      'Gr 2 · Level 2 · Unit 11 — Homophones',
      'Homophones tied to vowel team spellings',
    ),
    makeWilson(
      11,
      6,
      'Gr 2 · Level 2 · Unit 11 — Trick Words (HFW): great, country, away, America, school, thought',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      11,
      7,
      'Gr 2 · Level 2 · Unit 11 — Phonetically regular HFW: say, way, Saturday, Thursday, rain, detail',
      'Decode/spell ai/ay HFW',
    ),
    makeWilson(
      11,
      8,
      'Gr 2 · Level 2 · Unit 11 — Sample words: paint, display',
      'Decode/spell ai/ay words',
    ),
    makeWilson(
      12,
      1,
      'Gr 2 · Level 2 · Unit 12 — Sounds of ee, ea, and ey',
      'Long e vowel teams ee/ea/ey',
    ),
    makeWilson(
      12,
      2,
      'Gr 2 · Level 2 · Unit 12 — Spelling option procedure for /ē/ sound',
      'Choose ee/ea/ey for /ē/',
    ),
    makeWilson(
      12,
      3,
      'Gr 2 · Level 2 · Unit 12 — Trick Words (HFW): whose, won, son, breakfast, head, ready',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      12,
      4,
      'Gr 2 · Level 2 · Unit 12 — Phonetically regular HFW: see, between, each, green, teachers, valley',
      'Decode/spell ee/ea/ey HFW',
    ),
    makeWilson(
      12,
      5,
      'Gr 2 · Level 2 · Unit 12 — Sample words: cheap, keep, chimney',
      'Decode/spell ee/ea/ey words',
    ),
    makeWilson(
      13,
      1,
      'Gr 2 · Level 2 · Unit 13 — Sounds of oi and oy',
      'Diphthongs oi/oy',
    ),
    makeWilson(
      13,
      2,
      'Gr 2 · Level 2 · Unit 13 — Spelling generalizations of /oi/ sound',
      'oi medial vs oy at end',
    ),
    makeWilson(
      13,
      3,
      'Gr 2 · Level 2 · Unit 13 — Trick Words (HFW): favorite, early, ocean',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      13,
      4,
      'Gr 2 · Level 2 · Unit 13 — Phonetically regular HFW: boy, enjoy, joined, oil, point, soil',
      'Decode/spell oi/oy HFW',
    ),
    makeWilson(
      13,
      5,
      'Gr 2 · Level 2 · Unit 13 — Sample words: moist, toys, paperboy',
      'Decode/spell oi/oy words',
    ),
    makeWilson(
      14,
      1,
      'Gr 2 · Level 2 · Unit 14 — Sounds of oa, oe, ow, ou (long o)',
      'Long o vowel teams oa/oe/ow/ou',
    ),
    makeWilson(
      14,
      2,
      'Gr 2 · Level 2 · Unit 14 — Spelling option procedure for /ō/ sound',
      'Choose oa/ow/oe for /ō/',
    ),
    makeWilson(
      14,
      3,
      'Gr 2 · Level 2 · Unit 14 — Trick Words (HFW): work, word, write, being, their, first, look, good, new',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      14,
      4,
      'Gr 2 · Level 2 · Unit 14 — Phonetically regular HFW: own, coast, hoe, follow, window, yellow',
      'Decode/spell long o vowel team HFW',
    ),
    makeWilson(
      15,
      1,
      'Gr 2 · Level 2 · Unit 15 — The /ū/ sound of oo, ou, ue, ew',
      'Long u vowel teams oo/ou/ue/ew',
    ),
    makeWilson(15, 2, 'Gr 2 · Level 2 · Unit 15 — The /yū/ sound of ue', 'ue as /yū/'),
    makeWilson(15, 3, 'Gr 2 · Level 2 · Unit 15 — The /ŭ/ sound of oo', 'Short oo /ŭ/'),
    makeWilson(
      15,
      4,
      'Gr 2 · Level 2 · Unit 15 — Spelling option procedure for /ū/ and /yū/ sounds',
      'Choose oo/ou/ue/ew for /ū/ vs /yū/',
    ),
    makeWilson(
      15,
      5,
      'Gr 2 · Level 2 · Unit 15 — Fluent passage reading',
      'Fluency',
      true,
    ),
    makeWilson(
      15,
      6,
      'Gr 2 · Level 2 · Unit 15 — Trick Words (HFW): January, February, July, enough, special, December',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      15,
      7,
      'Gr 2 · Level 2 · Unit 15 — Phonetically regular HFW: look, good, new, you, too, blue',
      'Decode/spell oo/ou/ue/ew HFW',
    ),
    makeWilson(
      15,
      8,
      'Gr 2 · Level 2 · Unit 15 — Sample words: spoon, soup, overdue, blew, argue',
      'Decode/spell oo/ou/ue/ew words',
    ),
    makeWilson(
      16,
      1,
      'Gr 2 · Level 2 · Unit 16 — Sounds of au and aw',
      'au/aw vowel teams',
    ),
    makeWilson(
      16,
      2,
      'Gr 2 · Level 2 · Unit 16 — Spelling generalizations for the /ô/ sound',
      'au medial vs aw at end',
    ),
    makeWilson(
      16,
      3,
      'Gr 2 · Level 2 · Unit 16 — Trick Words (HFW): August, laugh, daughter',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      16,
      4,
      'Gr 2 · Level 2 · Unit 16 — Phonetically regular HFW: draw, drawing, law, saw',
      'Decode/spell au/aw HFW',
    ),
    makeWilson(
      16,
      5,
      'Gr 2 · Level 2 · Unit 16 — Sample words: claw, aunt',
      'Decode/spell au/aw words',
    ),
    makeWilson(
      17,
      1,
      'Gr 2 · Level 2 · Unit 17 — Consonant-le syllable type',
      'C-le syllable type',
    ),
    makeWilson(
      17,
      2,
      'Gr 2 · Level 2 · Unit 17 — Spelling consonant-le words',
      'Spelling -le pattern',
    ),
    makeWilson(
      17,
      3,
      'Gr 2 · Level 2 · Unit 17 — Review of all 6 syllable types',
      'All six syllable types cumulative',
    ),
    makeWilson(
      17,
      4,
      'Gr 2 · Level 2 · Unit 17 — Trick Words (HFW): trouble, couple, young',
      'Read/spell irregular Trick Words',
    ),
    makeWilson(
      17,
      5,
      'Gr 2 · Level 2 · Unit 17 — Phonetically regular HFW: little, apple, example, single, table, middle',
      'Decode/spell -le HFW',
    ),
    makeWilson(
      17,
      6,
      'Gr 2 · Level 2 · Unit 17 — Sample words: gobble, tumble, noble',
      'Decode/spell -le words',
    ),
  ],
  '3': [
    makeWilson(
      1,
      1,
      'Gr 3 · Level 3 · Unit 1 — Closed-syllable concept',
      'Closed syllable type review',
    ),
    makeWilson(
      1,
      2,
      'Gr 3 · Level 3 · Unit 1 — Glued sounds: all, an, am',
      '-all/-an/-am glued sounds',
    ),
    makeWilson(
      1,
      3,
      'Gr 3 · Level 3 · Unit 1 — Glued sounds: ng/nk',
      '-ng/-nk family glued sounds',
    ),
    makeWilson(
      1,
      4,
      'Gr 3 · Level 3 · Unit 1 — Closed-syllable exceptions: olt, old, ost, ind, ild',
      'Long VCC patterns olt/old/ost/ind/ild',
    ),
    makeWilson(
      1,
      5,
      'Gr 3 · Level 3 · Unit 1 — ck and tch spelling',
      'ck after short vowel; tch after short vowel',
    ),
    makeWilson(
      1,
      6,
      'Gr 3 · Level 3 · Unit 1 — w and qu effect on a (/ö/)',
      'w + a and qu + a sound shift',
    ),
    makeWilson(
      1,
      7,
      'Gr 3 · Level 3 · Unit 1 — Lowercase cursive letters: l, e, h, b, f, k',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      1,
      8,
      'Gr 3 · Level 3 · Unit 1 — Sample words: quilt, elf, shred, kick, crush, string, pitch, colt, squash',
      'Decode/spell closed syllable & exception words',
    ),
    makeWilson(
      1,
      9,
      'Gr 3 · Level 3 · Unit 1 — Sound-alike words: know/no, which/witch',
      'Homophones tied to spelling',
    ),
    makeWilson(
      2,
      1,
      'Gr 3 · Level 3 · Unit 2 — Review suffixes: -s, -es, -ed, -ing, -ive, -able, -en, -er, -est, -ish, -y, -ful, -less, -ness, -ly, -ty, -ment',
      'Cumulative suffix review',
    ),
    makeWilson(
      2,
      2,
      'Gr 3 · Level 3 · Unit 2 — Forming plurals with -s and -es',
      'Plural rules with -s/-es',
    ),
    makeWilson(
      2,
      3,
      'Gr 3 · Level 3 · Unit 2 — Irregular plurals',
      'Irregular plural forms',
    ),
    makeWilson(
      2,
      4,
      'Gr 3 · Level 3 · Unit 2 — Three sounds of -ed: /ed/, /d/, /t/',
      'The three pronunciations of -ed',
    ),
    makeWilson(
      2,
      5,
      'Gr 3 · Level 3 · Unit 2 — 1-1-1 Doubling Spelling Rule (vowel suffix)',
      'Double final consonant before vowel suffix',
    ),
    makeWilson(
      2,
      6,
      'Gr 3 · Level 3 · Unit 2 — Lowercase cursive letters: i, j, p, r, s, t, u, w',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      2,
      7,
      'Gr 3 · Level 3 · Unit 2 — Sample words: stronger, tallest, shipped, shipment',
      'Decode/spell suffix words',
    ),
    makeWilson(
      2,
      8,
      'Gr 3 · Level 3 · Unit 2 — Sound-alike words: son/sun, some/sum, banned/band, guest/guessed, missed/mist',
      'Homophones',
    ),
    makeWilson(
      3,
      1,
      'Gr 3 · Level 3 · Unit 3 — Review vowel-consonant-e (VCe) syllables',
      'VCe syllable type review',
    ),
    makeWilson(
      3,
      2,
      'Gr 3 · Level 3 · Unit 3 — Spelling of /k/ in a VCe syllable',
      'k vs c choice in VCe context',
    ),
    makeWilson(
      3,
      3,
      'Gr 3 · Level 3 · Unit 3 — s saying /z/ between two vowels',
      'Voiced s between vowels',
    ),
    makeWilson(
      3,
      4,
      'Gr 3 · Level 3 · Unit 3 — Review division of multisyllabic words',
      'Syllable division review',
    ),
    makeWilson(
      3,
      5,
      'Gr 3 · Level 3 · Unit 3 — Two-syllable words with closed + closed and closed + VCe syllables',
      'Multisyllabic decoding combinations',
    ),
    makeWilson(
      3,
      6,
      'Gr 3 · Level 3 · Unit 3 — Spelling multisyllabic words',
      'Multisyllabic spelling procedure',
    ),
    makeWilson(
      3,
      7,
      'Gr 3 · Level 3 · Unit 3 — Lowercase cursive letters: c, a, o',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      3,
      8,
      'Gr 3 · Level 3 · Unit 3 — Sample words: rose, invent, splendid, theme, confuse, reptile',
      'Decode/spell multisyllabic VCe words',
    ),
    makeWilson(
      3,
      9,
      'Gr 3 · Level 3 · Unit 3 — Sound-alike words: plain/plane',
      'Homophones',
    ),
    makeWilson(
      4,
      1,
      'Gr 3 · Level 3 · Unit 4 — -ve at the end of a word (/v/)',
      "English words don't end in v—use -ve",
    ),
    makeWilson(4, 2, 'Gr 3 · Level 3 · Unit 4 — -ive as a suffix', '-ive suffix'),
    makeWilson(
      4,
      3,
      'Gr 3 · Level 3 · Unit 4 — Silent e spelling rule',
      'Drop silent e before vowel suffix',
    ),
    makeWilson(
      4,
      4,
      'Gr 3 · Level 3 · Unit 4 — Lowercase cursive letters: d, g, q',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      4,
      5,
      'Gr 3 · Level 3 · Unit 4 — Sample words: captive, hoping, concluded, bravely',
      'Decode/spell -ve, -ive, silent e words',
    ),
    makeWilson(
      4,
      6,
      'Gr 3 · Level 3 · Unit 4 — Sound-alike words: mail/male, mind/mined, find/fined',
      'Homophones',
    ),
    makeWilson(
      5,
      1,
      'Gr 3 · Level 3 · Unit 5 — Identifying schwa',
      'Schwa as the unaccented vowel sound',
    ),
    makeWilson(
      5,
      2,
      'Gr 3 · Level 3 · Unit 5 — Reading words with a schwa in an unaccented syllable',
      'Decode schwa in unaccented syllables',
    ),
    makeWilson(
      5,
      3,
      'Gr 3 · Level 3 · Unit 5 — Spelling words with a schwa in an unaccented syllable',
      'Spell schwa',
    ),
    makeWilson(
      5,
      4,
      'Gr 3 · Level 3 · Unit 5 — -et spelling at end of words (e.g., jacket)',
      'Final -et spelling pattern',
    ),
    makeWilson(
      5,
      5,
      'Gr 3 · Level 3 · Unit 5 — Dictionary skills',
      'Dictionary skills',
      true,
    ),
    makeWilson(
      5,
      6,
      'Gr 3 · Level 3 · Unit 5 — Lowercase cursive letters: n, m, v, y',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      5,
      7,
      'Gr 3 · Level 3 · Unit 5 — Sample words: random, method, rockets',
      'Decode/spell schwa words',
    ),
    makeWilson(
      5,
      8,
      'Gr 3 · Level 3 · Unit 5 — Sound-alike words: weather/whether, father/farther',
      'Homophones',
    ),
    makeWilson(
      6,
      1,
      'Gr 3 · Level 3 · Unit 6 — Open syllable review',
      'Open syllable type',
    ),
    makeWilson(
      6,
      2,
      'Gr 3 · Level 3 · Unit 6 — y as a vowel: /ī/ and /ē/',
      'y as long i and long e',
    ),
    makeWilson(
      6,
      3,
      'Gr 3 · Level 3 · Unit 6 — Schwa in an open syllable with a',
      'Schwa-a (e.g., a-bout)',
    ),
    makeWilson(
      6,
      4,
      'Gr 3 · Level 3 · Unit 6 — Schwa in an open syllable with i',
      'Schwa-i in open syllables',
    ),
    makeWilson(
      6,
      5,
      'Gr 3 · Level 3 · Unit 6 — Open-syllable exception',
      'Open syllable exceptions',
    ),
    makeWilson(
      6,
      6,
      'Gr 3 · Level 3 · Unit 6 — Soft c: ce, ci, cy (reading)',
      'Soft c triggered by e/i/y',
    ),
    makeWilson(
      6,
      7,
      'Gr 3 · Level 3 · Unit 6 — Soft g: ge, gi, gy (reading)',
      'Soft g triggered by e/i/y',
    ),
    makeWilson(
      6,
      8,
      'Gr 3 · Level 3 · Unit 6 — Lowercase cursive letters: z, x',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      6,
      9,
      'Gr 3 · Level 3 · Unit 6 — Sample words: music, relate, cry, giant, Alaska, indicate, champion, recent',
      'Decode/spell open syllable & soft c/g words',
    ),
    makeWilson(
      6,
      10,
      'Gr 3 · Level 3 · Unit 6 — Sound-alike words: hi/high, by/buy/bye, oh/owe',
      'Homophones',
    ),
    makeWilson(
      'Bonus',
      1,
      'Gr 3 · Level 3 · Bonus Unit — Review of closed syllables (with nonsense words)',
      'Cumulative closed syllable practice',
    ),
    makeWilson(
      'Bonus',
      2,
      'Gr 3 · Level 3 · Bonus Unit — Concepts of prefixes and roots',
      'Prefix + root morphology',
    ),
    makeWilson(
      'Bonus',
      3,
      'Gr 3 · Level 3 · Bonus Unit — Review of syllable division for multisyllabic words',
      'Multisyllabic syllable division',
    ),
    makeWilson(
      'Bonus',
      4,
      'Gr 3 · Level 3 · Bonus Unit — Closed/open syllable prefixes: con-, dis-, un-, en-, em-, ex-, in-, im-, il-, ir-, mis-, non-, sub-, trans-, de-, e-, pre-, pro-, re-',
      'Common Latin prefixes',
    ),
    makeWilson(
      'Bonus',
      5,
      'Gr 3 · Level 3 · Bonus Unit — Closed-syllable roots: dict, duct, fect, fract, ject, junct, lect, min, scrib, sist, spec, struct, tract',
      'Common Latin roots',
    ),
    makeWilson(
      7,
      1,
      'Gr 3 · Level 3 · Unit 7 — Pluralizing words ending in y and o',
      'Plural rules for -y and -o words',
    ),
    makeWilson(
      7,
      2,
      'Gr 3 · Level 3 · Unit 7 — Y and suffix spelling rule',
      'Change y to i before suffix',
    ),
    makeWilson(
      7,
      3,
      'Gr 3 · Level 3 · Unit 7 — Uppercase cursive letters: A, O, C, G, E',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      7,
      4,
      'Gr 3 · Level 3 · Unit 7 — Sample words: happiest, studied, tries, duties, volcanoes',
      'Decode/spell y-to-i suffix words',
    ),
    makeWilson(
      7,
      5,
      'Gr 3 · Level 3 · Unit 7 — Sound-alike words: flour/flower, thrown/throne',
      'Homophones',
    ),
    makeWilson(
      8,
      1,
      'Gr 3 · Level 3 · Unit 8 — Final stable syllable',
      'Final stable syllable concept',
    ),
    makeWilson(
      8,
      2,
      'Gr 3 · Level 3 · Unit 8 — Consonant-le syllable',
      'C-le syllable type',
    ),
    makeWilson(
      8,
      3,
      'Gr 3 · Level 3 · Unit 8 — Suffixes -tion, -sion',
      'Suffixes -tion and -sion',
    ),
    makeWilson(
      8,
      4,
      'Gr 3 · Level 3 · Unit 8 — Consonant-le exception',
      'C-le exceptions',
    ),
    makeWilson(
      8,
      5,
      'Gr 3 · Level 3 · Unit 8 — Final syllable with schwa vs. consonant-le',
      'Distinguish schwa-l from c-le',
    ),
    makeWilson(
      8,
      6,
      'Gr 3 · Level 3 · Unit 8 — Consonant-le: adding suffixes',
      'Suffixes added to c-le bases',
    ),
    makeWilson(
      8,
      7,
      'Gr 3 · Level 3 · Unit 8 — Uppercase cursive letters: F, T, B, P, R, L',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      8,
      8,
      'Gr 3 · Level 3 · Unit 8 — Sample words: tumble, noble, bugle, whistle, bagel, settling, settlement, expansion, construction, vision',
      'Decode/spell c-le and -tion/-sion words',
    ),
    makeWilson(
      8,
      9,
      'Gr 3 · Level 3 · Unit 8 — Sound-alike words: principal/principle, wear/where, weight/wait, week/weak, meet/meat',
      'Homophones',
    ),
    makeWilson(
      9,
      1,
      'Gr 3 · Level 3 · Unit 9 — All sounds of ar, er, ir, or, ur (incl. /ər/ for ar/or)',
      'R-controlled vowels including unstressed schwar',
    ),
    makeWilson(
      9,
      2,
      'Gr 3 · Level 3 · Unit 9 — Combining er/ir/ur r-controlled syllables with other syllable types',
      'Multisyllabic r-controlled words',
    ),
    makeWilson(
      9,
      3,
      'Gr 3 · Level 3 · Unit 9 — war and wor patterns',
      'w + ar/or sound shift',
    ),
    makeWilson(
      9,
      4,
      'Gr 3 · Level 3 · Unit 9 — -ward and -or as a suffix',
      'Suffixes -ward, -or',
    ),
    makeWilson(
      9,
      5,
      'Gr 3 · Level 3 · Unit 9 — Spelling option procedure for /ər/ sound',
      'Choose ar/or for unstressed /er/',
    ),
    makeWilson(
      9,
      6,
      'Gr 3 · Level 3 · Unit 9 — Dictionary skills',
      'Dictionary skills',
      true,
    ),
    makeWilson(
      9,
      7,
      'Gr 3 · Level 3 · Unit 9 — R-controlled exception (rr)',
      'Doubled r exception',
    ),
    makeWilson(
      9,
      8,
      'Gr 3 · Level 3 · Unit 9 — 1-1-1 spelling rule with r-controlled words',
      'Doubling rule applied to r-controlled',
    ),
    makeWilson(
      9,
      9,
      'Gr 3 · Level 3 · Unit 9 — Uppercase cursive letters: H, K, N, M, D, W',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      9,
      10,
      'Gr 3 · Level 3 · Unit 9 — Sample words: burst, termite, orbit, solar, cherry, warmth, furry, stirred',
      'Decode/spell r-controlled words',
    ),
    makeWilson(
      9,
      11,
      'Gr 3 · Level 3 · Unit 9 — Sound-alike words: heard/herd, berry/bury, warn/worn',
      'Homophones',
    ),
    makeWilson(
      10,
      1,
      'Gr 3 · Level 3 · Unit 10 — Double vowel (vowel team) syllable',
      'Vowel team syllable type',
    ),
    makeWilson(
      10,
      2,
      'Gr 3 · Level 3 · Unit 10 — Additional vowel teams: eigh, ei, ea, ie, igh, oo, ui',
      'Less common vowel teams',
    ),
    makeWilson(
      10,
      3,
      'Gr 3 · Level 3 · Unit 10 — Adding suffixes to double vowel words',
      'Suffix attachment to vowel team bases',
    ),
    makeWilson(
      10,
      4,
      'Gr 3 · Level 3 · Unit 10 — Double vowel exception',
      'Vowel team exceptions',
    ),
    makeWilson(
      10,
      5,
      'Gr 3 · Level 3 · Unit 10 — Uppercase cursive letters: U, V, Y, Z',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      10,
      6,
      'Gr 3 · Level 3 · Unit 10 — Sample words: understood, delighted, destroying',
      'Decode/spell vowel team words',
    ),
    makeWilson(
      10,
      7,
      'Gr 3 · Level 3 · Unit 10 — Sound-alike words: sail/sale, led/lead, break/brake, pail/pale',
      'Homophones',
    ),
    makeWilson(
      11,
      1,
      'Gr 3 · Level 3 · Unit 11 — Contractions',
      'Contraction formation and decoding',
    ),
    makeWilson(
      11,
      2,
      'Gr 3 · Level 3 · Unit 11 — Uppercase cursive letters: Q, X',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      11,
      3,
      "Gr 3 · Level 3 · Unit 11 — Sample words: doesn't, can't, don't",
      'Decode/spell contractions',
    ),
    makeWilson(
      11,
      4,
      "Gr 3 · Level 3 · Unit 11 — Sound-alike words: it's/its, there/their/they're",
      'Homophones (contractions vs possessives)',
    ),
    makeWilson(
      12,
      1,
      'Gr 3 · Level 3 · Unit 12 — Soft c before e, i, y (spelling)',
      'Soft c spelling',
    ),
    makeWilson(
      12,
      2,
      'Gr 3 · Level 3 · Unit 12 — Soft g before e, i, y (spelling)',
      'Soft g spelling',
    ),
    makeWilson(
      12,
      3,
      'Gr 3 · Level 3 · Unit 12 — Spelling English words ending with /j/ sound',
      '-ge / -dge endings',
    ),
    makeWilson(
      12,
      4,
      'Gr 3 · Level 3 · Unit 12 — dge after a short vowel',
      '-dge trigraph after short vowel',
    ),
    makeWilson(
      12,
      5,
      'Gr 3 · Level 3 · Unit 12 — Silent e spelling rule after soft g and c',
      'Silent e to keep c/g soft',
    ),
    makeWilson(
      12,
      6,
      'Gr 3 · Level 3 · Unit 12 — Uppercase cursive letters: S, I, J',
      'Cursive handwriting',
      true,
    ),
    makeWilson(
      12,
      7,
      'Gr 3 · Level 3 · Unit 12 — Sample words: gentleman, entrance, bridges, replacing',
      'Decode/spell soft c/g and dge words',
    ),
    makeWilson(
      12,
      8,
      'Gr 3 · Level 3 · Unit 12 — Sound-alike words: cell/sell, peace/piece, cent/sent/scent, scene/seen',
      'Homophones',
    ),
    makeWilson(
      13,
      1,
      'Gr 3 · Level 3 · Unit 13 — New sounds: ch /k/ (chorus), ph /f/ (phone)',
      'Greek-origin spellings ch=/k/ and ph=/f/',
    ),
    makeWilson(
      13,
      2,
      'Gr 3 · Level 3 · Unit 13 — Silent letter clusters: wr, rh, gn, kn, mn, mb, gh',
      'Silent letter consonant patterns',
    ),
    makeWilson(
      13,
      3,
      'Gr 3 · Level 3 · Unit 13 — Sample words: chorus, triumph, column, wrinkle',
      'Decode/spell ch=/k/, ph=/f/, silent letter words',
    ),
    makeWilson(
      13,
      4,
      'Gr 3 · Level 3 · Unit 13 — Sound-alike words: knew/new, knight/night, knows/nose',
      'Homophones',
    ),
    makeWilson(
      14,
      1,
      'Gr 3 · Level 3 · Unit 14 — Review: ch /k/ (chorus), ph /f/ (phone)',
      'Cumulative review of Greek-origin patterns',
    ),
    makeWilson(
      14,
      2,
      'Gr 3 · Level 3 · Unit 14 — Review: silent letters wr, rh, gn, kn, mn, mb, gh',
      'Cumulative silent letter review',
    ),
    makeWilson(
      14,
      3,
      'Gr 3 · Level 3 · Unit 14 — Sample words: chorus, triumph, column, wrinkle',
      'Decode/spell cumulative review words',
    ),
    makeWilson(
      14,
      4,
      'Gr 3 · Level 3 · Unit 14 — Sound-alike words: knew/new, knight/night, knows/nose',
      'Homophones cumulative review',
    ),
  ],
};

const makeWonders = (
  unitLabel: string,
  weekNumber: number,
  skill: string,
  unitNumber: number,
): PhonicsObjective => ({
  unitNumber,
  lessonNumber: weekNumber,
  value: skill,
  label: `${unitLabel} · Week ${weekNumber} — ${skill}`,
  disabled: isNonDecodingLesson(skill) || undefined,
  exampleWords: deriveExampleWords(skill),
});

const WONDERS_OBJECTIVES_BY_GRADE: Partial<Record<Grade, PhonicsObjective[]>> = {
  K: [
    // Smart Start (Unit 0)
    makeWonders(
      'Smart Start',
      1,
      'Letter Recognition (Aa, Bb, Cc, Dd, Ee, Ff, Gg, Hh)',
      0,
    ),
    makeWonders(
      'Smart Start',
      2,
      'Letter Recognition (Ii, Jj, Kk, Ll, Mm, Nn, Oo, Pp, Qq, Rr)',
      0,
    ),
    makeWonders(
      'Smart Start',
      3,
      'Letter Recognition (Ss, Tt, Uu, Vv, Ww, Xx, Yy, Zz)',
      0,
    ),
    // Unit 1
    makeWonders('Unit 1', 1, 'initial/final consonant (m /m/)', 1),
    makeWonders('Unit 1', 2, 'initial/medial vowel (a /ă/)', 1),
    makeWonders('Unit 1', 3, 'initial consonant (s /s/)', 1),
    // Unit 2
    makeWonders('Unit 2', 1, 'initial/final consonant (p /p/)', 2),
    makeWonders('Unit 2', 2, 'initial/final consonant (t /t/)', 2),
    makeWonders('Unit 2', 3, 'review (m /m/, a /ă/, s /s/, p /p/, t /t/)', 2),
    // Unit 3
    makeWonders('Unit 3', 1, 'medial vowel (i /ĭ/)', 3),
    makeWonders('Unit 3', 2, 'consonant (n /n/)', 3),
    makeWonders('Unit 3', 3, 'consonant (c /k/)', 3),
    // Unit 4
    makeWonders('Unit 4', 1, 'initial/medial vowel (o /ŏ/)', 4),
    makeWonders('Unit 4', 2, 'initial/final consonant (d /d/)', 4),
    makeWonders(
      'Unit 4',
      3,
      'review (i /ĭ/, n /n/, c /k/, o /ŏ/, d /d/); S-blends (sn, sp, st)',
      4,
    ),
    // Unit 5
    makeWonders('Unit 5', 1, 'initial consonant (h /h/)', 5),
    makeWonders('Unit 5', 2, 'initial/medial vowel (e /ĕ/)', 5),
    makeWonders(
      'Unit 5',
      3,
      'initial/final consonant (f /f/); initial consonant (r /r/)',
      5,
    ),
    // Unit 6
    makeWonders(
      'Unit 6',
      1,
      'initial/final consonant (b /b/); initial consonant (l /l/)',
      6,
    ),
    makeWonders('Unit 6', 2, 'initial consonant (k /k/); final consonant (ck /k/)', 6),
    makeWonders(
      'Unit 6',
      3,
      'review (h /h/, e /ĕ/, f /f/, r /r/, b /b/, l /l/, k /k/, ck /k/); L-blends (bl, cl, fl, sl)',
      6,
    ),
    // Unit 7
    makeWonders('Unit 7', 1, 'initial/medial vowel (u /ŭ/)', 7),
    makeWonders(
      'Unit 7',
      2,
      'initial/final consonant (g /g/); initial consonant (w /w/)',
      7,
    ),
    makeWonders('Unit 7', 3, 'final consonant (x /ks/); initial consonant (v /v/)', 7),
    // Unit 8
    makeWonders('Unit 8', 1, 'initial consonant (j /j/); initial consonant (qu /kw/)', 8),
    makeWonders('Unit 8', 2, 'initial consonant (y /y/); initial consonant (z /z/)', 8),
    makeWonders(
      'Unit 8',
      3,
      'review (u /ŭ/, g /g/, w /w/, x /ks/, v /v/, j /j/, qu /kw/, y /y/, z /z/); R-blends (br, cr, dr, gr, tr)',
      8,
    ),
    // Unit 9
    makeWonders('Unit 9', 1, 'long vowel a (a_e)', 9),
    makeWonders('Unit 9', 2, 'long vowel i (i_e)', 9),
    makeWonders('Unit 9', 3, 'long vowel o (o_e)', 9),
    // Unit 10
    makeWonders('Unit 10', 1, 'long vowel u (u_e)', 10),
    makeWonders('Unit 10', 2, 'long vowel e (e /ē/, ee, e_e)', 10),
    makeWonders(
      'Unit 10',
      3,
      'long vowels (a, e, i, o, u); final blends (st, nd, nk)',
      10,
    ),
  ],
  '1': [
    // Smart Start (Unit 0)
    makeWonders('Smart Start', 1, 'identify and blend (m, s, a, p, t, n, r, i)', 0),
    makeWonders(
      'Smart Start',
      2,
      'identify and blend (c, f, o, d, h, s, e, b, l, ll)',
      0,
    ),
    makeWonders(
      'Smart Start',
      3,
      'identify and blend (k, ck, u, g, w, x, v, qu, j, y, z)',
      0,
    ),
    // Unit 1
    makeWonders('Unit 1', 1, 'short a', 1),
    makeWonders('Unit 1', 2, 'short i', 1),
    makeWonders('Unit 1', 3, 'beginning consonant blends (L-blends)', 1),
    makeWonders('Unit 1', 4, 'short o', 1),
    makeWonders('Unit 1', 5, 'beginning consonant blends (R-blends, S-blends)', 1),
    // Unit 2
    makeWonders('Unit 2', 1, 'short e (spelled e, ea)', 2),
    makeWonders('Unit 2', 2, 'short u', 2),
    makeWonders('Unit 2', 3, 'ending consonant blends', 2),
    makeWonders('Unit 2', 4, 'consonant digraphs (sh, th, -ng)', 2),
    makeWonders('Unit 2', 5, 'consonant digraphs (ch, -tch, wh, ph)', 2),
    // Unit 3
    makeWonders('Unit 3', 1, 'long a (spelled a_e)', 3),
    makeWonders('Unit 3', 2, 'long i (spelled i_e)', 3),
    makeWonders('Unit 3', 3, 'soft c, g/dge', 3),
    makeWonders(
      'Unit 3',
      4,
      'long o (spelled o_e); long u (spelled u_e); long e (spelled e_e)',
      3,
    ),
    makeWonders('Unit 3', 5, 'variant vowel spellings with digraphs (oo, u)', 3),
    // Unit 4
    makeWonders('Unit 4', 1, 'long a (spelled a, ai, ay)', 4),
    makeWonders('Unit 4', 2, 'long e (spelled e, ee, ea, ie)', 4),
    makeWonders('Unit 4', 3, 'long o (spelled o, oa, ow, oe)', 4),
    makeWonders('Unit 4', 4, 'long i (spelled i, igh, y, ie)', 4),
    makeWonders('Unit 4', 5, 'long e (spelled y, ey)', 4),
    // Unit 5
    makeWonders('Unit 5', 1, 'r-controlled vowel (ar)', 5),
    makeWonders('Unit 5', 2, 'r-controlled vowels (or, ir, ur, er)', 5),
    makeWonders('Unit 5', 3, 'r-controlled vowels (or, ore, oar)', 5),
    makeWonders('Unit 5', 4, 'diphthongs (ou, ow)', 5),
    makeWonders('Unit 5', 5, 'diphthongs (oi, oy)', 5),
    // Unit 6
    makeWonders('Unit 6', 1, 'variant vowel spellings (oo, ou, u_e, ew, ui, ue, u)', 6),
    makeWonders(
      'Unit 6',
      2,
      'variant vowel spellings with digraphs (au, aw, a, augh, al)',
      6,
    ),
    makeWonders('Unit 6', 3, 'silent letter consonant digraphs (wr, kn, gn)', 6),
    makeWonders(
      'Unit 6',
      4,
      'three-letter consonant blends (scr, spl, spr, str, thr, shr)',
      6,
    ),
    makeWonders('Unit 6', 5, 'r-controlled vowels (air, are, ear)', 6),
  ],
  '2': [
    // Unit 1
    makeWonders('Unit 1', 1, 'short vowels (a, i); plural nouns (-s, -es)', 1),
    makeWonders('Unit 1', 2, 'short vowels (e, o, u); inflectional endings (-s, -es)', 1),
    makeWonders(
      'Unit 1',
      3,
      'two-letter blends: r-blends (br, cr, dr, fr, gr); s-blends (sc, sk, sl, sm, sn, sp, st, sw); t-blends (tr, tw, -nt); l-blends (bl, cl, fl, gl, pl, -lk, -lt); closed syllables',
      1,
    ),
    makeWonders(
      'Unit 1',
      4,
      'short vowel a; long vowel a (a_e); inflectional endings (-ed, -ing)',
      1,
    ),
    makeWonders('Unit 1', 5, 'short vowel i; long vowel i (i_e)', 1),
    // Unit 2
    makeWonders(
      'Unit 2',
      1,
      'short vowel o; long vowel o (o_e); inflectional endings (-ed, -ing)',
      2,
    ),
    makeWonders('Unit 2', 2, 'short vowel u; long vowel u (u_e); CVCe syllables', 2),
    makeWonders('Unit 2', 3, 'soft c and g; prefixes (re-, un-, dis-)', 2),
    makeWonders('Unit 2', 4, 'consonant digraphs; suffixes (-ful, -less)', 2),
    makeWonders(
      'Unit 2',
      5,
      'three-letter blends (scr, spr, str, thr, spl, shr); compound words',
      2,
    ),
    // Unit 3
    makeWonders(
      'Unit 3',
      1,
      "long vowel a (a, ai, ay, ea, ei, eigh, ey); contractions ('s, 're, 'll, 've)",
      3,
    ),
    makeWonders('Unit 3', 2, 'long vowel i (i, y, igh, ie); open syllables', 3),
    makeWonders('Unit 3', 3, 'long vowel o (o, oa, ow, oe); contractions (with not)', 3),
    makeWonders(
      'Unit 3',
      4,
      'long vowel e (e, ee, ea, ie, y, ey, e_e); plurals (with y to i changes: -s, -es)',
      3,
    ),
    makeWonders(
      'Unit 3',
      5,
      'long vowel u (u_e, ue, u, ew); comparative endings (-er, -est)',
      3,
    ),
    // Unit 4
    makeWonders(
      'Unit 4',
      1,
      'silent letters (wr, kn, gn, mb, sc); prefixes (re-, un-, dis-); suffixes (-ful, -less)',
      4,
    ),
    makeWonders(
      'Unit 4',
      2,
      'r-controlled vowel /ûr/: er, ir, ur, or; inflectional endings (-s, -es, -ed, -ing)',
      4,
    ),
    makeWonders(
      'Unit 4',
      3,
      'r-controlled vowels (/ôr/: or, ore, oar and /är/: ar); plurals (irregular)',
      4,
    ),
    makeWonders(
      'Unit 4',
      4,
      'r-controlled vowel (/îr/: eer, ere, ear); abbreviations',
      4,
    ),
    makeWonders(
      'Unit 4',
      5,
      'r-controlled vowel (/âr/: are, air, ear, ere); r-controlled vowel syllables',
      4,
    ),
    // Unit 5
    makeWonders('Unit 5', 1, 'diphthongs (ou, ow); plurals (irregular)', 5),
    makeWonders(
      'Unit 5',
      2,
      'diphthongs (oy, oi); consonant + le syllables (el, al, tion, sion)',
      5,
    ),
    makeWonders(
      'Unit 5',
      3,
      'variant vowels (/ü/: oo, u, u_e, ew, ue, ui and /ů/: oo, ou, u); contractions (with not)',
      5,
    ),
    makeWonders(
      'Unit 5',
      4,
      'variant vowel /ô/: a, aw, au, augh, al, ough; vowel team syllables',
      5,
    ),
    makeWonders(
      'Unit 5',
      5,
      'short vowel digraphs (/e/ ea; /u/ ou; /i/ y); alphabetical order',
      5,
    ),
    // Unit 6
    makeWonders('Unit 6', 1, 'closed and open syllables; compound words', 6),
    makeWonders(
      'Unit 6',
      2,
      'CVCe syllables; review prefixes (re-, un-, dis-); review suffixes (-ful, -less)',
      6,
    ),
    makeWonders(
      'Unit 6',
      3,
      'final stable syllables: consonant + le (el, al, tion, sion); contractions and possessives',
      6,
    ),
    makeWonders(
      'Unit 6',
      4,
      'vowel team syllables; comparative endings (with spelling changes: -er, -est)',
      6,
    ),
    makeWonders(
      'Unit 6',
      5,
      'words with r-controlled vowel syllables; words with three or more syllables',
      6,
    ),
  ],
};

// HMH Into Reading ships a single phonics scope and sequence that is shared
// across grade levels rather than partitioned by unit per grade. We attach the
// same ordered list to every supported grade so the picker behaves identically
// regardless of which grade the teacher selected.
const makeHMH = (
  lessonNumber: number,
  skill: string,
  options: { isFree?: boolean } = {},
): PhonicsObjective => ({
  lessonNumber,
  value: skill,
  label: `Lesson ${lessonNumber}: ${skill}`,
  ...(options.isFree ? { isFree: true } : {}),
  disabled: isNonDecodingLesson(skill) || undefined,
  exampleWords: deriveExampleWords(skill),
});

const HMH_INTO_READING_OBJECTIVES: PhonicsObjective[] = [
  makeHMH(1, 'm', { isFree: true }),
  makeHMH(2, 's', { isFree: true }),
  makeHMH(3, 't', { isFree: true }),
  makeHMH(4, 'b'),
  makeHMH(5, 'a'),
  makeHMH(6, 'n'),
  makeHMH(7, 'd'),
  makeHMH(8, 'c'),
  makeHMH(9, 'p'),
  makeHMH(10, 'i'),
  makeHMH(11, 'r'),
  makeHMH(12, 'f'),
  makeHMH(13, 'ending s'),
  makeHMH(14, 'g'),
  makeHMH(15, 'k'),
  makeHMH(16, 'o'),
  makeHMH(17, 'l'),
  makeHMH(18, 'h'),
  makeHMH(19, 'w'),
  makeHMH(20, 'j'),
  makeHMH(21, 'u'),
  makeHMH(22, 'v'),
  makeHMH(23, 'y'),
  makeHMH(24, 'qu'),
  makeHMH(25, 'x'),
  makeHMH(26, 'z'),
  makeHMH(27, 's blends'),
  makeHMH(28, 'l blends'),
  makeHMH(29, 'final st and nd'),
  makeHMH(30, 'ss, ff, ll, zz'),
  makeHMH(31, 'ck'),
  makeHMH(32, 'sh'),
  makeHMH(33, 'ch'),
  makeHMH(34, 'th'),
  makeHMH(35, 'wh'),
  makeHMH(36, 'a and i CVCe'),
  makeHMH(37, 'o and u CVCe'),
  makeHMH(38, 'e CVCe'),
  makeHMH(39, 'ee'),
  makeHMH(40, 'soft c and g'),
  makeHMH(41, 'tch'),
  makeHMH(42, 'compound words'),
  makeHMH(43, 'r blends'),
  makeHMH(44, '-ed'),
  makeHMH(45, 'open vowels'),
  makeHMH(46, 'possessives'),
  makeHMH(47, 'kn and wr'),
  makeHMH(48, 'ea'),
  makeHMH(49, 'ai and ay'),
  makeHMH(50, 'oa and ow'),
  makeHMH(51, 'igh and y'),
  makeHMH(52, 'ar'),
  makeHMH(53, 'or'),
  makeHMH(54, 'er, ir, ur'),
  makeHMH(55, 'ng and nk'),
  makeHMH(56, 'oo'),
  makeHMH(57, 'ow and ou'),
  makeHMH(58, 'oy and oi'),
  makeHMH(59, 'ie and ey'),
  makeHMH(60, 'suffix ful, less, ly, y'),
  makeHMH(61, 'prefix un and re'),
  makeHMH(62, 'suffix er and est'),
  makeHMH(63, 'two syllable words'),
];

export const HMH_INTO_READING_OBJECTIVES_BY_GRADE: Partial<Record<
  Grade,
  PhonicsObjective[]
>> = {
  K: HMH_INTO_READING_OBJECTIVES,
  '1': HMH_INTO_READING_OBJECTIVES,
  '2': HMH_INTO_READING_OBJECTIVES,
  '3': HMH_INTO_READING_OBJECTIVES,
  '4': HMH_INTO_READING_OBJECTIVES,
  '5': HMH_INTO_READING_OBJECTIVES,
};

// Really Great Reading Phonics Boost is an ungraded intervention sequence (80
// lessons across 4 books — "All students start with Lesson 1"), so like HMH we
// attach the same ordered list to every supported grade. `unitNumber` carries
// the book number so the free-slice helper picks one free objective per book.
const makeBoost = (
  bookNumber: number,
  lessonNumber: number,
  skill: string,
  disabled = false,
): PhonicsObjective => ({
  unitNumber: bookNumber,
  lessonNumber,
  value: skill,
  label: `Book ${bookNumber} · Lesson ${lessonNumber} — ${skill}`,
  disabled: disabled || isNonDecodingLesson(skill),
  exampleWords: deriveExampleWords(skill),
});

const RGR_PHONICS_BOOST_OBJECTIVES: PhonicsObjective[] = [
  // Book 1
  makeBoost(1, 1, 'Introduce Phonics Boost Lessons; Teach Oral Reading Marking', true),
  makeBoost(1, 2, 'Teach Oral Reading Scoring; Practice Oral Reading Procedure', true),
  makeBoost(1, 3, 'Short a and Short i Letter Sounds; Consonant Letter Sounds'),
  makeBoost(1, 4, 'Closed Syllables'),
  makeBoost(1, 5, 'Nonsense Words'),
  makeBoost(1, 6, 'Short o Letter Sound'),
  makeBoost(1, 7, 'Digraph sh'),
  makeBoost(1, 8, 'Short u Letter Sound'),
  makeBoost(1, 9, 'Digraph th'),
  makeBoost(1, 10, 'Short e Letter Sound'),
  makeBoost(1, 11, 'Digraph ch'),
  makeBoost(1, 12, 'Digraphs wh and ph'),
  makeBoost(1, 13, 'Spelling with Doubled Letters ff, ll, ss'),
  makeBoost(1, 14, 'Spelling with Digraph ck'),
  makeBoost(1, 15, 'Trigraphs tch and dge'),
  makeBoost(1, 16, 'Final 2-Sound Blends'),
  makeBoost(1, 17, 'Initial 2-Sound Blends'),
  makeBoost(1, 18, '3-Sound Blends'),
  makeBoost(1, 19, 'Digraph Blends and squ'),
  makeBoost(1, 20, 'Suffix -s'),
  // Book 2
  makeBoost(2, 21, 'Suffix -es'),
  makeBoost(2, 22, 'ang, ing, ong, ung, ank, ink, onk, unk'),
  makeBoost(2, 23, 'Reading Two-Syllable Words'),
  makeBoost(2, 24, 'Spelling Two-Syllable Words'),
  makeBoost(2, 25, 'Schwa'),
  makeBoost(2, 26, 'Reading Challenging Words with Three or More Syllables'),
  makeBoost(2, 27, 'Reading More Challenging Words with Three or More Syllables'),
  makeBoost(2, 28, 'Reading Most Challenging Words with Three or More Syllables'),
  makeBoost(2, 29, 'Spelling Words with Three or More Syllables'),
  makeBoost(2, 30, 'Suffix -ed Adds Syllable /ed/'),
  makeBoost(2, 31, 'Suffix -ed Adds Sound /d/ or /t/'),
  makeBoost(2, 32, 'One-Syllable Words with Suffix -ed'),
  makeBoost(2, 33, 'Multi-Syllable Words with Suffix -ed'),
  makeBoost(2, 34, 'Pay Attention to Consonant Suffixes'),
  makeBoost(2, 35, 'Pay Attention to Vowel Suffixes'),
  makeBoost(2, 36, 'Adding Consonant Suffixes to Closed-Syllable Words'),
  makeBoost(2, 37, '1-1-1 Doubling Rule'),
  makeBoost(2, 38, 'The Letters ar Spell /ar/ and the Letters or Spell /or/'),
  makeBoost(2, 39, 'Four Spellings for /er/: er, ir, ur, and ear in One-Syllable Words'),
  makeBoost(
    2,
    40,
    'Four Spellings for /er/: er, ir, ur, and ear in Multi-Syllable Words',
  ),
  // Book 3
  makeBoost(3, 41, 'Two Additional Spellings of /er/: or and ar'),
  makeBoost(3, 42, 'Consonant-le'),
  makeBoost(3, 43, 'Other Spellings of /əl/'),
  makeBoost(3, 44, 'y as a Vowel'),
  makeBoost(3, 45, 'Open Syllables in Two-Syllable Words'),
  makeBoost(3, 46, 'Open Syllables in Words with Three or More Syllables'),
  makeBoost(3, 47, 'Consonant-le with Open Syllables'),
  makeBoost(3, 48, 'Hard and Soft c'),
  makeBoost(3, 49, 'Hard and Soft g'),
  makeBoost(3, 50, 'Soft g Exceptions'),
  makeBoost(3, 51, 'Vowel-Consonant-E in One-Syllable Words'),
  makeBoost(3, 52, 'Vowel-Consonant-E in Two-Syllable Words'),
  makeBoost(3, 53, 'Vowel-Consonant-E in Words with Three or More Syllables'),
  makeBoost(3, 54, 'Vowel-Consonant-E Spelling Schwa'),
  makeBoost(3, 55, 'Reading Words with e Dropped to Add a Vowel Suffix'),
  makeBoost(3, 56, 'Spelling Words with e Dropped to Add a Vowel Suffix'),
  makeBoost(3, 57, 'Odd Syllables: tion, sion, ture, and cious'),
  makeBoost(3, 58, 'Six Spellings of Long o: o, oa, ow, oe, o-e, and ough'),
  makeBoost(3, 59, 'Six Spellings of Long o in Words with Three or More Syllables'),
  makeBoost(3, 60, 'Six Spellings of Long a: a, ai, ay, a-e, eigh, and ea'),
  // Book 4
  makeBoost(4, 61, 'Six Spellings of Long a in Words with Three or More Syllables'),
  makeBoost(4, 62, 'Five Spellings of Long i: i, ie, y, i-e, and igh'),
  makeBoost(4, 63, 'Five Spellings of Long i in Words with Three or More Syllables'),
  makeBoost(4, 64, 'Three Spellings of Long e: e, ee, and ea'),
  makeBoost(4, 65, 'Four Additional Spellings of Long e: ie, y, ey, and e-e'),
  makeBoost(4, 66, 'The Letter i Spells Long e in an Open Syllable'),
  makeBoost(4, 67, 'Eight Spellings of Long e: e, ee, ea, ie, y, ey, e-e, and i'),
  makeBoost(4, 68, 'Four Spellings of Long u: u, u-e, ue, and ew'),
  makeBoost(4, 69, 'Six Spellings of /oo/ as in Food: oo, ou, ew, u, ue, and u-e'),
  makeBoost(4, 70, 'Two Spellings of /oi/: oi and oy'),
  makeBoost(4, 71, 'Two Spellings of /oo/ as in Book: oo and ou'),
  makeBoost(4, 72, 'Two Spellings of /ou/: ou and ow'),
  makeBoost(4, 73, 'Two Spellings of /aw/: aw and au'),
  makeBoost(4, 74, 'The Letters ow Spell Two Sounds: /ō/ as in Snow and /ow/ as in Cow'),
  makeBoost(
    4,
    75,
    'The Letters oo Spell Two Sounds: /oo/ as in Food and /oo/ as in Book',
  ),
  makeBoost(
    4,
    76,
    'The Letters ea Spell Three Sounds: /ē/ as in Eat, /ĕ/ as in Bread, /ā/ as in Steak',
  ),
  makeBoost(4, 77, 'Two Vowels Together Can Spell Two Sounds'),
  makeBoost(4, 78, 'More Words with Two Vowels Spelling Two Sounds'),
  makeBoost(4, 79, 'Prefixes'),
  makeBoost(4, 80, 'Prefix or Not?'),
];

export const RGR_PHONICS_BOOST_OBJECTIVES_BY_GRADE: Partial<Record<
  Grade,
  PhonicsObjective[]
>> = {
  '1': RGR_PHONICS_BOOST_OBJECTIVES,
  '2': RGR_PHONICS_BOOST_OBJECTIVES,
  '3': RGR_PHONICS_BOOST_OBJECTIVES,
  '4': RGR_PHONICS_BOOST_OBJECTIVES,
  '5': RGR_PHONICS_BOOST_OBJECTIVES,
};

// Savvas myView Literacy — unit/week structure (K–2). The phonic-skill
// sequence is taken from the published myView scope & sequence. Each myView
// week is split into one objective per distinct skill it lists (e.g. a week
// covering sh, th, -ing becomes three lessons), so every objective targets a
// single phoneme/grapheme/pattern. `value` uses `grapheme /phoneme/` notation
// so `deriveExampleWords` resolves example words; for vowel teams / r-controlled
// vowels / diphthongs the grapheme is used as the sound key (e.g. `ay /ay/`) so
// chips are spelling-specific. Lessons are numbered sequentially within their
// unit; the week is kept in the label. Pure cumulative-review days and a few
// abstract patterns have no derivable rule — that's expected (no chips / no
// standalone landing page), and none are pure pedagogy so all stay enabled.
const myViewUnit = (
  unitNumber: number,
  lessons: { week: number; value: string }[],
): PhonicsObjective[] =>
  lessons.map(({ week, value }, i) => ({
    unitNumber,
    lessonNumber: i + 1,
    value,
    label: `Unit ${unitNumber} Week ${week} - ${value}`,
    disabled: isNonDecodingLesson(value) || undefined,
    exampleWords: deriveExampleWords(value),
  }));

export const SAVVAS_MYVIEW_OBJECTIVES_BY_GRADE: Partial<Record<
  Grade,
  PhonicsObjective[]
>> = {
  K: [
    ...myViewUnit(1, [
      { week: 1, value: 'm /m/' },
      { week: 1, value: 't /t/' },
      { week: 2, value: 'a /ă/' },
      { week: 2, value: 's /s/' },
      { week: 3, value: 'p /p/' },
      { week: 3, value: 'c /k/' },
      { week: 4, value: 'i /ĭ/' },
      { week: 4, value: 'n /n/' },
      { week: 5, value: 'b /b/' },
      { week: 5, value: 'r /r/' },
    ]),
    ...myViewUnit(2, [
      { week: 1, value: 'd /d/' },
      { week: 1, value: 'k /k/' },
      { week: 2, value: 'o /ŏ/' },
      { week: 2, value: 'f /f/' },
      { week: 3, value: 'h /h/' },
      { week: 3, value: 'l /l/' },
      { week: 4, value: 'g /g/' },
      { week: 4, value: 'CVCC & CCVC blends' },
      { week: 5, value: 'e /ĕ/' },
      { week: 5, value: 'w /w/' },
      { week: 5, value: 'y /y/' },
    ]),
    ...myViewUnit(3, [
      { week: 1, value: 'j /j/' },
      { week: 1, value: 'x /ks/' },
      { week: 2, value: 'u /ŭ/' },
      { week: 2, value: 'v /v/' },
      { week: 3, value: 'z /z/' },
      { week: 3, value: 'qu /kw/' },
      { week: 4, value: 'a_e /ā/' },
      { week: 5, value: 'i_e /ī/' },
    ]),
    ...myViewUnit(4, [
      { week: 1, value: 'o_e /ō/' },
      { week: 2, value: 'u_e /ū/' },
      { week: 3, value: 'e_e /ē/' },
      { week: 4, value: 'Review: p, y, long i (i_e /ī/)' },
      { week: 5, value: 'Review: d, f, v, short e (e /ĕ/)' },
    ]),
    ...myViewUnit(5, [
      { week: 1, value: 'Review: c, t, long & short o (o_e /ō/)' },
      { week: 2, value: 'CVCC blends' },
      { week: 2, value: 'CCVC blends' },
      { week: 2, value: 'CCVCC blends' },
      { week: 3, value: 'Review: g, q, long & short a (a_e /ā/)' },
      { week: 4, value: 'Review: k, s, w, m' },
      { week: 5, value: 'Review: l, n, r, z' },
    ]),
  ],
  '1': [
    ...myViewUnit(1, [
      { week: 1, value: 'a /ă/' },
      { week: 1, value: 'm /m/' },
      { week: 1, value: 's /s/' },
      { week: 1, value: 't /t/' },
      { week: 2, value: 'i /ĭ/' },
      { week: 2, value: 'c /k/' },
      { week: 2, value: 'p /p/' },
      { week: 2, value: 'n /n/' },
      { week: 3, value: 'o /ŏ/' },
      { week: 3, value: 'f /f/' },
      { week: 3, value: 'b /b/' },
      { week: 3, value: 'g /g/' },
      { week: 4, value: 'e /ĕ/' },
      { week: 4, value: 'd /d/' },
      { week: 4, value: 'l /l/' },
      { week: 4, value: 'h /h/' },
      { week: 5, value: 'u /ŭ/' },
      { week: 5, value: 'r /r/' },
      { week: 5, value: 'w /w/' },
      { week: 5, value: 'j /j/' },
      { week: 5, value: 'k /k/' },
    ]),
    ...myViewUnit(2, [
      { week: 1, value: 'x /ks/' },
      { week: 1, value: 'CCVC blends' },
      { week: 2, value: 'ck /k/' },
      { week: 2, value: '-s (plural)' },
      { week: 3, value: 'Final blends (CVCC)' },
      { week: 3, value: '-s (inflectional)' },
      { week: 4, value: 'sh /sh/' },
      { week: 4, value: 'th /th/' },
      { week: 4, value: '-ing' },
      { week: 5, value: 'a_e /ā/' },
      { week: 5, value: 'al, aw /aw/' },
    ]),
    ...myViewUnit(3, [
      { week: 1, value: 'wh /w/' },
      { week: 1, value: 'ph /f/' },
      { week: 1, value: 'ch /ch/' },
      { week: 1, value: 'tch /ch/' },
      { week: 2, value: 'o_e /ō/' },
      { week: 2, value: 'u_e /ū/' },
      { week: 2, value: 'e_e /ē/' },
      { week: 3, value: 'ee /ee/' },
      { week: 3, value: '-ed' },
      { week: 4, value: 'y /ē/' },
      { week: 4, value: 'y /ī/' },
      { week: 5, value: 'ng /ŋ/' },
      { week: 5, value: 'nk /ŋk/' },
    ]),
    ...myViewUnit(4, [
      { week: 1, value: 'ar /ar/' },
      { week: 1, value: '-es' },
      { week: 2, value: 'er /er/' },
      { week: 2, value: 'ir /ir/' },
      { week: 2, value: 'ur /ur/' },
      { week: 3, value: 'dge /j/' },
      { week: 3, value: 'Comparative endings (-er, -est)' },
      { week: 4, value: 'ow /ow/' },
      { week: 4, value: 'ou /ou/' },
      { week: 4, value: 'ai /ai/' },
      { week: 4, value: 'ay /ay/' },
      { week: 5, value: 'oi /oi/' },
      { week: 5, value: 'oy /oy/' },
      { week: 5, value: 'ea /ea/' },
    ]),
    ...myViewUnit(5, [
      { week: 1, value: 'oa /oa/' },
      { week: 1, value: 'ow /ō/' },
      { week: 1, value: 'oe /ō/' },
      { week: 1, value: 'Three-letter blends' },
      { week: 2, value: 'igh /igh/' },
      { week: 2, value: 'Suffixes: -er, -or' },
      { week: 3, value: 'ue /ū/' },
      { week: 3, value: 'ew /ew/' },
      { week: 3, value: 'ui /ū/' },
      { week: 3, value: 'Prefixes: re-, un-' },
      { week: 4, value: 'Long i (igh, ie, i_e, y)' },
      { week: 4, value: 'Long o (oa, ow, oe, o_e)' },
      { week: 4, value: '-ful, -ly' },
      { week: 5, value: 'oo /oo/' },
      { week: 5, value: 'ou /ou/' },
    ]),
  ],
  '2': [
    ...myViewUnit(1, [
      { week: 1, value: 'Short vowels (a, e, i, o, u)' },
      { week: 2, value: 'VCe (a_e, e_e, i_e, o_e, u_e)' },
      { week: 3, value: 'CVCC blends' },
      { week: 3, value: 'CCVC blends' },
      { week: 3, value: 'CCVCC blends' },
      { week: 4, value: 'ch /ch/' },
      { week: 4, value: 'sh /sh/' },
      { week: 4, value: 'wh /w/' },
      { week: 4, value: 'th /th/' },
      { week: 4, value: 'ph /f/' },
      { week: 4, value: 'tch /ch/' },
      { week: 5, value: '-s' },
      { week: 5, value: '-es' },
      { week: 5, value: '-ed' },
      { week: 5, value: '-ing' },
    ]),
    ...myViewUnit(2, [
      { week: 1, value: 'Contractions' },
      { week: 2, value: 'ai /ai/' },
      { week: 2, value: 'ay /ay/' },
      { week: 2, value: 'ea /ea/' },
      { week: 3, value: 'ie /ī/' },
      { week: 4, value: 'ee /ee/' },
      { week: 4, value: 'ea /ea/' },
      { week: 4, value: 'ey /ey/' },
      { week: 4, value: 'y /ē/' },
      { week: 5, value: 'o /ō/' },
      { week: 5, value: 'oa /oa/' },
      { week: 5, value: 'ow /ō/' },
    ]),
    ...myViewUnit(3, [
      { week: 1, value: 'i /ī/' },
      { week: 1, value: 'ie /ī/' },
      { week: 1, value: 'i_e /ī/' },
      { week: 1, value: 'igh /igh/' },
      { week: 1, value: 'y /ī/' },
      { week: 2, value: 'Comparative endings (-er, -est)' },
      { week: 3, value: 'er /er/' },
      { week: 3, value: 'ir /ir/' },
      { week: 3, value: 'ur /ur/' },
      { week: 4, value: 'ou /ou/' },
      { week: 4, value: 'ow /ow/' },
      { week: 4, value: 'oi /oi/' },
      { week: 4, value: 'oy /oy/' },
      { week: 5, value: 'oo /oo/' },
      { week: 5, value: 'ue /ū/' },
      { week: 5, value: 'ew /ew/' },
      { week: 5, value: 'ui /ū/' },
    ]),
    ...myViewUnit(4, [
      { week: 1, value: 'Closed syllables' },
      { week: 2, value: 'Open syllables' },
      { week: 3, value: '-ly' },
      { week: 3, value: '-ful' },
      { week: 3, value: '-er' },
      { week: 3, value: '-less' },
      { week: 3, value: '-or' },
      { week: 4, value: 'Prefix un-' },
      { week: 4, value: 'Prefix re-' },
      { week: 4, value: 'Prefix pre-' },
      { week: 4, value: 'Prefix dis-' },
      { week: 5, value: 'Syllable division: VCCV' },
    ]),
    ...myViewUnit(5, [
      { week: 1, value: 'Homographs' },
      { week: 2, value: 'FLSZ doubling (ff, ll, ss, zz)' },
      { week: 3, value: 'aw /aw/' },
      { week: 3, value: 'au /au/' },
      { week: 3, value: 'augh /aw/' },
      { week: 3, value: 'al /aw/' },
      { week: 4, value: 'Syllable division: VCCCV' },
      { week: 5, value: 'Abbreviations' },
    ]),
  ],
};

export const PHONICS_OBJECTIVES_BY_SCOPE: Record<
  string,
  Partial<Record<Grade, PhonicsObjective[]>>
> = {
  'Bluebonnet Learning': PHONICS_OBJECTIVES_BY_GRADE,
  'UFLI Foundations': UFLI_OBJECTIVES_BY_GRADE,
  'Amplify CKLA': CKLA_OBJECTIVES_BY_GRADE,
  'Heggerty Phonemic Awareness': HEGGERTY_OBJECTIVES_BY_GRADE,
  'Wilson Fundations': WILSON_OBJECTIVES_BY_GRADE,
  Wonders: WONDERS_OBJECTIVES_BY_GRADE,
  'HMH Into Reading': HMH_INTO_READING_OBJECTIVES_BY_GRADE,
  'Really Great Reading Phonics Boost': RGR_PHONICS_BOOST_OBJECTIVES_BY_GRADE,
  'Savvas myView': SAVVAS_MYVIEW_OBJECTIVES_BY_GRADE,
};

export const ALLOWED_GRADES_BY_SCOPE: Record<string, Grade[]> = {
  'Bluebonnet Learning': ['K', '1', '2', '3'],
  'UFLI Foundations': ['K', '1', '2'],
  'Amplify CKLA': ['K', '1', '2'],
  'Heggerty Phonemic Awareness': ['Pre-K', 'K', '1', '2'],
  'Wilson Fundations': ['K', '1', '2', '3'],
  Wonders: ['K', '1', '2'],
  'HMH Into Reading': ['K', '1', '2', '3', '4', '5'],
  'Really Great Reading Phonics Boost': ['1', '2', '3', '4', '5'],
  'Savvas myView': ['K', '1', '2'],
};

export const SCOPE_AND_SEQUENCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Bluebonnet Learning', label: 'Bluebonnet Learning' },
  { value: 'UFLI Foundations', label: 'UFLI' },
  { value: 'Amplify CKLA', label: 'CKLA' },
  { value: 'Heggerty Phonemic Awareness', label: 'Heggerty' },
  { value: 'Wilson Fundations', label: 'Wilson Fundations' },
  { value: 'Wonders', label: 'Wonders' },
  { value: 'HMH Into Reading', label: 'HMH Into Reading' },
  { value: 'Really Great Reading Phonics Boost', label: 'Really Great Reading' },
  { value: 'Savvas myView', label: 'Savvas myView' },
];
